import {
  AnyID,
  Getter,
  Item,
  ItemGroup,
  ItemWithOptions,
  LinkedItem,
  Morphs,
  Partition,
  SceneObjects,
  Setter,
  SnapSide,
  Textures,
  Walls,
  Zone,
} from '@types'
import {
  isLateralPlacementBatchActive,
  isLinkedVisualBatchActive,
  runLateralPlacementBatch,
} from '@context/utils/linked/runLinkedVisualBatch'
import {
  applyTiradorPlacement,
  copyTiradorSlotPositions,
  findTiradorSlotOnParentElements,
  hasTiradorSlotPositions,
  isSinTiradorCatalog,
  replaceTiradorSlotPositions,
} from '@utils/calculateTiradorPosition'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { validateHex } from '@utils/conversion/validateHex'
import { degrees, radians } from '@utils/generalMaths/geometry'
import { ensureSidebarMorphs } from '@utils/catalog/ensureSidebarMorphs'
import { isKARBCatalog, isKbrRinconeroFamilyCatalog } from '@utils/catalog/kbrCatalog'
import { API } from '@utils/queryBuilder'
import getMorphsFromGLB from './getMorphsFromGLB'
import isArea from '@utils/sceneObjects/assertions/isArea'
import isHiddenWall from '@utils/sceneObjects/assertions/isHiddenWall'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isPartition from '@utils/sceneObjects/assertions/isPartition'
import isWall from '@utils/sceneObjects/assertions/isWall'
import { computeItemGroupOBB } from '@utils/sceneObjects/computeItemGroupOBB'
import {
  getExistingTexturesFromScene,
  mapExistingTexturesToNewItemByDisplayName,
  syncZonesWithMappedTextures,
} from '@utils/sceneObjects/getExistingTexturesFromScene'
import getId from '@utils/sceneObjects/getId'
import { itemUsesAccesorioFregaderoCatalog } from '@utils/sceneObjects/isAccesorioFregaderoCatalog'
import { addItemOffset } from '@utils/sceneObjects/items/addItemOffset'
import { getGLB } from '@utils/sceneObjects/items/getGLB'
import { getGLBSize } from '@utils/sceneObjects/items/getGLBSize'
import { getRelativeMatrix } from '@utils/sceneObjects/items/getRelativeMatrix'
import {
  FRONTAL_HEIGHT_RUN_EPS_MM,
  applyClosestFrontalFinish,
  composeFrontalWorldMatrix,
  applyCatalogOptionToFrontal,
  findEncimeraSonOnUnit,
  findExistingFroFinishInScene,
  isFroFrontalItem,
  pickFrontalTextureByEncimeraDescription,
  resolveFroFrontalTextureFromEncimera,
  frontalBelongsToHosts,
  getFrontalHostWidthMm,
  getFrontalRunAnchor,
  getFrontalRunStartPose,
  getFrontalRunsForRinconeroReturnArm,
  getFrontalRunsForWallGroup,
  getRinconeroReturnExtraToHostMm,
  getRinconeroReturnFrontalLengthMm,
  getRunFrontalWorktopYMm,
  getUnitFrontalWorktopYMm,
  isCompactoFinish,
  isFrontalCatalog,
  isFrontalRegletaHost,
  isFrontalRunAttachedToRinconero,
  setFrontalMatrixWorktopY,
  snapshotFrontalFinishes,
  splitFrontalArmByRealGap,
  sumFrontalRunWidthMm,
} from '@utils/sceneObjects/frontals/frontalRuns'
import {
  collectReseatPosesOnHost,
  computeSeatedAppliancePose,
  doesPosePenetrateSolids,
  findHostUnitUnderAppliance,
  findNonPenetratingPosition,
  getFloorApplianceYMm,
  isDecorationMenuUnit,
  isEncimeraHostUnit,
  isSmallCountertopAppliance,
  resolveSmallApplianceFreeDrag,
} from '@utils/sceneObjects/items/seatSmallApplianceOnUnit'
import {
  getChangedCutKitDimensions,
  getCutKitCatalogId,
  isCorteKitCatalog,
} from '@utils/sceneObjects/isCorteKitCatalog'
import {
  fetchUnidadByCatalog,
  isGolaEffective,
  isSelfReferencingGolaUnit,
  resolveArmazonBlanco,
  resolveDrawGolaInheritanceTarget,
  shouldHideTiradoresForParent,
  shouldInheritGolaModoOnDraw,
} from '@context/utils/linked/linkedUnitVariants'
import { syncLinkedItemZonesFromTextures } from '@context/utils/linked/syncLinkedItemZonesFromTextures'
import { newGetDataFromGLB } from '@utils/sceneObjects/items/newGetDataFromGLB'
import { sanitizeDisallowedCostadoOnLinkedItem } from '@utils/sceneObjects/linked/linkedZoneTextureMapping'
import {
  buildDimFlowSnapshot,
  logDimFlow,
} from '@utils/debug/dimensionFlowDiagnostic'
import {
  normalizeInheritSourceId,
  looksLikeZocaloCatalogName,
  resolvePuertaForZocalo,
  resolveZocaloComplementoFromPuerta,
  resolveZocaloInheritSource,
} from '@utils/debug/zocaloHerenciaLog'
import { depthVisualOrderSorter } from '@utils/sceneObjects/linked/depthVisualOrderSorter'
import fetchLinkedChildrenInfo from '@utils/sceneObjects/linked/fetchLinkedChildrenInfo'
import {
  applyEncimeraThicknessMm,
  getEncimeraTextureEntry,
  isEncimeraLinkedSonItem,
  isRbFreeDimensionRegleta,
  logFreeDimensionEncimeraFondo,
  logFreeDimensionFondo,
  logFreeDimensionPuertaMaterial,
  logFreeDimensionZocaloMaterial,
  matchesRbRegletaPreferredEncimera,
  patchEncimeraLinkedTextureIdOnItem,
  buildCcLateralCatalogID,
  clampMorphMm,
  ensureFreeDimensionCatalogBases,
  getFreeDimensionSidebarAlto,
  getColumnLateralOwnCatalogAltoMm,
  getLateralCatalogHeightMm,
  getLateralSceneTargetDimensionsMm,
  getMalLateralHeightCm,
  getMalLateralCatalogFallbacks,
  getMbjLateralCatalogFallbacks,
  getRinconeroRegletaFrontReachMm,
  isFreeDimensionLateralCascadaAnchoCatalog,
  isFreeDimensionLateralOrCostadoCatalog,
  morphHasValidRange,
  placeLinkedEncimeraY,
  RB_REGLETA_ENCIMERA_CODIGO,
  resolveMalLateralCatalogID,
  resolveMbjLateralCatalogID,
  resolveRbRegletaDefaultLinkedChildren,
  freeDimensionUsesLb8060StyleAltoOffset,
  getFreeDimensionCollisionAnchoMm,
  syncFreeDimensionEncimeraAnchoToParent,
  syncFreeDimensionEncimeraFondoToParent,
  usesFreeDimensionScaling,
  shouldUseIndependentZocaloUnit,
  isIndependentZocaloLinkedSon,
  linkedSonKeepsApiLocalTransform,
  placeLinkedIndependentZocalo,
  raiseParentForIndependentZocalo,
  revertIndependentZocaloFloorOffsetIfNeeded,
  syncGvIndependentZocaloToRun,
  isGvMoldingUnit,
  INDEPENDENT_ZOCALO_CATALOG_ID,
  INDEPENDENT_ZOCALO_HEIGHT_MM,
  independentZocaloSceneGlbKeys,
  vector3ToFreeDimensionGlbBaseMm,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import { rebuildSnappedSides } from '@utils/sceneObjects/rebuildSnappedSides'
import { reconcileSnappedSides } from '@utils/sceneObjects/reconcileSnappedSides'
import {
  attachRinconeroIndependentZocaloReturn,
  migrateLegacyIndependentZocalosOnLoad,
} from '@utils/sceneObjects/linked/ensureIndependentZocaloOnProjectLoad'
import {
  OPPOSITE_SIDE,
  SNAP_FREE_SPACE_TOLERANCE_MM,
  getObbCenter,
  getSideFacing,
  getSideFacingSlab,
  getRinconeroHandedness,
  getRinconeroDoorRunSide,
  getRinconeroLTurnSide,
  getRinconeroTouchedFace,
  getSlideStopOffset,
  getTouchedFace,
  isSlabId,
  releaseSnappedSides,
  setSnappedSide,
  type TouchedFace,
} from '@utils/sceneObjects/snappedSides'
import { getSnapCandidates, getSnapGroups, type SnapGroup } from '@utils/sceneObjects/snapGroups'
import {
  COVERING_FAMILIES,
  findAdjacentCabinetOnSide,
  getCabinetFamily,
  isCoveringFamily,
  shouldHostLateral,
} from '@utils/sceneObjects/lateralResponsibility'
import {
  LATERAL_GROUP_GAP_MM,
  splitOrderedGroupByRealGap,
} from '@utils/kitchenInspector/geometry'
import { purgeSceneGlbForCatalog } from '@utils/sceneObjects/purgeSceneGlbForCatalog'
import { setGLTFLoader } from '@utils/sceneObjects/setGLTFLoader'
import {
  buildCatalogIDWithSuffix,
  getBaseCatalogID,
  mapUnitsWithSuffixes,
  updateGlbPathWithSuffix,
  updateReferenciaWithSuffix,
} from '@utils/sceneObjects/suffixUtils'
import { Euler, Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
import { radToDeg } from 'three/src/math/MathUtils'
import { getElementosEnlazadosUnidadAcabados } from '../proxies/getElementosEnlazados'
import {
  fetchPorcelanicas12FrontalTextures,
  getEncimerasFrontales,
} from '../proxies/getEncimerasFrontales'
import getLinkedChildrenInfo from './getLinkedChildrenInfo'

export type ItemActions = ReturnType<typeof createItemActions>
export const dictionary = {
  MAL: 'highItems' as const,
  MBJ: 'lowItems' as const,
}

// Ancho de referencia (mm) con el que está calibrada la separación de la cara frontal
// de un rinconero: los items de este ancho usan el hueco de la regleta tal cual, y el
// resto lo corrigen en proporción a la diferencia de ancho (ver collideItems).
const RINCONERO_FRONT_REFERENCE_WIDTH_MM = 600
// Regla de 3 medida sobre el ancho de 450: 150 mm menos de ancho piden 75 mm menos de
// separación, es decir medio mm de hueco por cada mm de ancho.
const RINCONERO_FRONT_WIDTH_GAP_RATIO = 75 / 150

function deriveContItemRuns<T extends { orderIndex: number }>(
  group: T[],
  keep: (entry: T) => boolean,
  sameRun: (prev: T, curr: T) => boolean,
): T[][] {
  const runs: T[][] = []
  let current: T[] = []
  let prevOrderIndex: number | null = null
  let prevEntry: T | null = null

  for (const entry of group) {
    if (!keep(entry)) {
      if (current.length) runs.push(current)
      current = []
      prevOrderIndex = null
      prevEntry = null
      continue
    }
    const isAdjacent = prevOrderIndex !== null && entry.orderIndex === prevOrderIndex + 1
    if (isAdjacent && prevEntry && sameRun(prevEntry, entry)) {
      current.push(entry)
    } else {
      if (current.length) runs.push(current)
      current = [entry]
    }
    prevOrderIndex = entry.orderIndex
    prevEntry = entry
  }
  if (current.length) runs.push(current)

  return runs.map((run) => run.map((entry, index) => ({ ...entry, orderIndex: index })))
}

/**
 * Iguala Alto/Fondo del lateral a la geometría viva del mueble (sin suelos de catálogo).
 * Compartido por colocación y sync tras cambiar dimensiones.
 */
function applyLateralSceneDimensionsToItem(
  get: Getter,
  set: Setter,
  lateralId: AnyID,
  furniture: LinkedItem,
) {
  const lateral = get().items.get(lateralId)
  const freshFurniture =
    (get().items.get(furniture.id) as LinkedItem | undefined) ?? furniture
  if (!lateral || !isLinkedItem(lateral)) return

  if (usesFreeDimensionScaling(lateral)) {
    ensureFreeDimensionCatalogBases(lateral)
  }

  const { altoMm: hostAltoMm, fondoMm } = getLateralSceneTargetDimensionsMm(freshFurniture)
  // CC/LC traen alto de catálogo propio (CC23060 → 2300). Eso evita comprimir el
  // costado al alto de un MBJ anfitrión (900). Si el anfitrión es COL, el panel
  // debe igualar la columna, no el SKU más cercano.
  const ownColumnAlto = getColumnLateralOwnCatalogAltoMm(lateral)
  const hostIsCovering = isCoveringFamily(getCabinetFamily(freshFurniture))
  const altoMm =
    ownColumnAlto > 0 && !hostIsCovering
      ? Math.max(hostAltoMm, ownColumnAlto)
      : hostAltoMm
  if (altoMm > 0) {
    const sidebarAlto = getFreeDimensionSidebarAlto(altoMm, lateral)
    const altoForMorph = sidebarAlto > 0 ? sidebarAlto : altoMm
    get().editMorph(String(lateralId), 'Alto', altoForMorph)
    set((store) => {
      const lat = store.items.get(lateralId)
      if (!lat || !isLinkedItem(lat)) return
      lat.height = altoMm
      const morphAlto = lat.morphs?.find((m) => m.name === 'Alto')
      if (morphAlto) {
        morphAlto._default = altoForMorph
        morphAlto.default = morphHasValidRange(morphAlto)
          ? clampMorphMm(altoForMorph, morphAlto.min, morphAlto.max)
          : altoForMorph
      }
    })
  }
  if (fondoMm > 0) {
    get().editMorph(String(lateralId), 'Fondo', fondoMm)
    set((store) => {
      const lat = store.items.get(lateralId)
      if (!lat || !isLinkedItem(lat)) return
      lat.thickness = fondoMm
      const morphFondo = lat.morphs?.find((m) => m.name === 'Fondo')
      if (morphFondo) {
        morphFondo._default = fondoMm
        morphFondo.default = morphHasValidRange(morphFondo)
          ? clampMorphMm(fondoMm, morphFondo.min, morphFondo.max)
          : fondoMm
      }
    })
  }

  // Ancho del panel lateral = catálogo del propio lateral (espesor de costado),
  // nunca el Ancho del mueble. En free-dim, freeDimensionCatalogMm.x es la ref GLB.
  const panelAnchoMm = lateral.freeDimensionCatalogMm?.x ?? 0
  if (panelAnchoMm > 0) {
    get().editMorph(String(lateralId), 'Ancho', panelAnchoMm)
    set((store) => {
      const lat = store.items.get(lateralId)
      if (!lat || !isLinkedItem(lat)) return
      lat.width = panelAnchoMm
      const morphAncho = lat.morphs?.find((m) => m.name === 'Ancho')
      if (morphAncho) {
        morphAncho._default = panelAnchoMm
        morphAncho.default = morphHasValidRange(morphAncho)
          ? clampMorphMm(panelAnchoMm, morphAncho.min, morphAncho.max)
          : panelAnchoMm
      }
    })
  }

  set((store) => {
    const lat = store.items.get(lateralId)
    if (!lat || !isLinkedItem(lat)) return
    // Solo bump de revisión para forzar re-render. NUNCA pisar glbSize con
    // {x:revision,y:1,z:1}: eso hacía scaleY = height/(1*1000) y el Alto del
    // lateral quedaba mal (Ancho/Fondo no usaban ese fallback).
    const revision = ((lat as LinkedItem & { glbRevision?: number }).glbRevision ?? 0) + 1
    ;(lat as LinkedItem & { glbRevision?: number }).glbRevision = revision
    const glbMm = lat.freeDimensionGlbBaseMm
    if (glbMm?.x && glbMm?.y) {
      lat.glbSize = {
        x: glbMm.x / 1000,
        y: glbMm.y / 1000,
        z: (glbMm.z > 0 ? glbMm.z : glbMm.x) / 1000,
      } as typeof lat.glbSize
    }
  })

  const lateralAfter = get().items.get(lateralId)
  if (
    lateralAfter &&
    isLinkedItem(lateralAfter) &&
    freeDimensionUsesLb8060StyleAltoOffset(lateralAfter) &&
    lateralAfter.childs &&
    lateralAfter.childs.size > 0
  ) {
    get().updateLinkedSonPositions(String(lateralId), 'Alto')
    get().updateLinkedSonPositions(String(lateralId), 'Fondo')
    get().updateLinkedSonPositions(String(lateralId), 'Ancho')
  }
}

/**
 * Lateral del tramo en línea (brazo puerta) de un rinconero, o null si no lo es.
 * El giro en L no es un lateral X: se coloca por el frontal con rotación 90°.
 * Derecha: tramo en `left`; izquierda: tramo en `right`.
 */
function getRinconeroFreeSide(reference: Item | LinkedItem): SnapSide | null {
  const hand = getRinconeroHandedness(reference.catalogID)
  if (!hand) return null
  return getRinconeroDoorRunSide(hand)
}

/**
 * Caras de la referencia donde puede caer el item nuevo, por orden de preferencia.
 *
 * Un mueble normal ofrece los dos laterales y, si ninguno vale (ocupado o sin sitio en la
 * pared), su frontal. Un rinconero ofrece el tramo en línea (brazo puerta) y el frontal
 * (giro en L, rotado 90°). En una esquina de la estancia no se usa el lateral X del giro:
 * va contra la pared perpendicular. Suelto a mitad de pared manda el frontal (el retorno
 * pegado al rinconero) y después los laterales: el tramo de puerta y el del giro, sin
 * rotar, continuando la fila por la misma pared.
 */
function getPlacementSides(get: Getter, reference: Item | LinkedItem): SnapSide[] {
  const freeSide = getRinconeroFreeSide(reference)
  if (!freeSide) return ['right', 'left', 'front']
  return isRinconeroAtWallCorner(get, reference)
    ? [freeSide, 'front']
    : ['front', freeSide, OPPOSITE_SIDE[freeSide]]
}

/**
 * Marco de la losa en el plano XZ tal y como lo construyen projectItemOnWall y
 * projectItemOnPartition: centro, rotación (X local a lo largo, Z a través del grosor) y
 * media longitud. `null` para las losas que no pasan por projectItemOnSlabFaces (las
 * paredes ocultas tienen su propia proyección, sin recorte a los extremos).
 */
function getSlabFrame(
  slab?: SceneObjects,
): { center: Vector3; quaternion: Quaternion; halfLength: number } | null {
  if (isWall(slab)) {
    const start = toVector3(slab.start)
    const end = toVector3(slab.end)
    start.y = 0
    end.y = 0
    const axis = end.clone().sub(start)
    const length = axis.length()
    if (length === 0) return null
    const quaternion = new Quaternion().setFromUnitVectors(new Vector3(1, 0, 0), axis.normalize())
    const center = start
      .clone()
      .add(end)
      .multiplyScalar(0.5)
      .sub(new Vector3(0, 0, 1).applyQuaternion(quaternion).multiplyScalar((slab.thickness || 0) / 2))
    return { center, quaternion, halfLength: length / 2 }
  }
  if (isPartition(slab)) {
    const center = new Vector3()
    const quaternion = new Quaternion()
    slab.matrix.decompose(center, quaternion, new Vector3())
    return { center, quaternion, halfLength: (slab.width || 0) / 2 }
  }
  return null
}

/**
 * True si el item, puesto en esa cara, se saldría de la pared a la que va pegada la
 * referencia: ya no queda tramo. No basta con dejarlo pasar, porque projectItemOnSlabFaces
 * lo metería a la fuerza dentro (recorta a `slabHalfWidth - halfAlong`) y acabaría montado
 * sobre el vecino. Solo aplica a los laterales: el frontal no consume pared.
 */
function overflowsReferenceWall(
  get: Getter,
  reference: Item | LinkedItem,
  side: SnapSide,
  drawn: Item | LinkedItem,
): boolean {
  if (side !== 'left' && side !== 'right') return false
  const frame = getSlabFrame(get().items.get(reference.snappedWall as AnyID))
  if (!frame) return false

  const { origin, quaternion } = getSidePlacement(drawn, reference, side)
  const center = getObbCenter(
    origin,
    quaternion,
    getCollisionWidthMm(drawn),
    drawn.height || 0,
    drawn.thickness || 0,
  )
  const slabXAxis = new Vector3(1, 0, 0).applyQuaternion(frame.quaternion)
  const halfAlong =
    (getCollisionWidthMm(drawn) / 2) *
      Math.abs(new Vector3(1, 0, 0).applyQuaternion(quaternion).dot(slabXAxis)) +
    ((drawn.thickness || 0) / 2) *
      Math.abs(new Vector3(0, 0, 1).applyQuaternion(quaternion).dot(slabXAxis))
  const along = new Vector3(center.x - frame.center.x, 0, center.z - frame.center.z).dot(slabXAxis)

  return Math.abs(along) + halfAlong > frame.halfLength + SNAP_FREE_SPACE_TOLERANCE_MM
}

/**
 * Ancho (mm) con el que trabaja el snap. Las unidades de dimensión libre (regletas
 * RA/RB/RC, laterales…) no lo tienen en morph.default: ahí ese valor es la referencia de
 * escala del GLB, no el ancho activo.
 */
function getCollisionWidthMm(item: Item | LinkedItem): number {
  const widthMorph = item.morphs?.find((morph) => morph.name === 'Ancho' || morph.name === 'width')
  const activeWidth = widthMorph?.default ?? item.width ?? 0
  const glbWidth = widthMorph?._default ?? item.width ?? 0
  return (
    getFreeDimensionCollisionAnchoMm(item as LinkedItem) ??
    (item.long?.toLowerCase().includes('encimera b3200')
      ? glbWidth
      : item.long !== 'Frontal' || item.long?.toLowerCase().includes('zocalo')
        ? activeWidth
        : glbWidth)
  )
}

/**
 * Centro de la escena en el plano XZ: centro de la caja que envuelve a todas las paredes.
 * En un proyecto sin paredes cae al origen.
 */
function getSceneCenterXZ(get: Getter): Vector3 {
  let minX = Infinity
  let maxX = -Infinity
  let minZ = Infinity
  let maxZ = -Infinity
  get().items.forEach((object) => {
    if (!isWall(object)) return
    for (const [x, z] of [object.start, object.end]) {
      if (!Number.isFinite(x) || !Number.isFinite(z)) continue
      minX = Math.min(minX, x)
      maxX = Math.max(maxX, x)
      minZ = Math.min(minZ, z)
      maxZ = Math.max(maxZ, z)
    }
  })
  if (!Number.isFinite(minX) || !Number.isFinite(minZ)) return new Vector3()
  return new Vector3((minX + maxX) / 2, 0, (minZ + maxZ) / 2)
}

/**
 * Deja el item con el origen de su matriz en `position` y, si se pasa `quaternion`, con
 * esa rotación. Mantiene al día x/y/z, rotation y la OBB de escena.
 */
function placeItemAt(set: Setter, itemId: AnyID, position: Vector3, quaternion?: Quaternion) {
  set((store) => {
    const item = store.items.get(itemId)
    if (!item || (!isItem(item) && !isLinkedItem(item))) return
    const currentQuaternion = new Quaternion()
    const scale = new Vector3()
    item.matrix.decompose(new Vector3(), currentQuaternion, scale)
    const finalQuaternion = quaternion ?? currentQuaternion

    // compose (y no makeRotationFromQuaternion) para no perder la escala de las
    // unidades de dimensión libre.
    item.matrix.compose(position, finalQuaternion, scale)
    item.x = position.x
    item.y = position.y
    item.z = position.z
    item.rotation = radToDeg(new Euler().setFromQuaternion(finalQuaternion).y)

    store.sceneBoundingBoxes.set(itemId, {
      boundingBox: new OBB(
        getObbCenter(position, finalQuaternion, item.width, item.height, item.thickness),
        toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
        new Matrix3().setFromMatrix4(new Matrix4().makeRotationFromQuaternion(finalQuaternion)),
      ),
      type: 'items',
    })
  })
}

/**
 * Origen de matriz y rotación con los que el item queda pegado a la cara `side` de la
 * referencia. No pretende ser la posición final —collideItems la afina después con la
 * holgura de la regleta y los topes al deslizarse—: solo tiene que dejar el centro del
 * item fuera de esa cara para que collideItems elija justo esa. La Y no se toca: es la
 * altura de colocación que trae el backend de la propia unidad.
 *
 * En el frontal de un rinconero no vale con pegarlo y centrarlo: ahí arranca el tramo
 * perpendicular, así que el item gira 90° (su ancho pasa a correr hacia el frente, su
 * frente mira al lado del giro en L) y se alinea a ras del lateral que va contra la pared
 * perpendicular, no del centro del rinconero.
 */
function getSidePlacement(
  item: Item | LinkedItem,
  reference: Item | LinkedItem,
  side: SnapSide,
): { origin: Vector3; quaternion: Quaternion } {
  const referencePosition = new Vector3()
  const referenceQuaternion = new Quaternion()
  reference.matrix.decompose(referencePosition, referenceQuaternion, new Vector3())

  const onXFace = side === 'left' || side === 'right'
  const hand = getRinconeroHandedness(reference.catalogID)
  const isRinconeroFront = !onXFace && hand != null

  // El frente del item mira al lado del giro en L: girando local +Z hasta ese lado, su
  // ancho (local +X) queda corriendo hacia el frente del rinconero.
  const quaternion = isRinconeroFront
    ? referenceQuaternion
        .clone()
        .multiply(
          new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            hand === 'right' ? -Math.PI / 2 : Math.PI / 2,
          ),
        )
    : referenceQuaternion.clone()

  const referenceXAxis = new Vector3(1, 0, 0).applyQuaternion(referenceQuaternion)
  const referenceZAxis = new Vector3(0, 0, 1).applyQuaternion(referenceQuaternion)
  const itemXAxis = new Vector3(1, 0, 0).applyQuaternion(quaternion)
  const itemZAxis = new Vector3(0, 0, 1).applyQuaternion(quaternion)
  /** Semi-extensión de la huella del item proyectada sobre un eje del mundo. */
  const itemExtentOn = (axis: Vector3) =>
    (getCollisionWidthMm(item) / 2) * Math.abs(itemXAxis.dot(axis)) +
    ((item.thickness || 0) / 2) * Math.abs(itemZAxis.dot(axis))

  const normal = (onXFace ? referenceXAxis : referenceZAxis)
    .clone()
    .multiplyScalar(side === 'right' || side === 'front' ? 1 : -1)
  const referenceExtent = onXFace
    ? getCollisionWidthMm(reference) / 2
    : (reference.thickness || 0) / 2

  const center = getObbCenter(
    referencePosition,
    referenceQuaternion,
    getCollisionWidthMm(reference),
    reference.height || 0,
    reference.thickness || 0,
  ).add(normal.multiplyScalar(referenceExtent + itemExtentOn(normal)))

  // Frontal de rinconero: el retorno (puerta + regleta) sobresale del OBB rectangular.
  // Si se pega a thickness, la unidad se mete dentro de la L.
  if (isRinconeroFront) {
    const reachMm = getRinconeroRegletaFrontReachMm(reference)
    const extraFront = Math.max(0, reachMm - (reference.thickness || 0))
    center.add(referenceZAxis.clone().multiplyScalar(extraFront))
    center.add(
      referenceXAxis
        .clone()
        .multiplyScalar(
          (hand === 'right' ? 1 : -1) *
            (getCollisionWidthMm(reference) / 2 - itemExtentOn(referenceXAxis)),
        ),
    )
  }

  const origin = center.sub(
    new Vector3(item.width / 2, item.height / 2, item.thickness / 2).applyQuaternion(quaternion),
  )
  origin.y = item.y

  return { origin, quaternion }
}

/** Pre-coloca el item pegado a la cara `side` de la referencia para que lo vea collideItems. */
function alignItemToSide(
  get: Getter,
  set: Setter,
  itemId: AnyID,
  reference: Item | LinkedItem,
  side: SnapSide,
) {
  const item = get().items.get(itemId)
  if (!item || (!isItem(item) && !isLinkedItem(item))) return
  const { origin, quaternion } = getSidePlacement(item, reference, side)
  placeItemAt(set, itemId, origin, quaternion)
}

/**
 * Apilado vertical (alto sobre bajo y bajo bajo alto): no hay cara lateral en juego, el
 * item va en la misma XZ que la referencia con la altura de colocación de su catálogo.
 */
function stackItemOverReference(
  get: Getter,
  set: Setter,
  itemId: AnyID,
  reference: Item | LinkedItem,
) {
  const item = get().items.get(itemId)
  if (!item || (!isItem(item) && !isLinkedItem(item))) return

  const referencePosition = new Vector3()
  const referenceQuaternion = new Quaternion()
  reference.matrix.decompose(referencePosition, referenceQuaternion, new Vector3())

  placeItemAt(
    set,
    itemId,
    new Vector3(referencePosition.x, item.y, referencePosition.z),
    referenceQuaternion,
  )
}

/**
 * True si esa cara lateral de la referencia ya tiene vecino. Además del registro de
 * snappedSides se comprueba el hueco geométricamente: los items cargados de proyecto o
 * colocados por otras rutas nunca pasaron por collideItems y no lo tienen registrado.
 */
function isLiveOccupant(get: Getter, occupantId: AnyID | undefined, selfId: AnyID) {
  return Boolean(occupantId && occupantId !== selfId && get().items.has(occupantId))
}

function isPlacementSideTaken(
  get: Getter,
  reference: Item | LinkedItem,
  side: SnapSide,
  drawn: Item | LinkedItem,
): boolean {
  if (isLiveOccupant(get, reference.snappedSides?.[side], drawn.id)) return true

  const { origin, quaternion } = getSidePlacement(drawn, reference, side)
  const center = getObbCenter(origin, quaternion, drawn.width, drawn.height, drawn.thickness)
  // Hueco que ocuparía el item pegado a esa cara, encogido por la holgura de contacto
  // para que rozar al vecino de al lado no cuente como ocupación.
  const halfSize = new Vector3(
    Math.max(1, drawn.width / 2 - SNAP_FREE_SPACE_TOLERANCE_MM),
    Math.max(1, drawn.height / 2 - SNAP_FREE_SPACE_TOLERANCE_MM),
    Math.max(1, drawn.thickness / 2 - SNAP_FREE_SPACE_TOLERANCE_MM),
  )

  // getSidePlacement deja el hueco a ras de la cara, pero en el frontal de un rinconero
  // collideItems separa además el saliente de la regleta, así que el ocupante real está
  // más afuera. Se alarga el hueco hacia fuera para cubrir las dos posiciones —el grosor
  // del rinconero como cota superior del saliente— o un mueble poco profundo podría no
  // llegar a solaparlo y la cara pasaría por libre. Con el giro en L el ancho del item
  // corre hacia el frente, así que "hacia fuera" es su X local.
  if (side === 'front' && getRinconeroFreeSide(reference)) {
    const reach = getRinconeroRegletaFrontReachMm(reference) || reference.thickness || 0
    if (reach > 0) {
      const referenceQuaternion = new Quaternion()
      reference.matrix.decompose(new Vector3(), referenceQuaternion, new Vector3())
      const outward = new Vector3(0, 0, 1).applyQuaternion(referenceQuaternion)
      const itemXAxis = new Vector3(1, 0, 0).applyQuaternion(quaternion)
      halfSize.x += reach / 2
      center.add(itemXAxis.multiplyScalar((itemXAxis.dot(outward) >= 0 ? 1 : -1) * (reach / 2)))
    }
  }

  const slot = new OBB(
    center,
    halfSize,
    new Matrix3().setFromMatrix4(new Matrix4().makeRotationFromQuaternion(quaternion)),
  )

  const store = get()
  for (const otherId of store.collisionableItems) {
    if (otherId === drawn.id || otherId === reference.id) continue
    const boundingBox = store.sceneBoundingBoxes.get(otherId as AnyID)?.boundingBox
    if (boundingBox && slot.intersectsOBB(boundingBox)) return true
  }
  return false
}

/**
 * Deja el item pegado a `slabId` (snappedWall guarda pared, partición o pared oculta, cada
 * una con su propia proyección).
 *
 * Heredar solo el id —lo que hace collideItems— no basta: el item queda pegado al vecino
 * pero ni a ras de la losa ni indexado en ella (highItems/lowItems, snappedSides). La
 * proyección conserva la posición a lo largo de la losa —solo corrige la normal y fuerza
 * la rotación—, así que no deshace el pegado que acaba de hacer collideItems.
 *
 * `preserveY` para el apilado vertical, donde la Y ya está resuelta y no debe reajustarse.
 */
function snapItemToSlab(
  get: Getter,
  set: Setter,
  itemId: AnyID,
  slabId: AnyID,
  preserveY = false,
) {
  if (!slabId) return

  const item = get().items.get(itemId)
  if (!item || (!isItem(item) && !isLinkedItem(item))) return

  const slab = get().items.get(slabId)
  const matrix = item.matrix.clone()
  if (isWall(slab)) {
    get().projectItemOnWall(itemId, slabId, matrix, false, preserveY)
  } else if (isPartition(slab)) {
    get().projectItemOnPartition(itemId, slabId, matrix)
  } else if (isHiddenWall(slab)) {
    get().projectItemOnHiddenWall(itemId, slabId, matrix)
  } else {
    return
  }

  set((store) => {
    const drawn = store.items.get(itemId)
    if (drawn && (isItem(drawn) || isLinkedItem(drawn))) drawn.snappedWall = slabId
  })
}

/** Holgura (mm) para dar el lateral del giro de un rinconero por pegado al extremo de su pared. */
const RINCONERO_CORNER_TOLERANCE_MM = 100

/**
 * True si el rinconero está en una esquina de la estancia: el extremo del ancho por el
 * lado del giro en L (el opuesto al tramo de puerta) llega al inicio o al final de la
 * pared a la que va pegado. Solo entonces el retorno arranca la pared perpendicular y ese
 * lateral queda tapado; un rinconero suelto a mitad de pared ofrece el frontal (giro 90°)
 * pegado a él y el lateral del giro como cara libre, sin saltar a otra pared.
 */
function isRinconeroAtWallCorner(get: Getter, rinconero: Item | LinkedItem): boolean {
  const hand = getRinconeroHandedness(rinconero.catalogID)
  if (!hand) return false
  const wall = get().items.get(rinconero.snappedWall as AnyID)
  if (!isWall(wall)) return false

  const position = new Vector3()
  const quaternion = new Quaternion()
  rinconero.matrix.decompose(position, quaternion, new Vector3())
  const cornerPoint =
    getRinconeroLTurnSide(hand) === 'right'
      ? position.add(
          new Vector3(1, 0, 0)
            .applyQuaternion(quaternion)
            .multiplyScalar(getCollisionWidthMm(rinconero)),
        )
      : position

  const start = new Vector3(wall.start[0], 0, wall.start[1])
  const axis = new Vector3(wall.end[0] - wall.start[0], 0, wall.end[1] - wall.start[1])
  const length = axis.length()
  if (length === 0) return false
  const along = new Vector3(cornerPoint.x - start.x, 0, cornerPoint.z - start.z).dot(
    axis.divideScalar(length),
  )
  return along <= RINCONERO_CORNER_TOLERANCE_MM || length - along <= RINCONERO_CORNER_TOLERANCE_MM
}

/**
 * Pared donde sigue el tramo que arranca en el frontal de un rinconero: la contigua por el
 * lado que hace esquina, o sea el lateral que NO es el giro en L.
 *
 * prevWall conecta por el `start` de la pared del rinconero y nextWall por su `end`, así
 * que se elige mirando hacia cuál de los dos apunta ese lateral. Se mide con el eje real
 * en vez de deducirlo del nombre porque el item puede estar en cualquiera de las dos caras
 * de la pared, y en la trasera su X local corre al revés.
 */
function getRinconeroPerpendicularWall(get: Getter, rinconero: Item | LinkedItem): AnyID | null {
  const freeSide = getRinconeroFreeSide(rinconero)
  if (!freeSide) return null
  // Suelto a mitad de pared no hay esquina: el retorno se queda pegado al frontal del
  // rinconero, no salta a la pared contigua.
  if (!isRinconeroAtWallCorner(get, rinconero)) return null

  const wall = get().items.get(rinconero.snappedWall as AnyID)
  if (!isWall(wall)) return null

  const quaternion = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), quaternion, new Vector3())
  // El lateral de la esquina es el opuesto al del giro en L.
  const cornerDirection = new Vector3(freeSide === 'left' ? 1 : -1, 0, 0).applyQuaternion(quaternion)
  const wallAxis = new Vector3(wall.end[0] - wall.start[0], 0, wall.end[1] - wall.start[1])

  const perpendicular = cornerDirection.dot(wallAxis) >= 0 ? wall.nextWall : wall.prevWall
  return perpendicular ? (perpendicular as AnyID) : null
}

/**
 * Con un MAL seleccionado, el siguiente MBJ/COL va al final del tramo bajo de esa pared:
 * pegarlo al alto no sirve, collideItems entre MBJ y MAL solo iguala alturas.
 */
function findLowGroupEndForMal(get: Getter, mal: LinkedItem): LinkedItem | null {
  const getContiguousItems = get().getContiguousItems
  if (!getContiguousItems) return null

  const items = get().items
  const { groups } = getContiguousItems()
  const wallId = mal.snappedWall

  const lowGroups = groups.filter((group) => {
    if (group.length === 0) return false
    if (group.some((entry) => entry.tipoNormalizado === 'MAL')) return false
    if (
      !group.every((entry) => entry.tipoNormalizado === 'MBJ' || entry.tipoNormalizado === 'COL')
    ) {
      return false
    }
    if (!wallId) return true
    return group.some((entry) => {
      const item = items.get(entry.id as AnyID)
      return isLinkedItem(item) && item.snappedWall === wallId
    })
  })

  if (lowGroups.length === 0) return null

  const malPosition = new Vector3()
  mal.matrix.decompose(malPosition, new Quaternion(), new Vector3())

  let bestGroup = lowGroups[0]
  let bestDistance = Infinity
  for (const group of lowGroups) {
    for (const entry of group) {
      const item = items.get(entry.id as AnyID)
      if (!isLinkedItem(item)) continue
      const position = new Vector3()
      item.matrix.decompose(position, new Quaternion(), new Vector3())
      const distance = Math.hypot(position.x - malPosition.x, position.z - malPosition.z)
      if (distance < bestDistance) {
        bestDistance = distance
        bestGroup = group
      }
    }
  }

  const endItem = items.get(bestGroup[bestGroup.length - 1].id as AnyID)
  return isLinkedItem(endItem) ? endItem : null
}

const syncFregaderoAccessoriesInFlight = new Map<string, Promise<unknown>>()

function collectCompactoAccessoriesForParent(
  items: Map<string, SceneObjects>,
  parentId: string,
) {
  const bajoEnc: string[] = []
  const grifo: string[] = []
  const seen = new Set<string>()
  items.forEach((child, id) => {
    if ((child as { firstParentID?: string }).firstParentID !== parentId) return
    if (!itemUsesAccesorioFregaderoCatalog(child as { catalogID?: string; codigo?: string })) return
    if (seen.has(id)) return
    seen.add(id)
    const cid = String(
      (child as { catalogID?: string }).catalogID ?? (child as { codigo?: string }).codigo ?? '',
    ).toUpperCase()
    if (cid.startsWith('COMP-BAJOENC')) bajoEnc.push(id)
    else if (cid.startsWith('COMP-GRIFO')) grifo.push(id)
  })
  return { bajoEnc, grifo }
}

export const createItemActions = (set: Setter, get: Getter) => ({
  drawItem: (
    id: string,
    catalogID: string,
    icono: string,
    visible: boolean, // Solo para puertas es false
    matrix: Matrix4 | null,
    type = 'items' as SceneObjects['type'],
    realCatalogID?: string,
    shouldGoToBackHeight = false,
    skipLinkedSons = false, // Cuando es true, NO crea linkedSons automáticamente
    textureSourceItemId?: AnyID, // Item del que heredar texturas (sin mutar currentObjectID)
    // Cuando es true se ignora `matrix` como posición y la decide placeDrawnItem
    // (lateral libre del item seleccionado, vía collideItems, o centro de la escena).
    autoPlaceFromSelection = false,
    menuOrigin?: { claveMenu?: string; menuPrincipal?: string },
  ): Promise<AnyID> => {
    return new Promise((resolve, reject) => {
      ;(async () => {
        try {
          const glbInScene = get().sceneGLB as Map<string | number, { glb: any; backGLBInfo: any }>
          let backGLBInfo, glb

          const store = get()
          const currentSourceID = normalizeInheritSourceId(
            textureSourceItemId ?? store.currentObjectID,
          )
          // El increment se asignará después con renumberItemsHierarchically

          if (glbInScene.has(catalogID)) {
            const { backGLBInfo: back, glb: _glb } = glbInScene.get(catalogID) as any
            glb = _glb
            backGLBInfo = back
          } else {
            backGLBInfo = await getGLB(glbInScene, catalogID, realCatalogID)
            if (!backGLBInfo) {
              // No mostrar error para laterales que no existen, es un caso esperado
              const isLateral =
                typeof catalogID === 'string' && /^(LA|LB|LC|CC|CB)\d+/.test(catalogID)
              if (isLateral) {
                // console.warn(
                  // `⚠️ Lateral ${catalogID} no existe en el catálogo. Se omite su creación.`,
                // )
              } else {
                console.error(`❌ No se pudo obtener GLB data para catalogID: ${catalogID}`)
              }
              reject(
                new Error(
                  `No se pudo cargar el elemento con catalogID: ${catalogID}. El elemento no existe en el catálogo.`,
                ),
              )
              return
            }
            const referenciaBlender = (backGLBInfo as any)?.referenciaBlender
            if (!referenciaBlender) {
              console.error(`❌ No se encontró referenciaBlender para catalogID: ${catalogID}`)
              reject(
                new Error(
                  `No se encontró la referencia del modelo 3D para catalogID: ${catalogID}`,
                ),
              )
              return
            }
            const gltfLoader = await setGLTFLoader()
            const _glb = await gltfLoader.loadAsync(referenciaBlender)
            glb = _glb
          }

          const extraInfo: { zonas: { idAcabadoUnidad: number; zona: string }[] } = { zonas: [] }
          if (!backGLBInfo.universal) {
            const concha = await API({
              catalogID,
              idUnidades: (backGLBInfo as any).idUnidades,
            }).api.acabadosGetUnidadAcabadosInfo((backGLBInfo as any).idUnidades)
            if (concha && Array.isArray(concha.data?.zonas)) {
              extraInfo.zonas = concha.data.zonas as { idAcabadoUnidad: number; zona: string }[]
            }
          }

          let item = newGetDataFromGLB({
            glbData: glb,
            catalogID,
            id: !backGLBInfo.universal ? getId('linkedItem') : id,
            type: !backGLBInfo.universal ? 'linkedItem' : 'items',
            icono,
            visible,
            backGLBInfo,
            paginaPdf: backGLBInfo.paginaPdf,
            matrix,
            extraInfo,
            shouldGoToBackHeight,
            canCollide: backGLBInfo.colision ?? !backGLBInfo.universal,
            claveMenu: menuOrigin?.claveMenu,
            menuPrincipal: menuOrigin?.menuPrincipal,
          }) as any

          if (menuOrigin?.claveMenu) item.claveMenu = menuOrigin.claveMenu
          if (menuOrigin?.menuPrincipal) item.menuPrincipal = menuOrigin.menuPrincipal

          // KBR*: morphs sintéticos + Regleta (API morphs=null pero regleta sí).
          // Zocalo 4000 (frente + retorno) se crea más abajo; NO apagar hasIndependentZocalo.
          if (
            isKbrRinconeroFamilyCatalog(catalogID) ||
            isKbrRinconeroFamilyCatalog((backGLBInfo as any)?.codigo) ||
            isKbrRinconeroFamilyCatalog((backGLBInfo as any)?.referencia)
          ) {
            if (typeof (backGLBInfo as any).regleta === 'number') {
              item.regleta = (backGLBInfo as any).regleta
              item.regletaMinimo = (backGLBInfo as any).regletaMinimo
              item.regletaMaximo = (backGLBInfo as any).regletaMaximo
            }
            const fromApi = getMorphsFromGLB(backGLBInfo as any)
            item.morphs = ensureSidebarMorphs({ ...item, morphs: fromApi })
            item.hasMorphs = true
          }

          logDimFlow({
            file: 'store/itemActions.ts',
            fn: 'drawItem',
            phase: 'catalog.load',
            reason:
              'drawItem: catálogo cargado (getGLB/cache) + GLB loadAsync(referenciaBlender) → newGetDataFromGLB. Punto de entrada de la unidad en escena.',
            snapshot: buildDimFlowSnapshot(item, {
              referenciaBlender: (backGLBInfo as { referenciaBlender?: string })?.referenciaBlender,
              fromSceneGLBCache: glbInScene.has(catalogID),
              elementosEnlazadosCount: (
                (backGLBInfo as { elementosEnlazados?: unknown[] }).elementosEnlazados ?? []
              ).length,
              hasZocaloInElementosEnlazados: (
                (backGLBInfo as { elementosEnlazados?: { descripcion?: string }[] })
                  .elementosEnlazados ?? []
              ).some((e) => String(e?.descripcion ?? '').toLowerCase().includes('zocalo')),
            }),
          })
          // if (!item.universal) {
          //   item.depth = 0
          //   item.type = 'linkedItem'
          //   item.id = getId('linkedItem')
          //   item.childrenOrder = []
          // }

          if (!item.universal) {
            item.depth = 0
            // Respetar el tipo si se especifica 'linkedSon'
            item.type = type === 'linkedSon' ? 'linkedSon' : 'linkedItem'
            item.id = type === 'linkedSon' ? id : getId('linkedItem')
            item.childrenOrder = []
          }

          // El increment se asignará después con renumberItemsHierarchically
          item.increment = '0'

          const toolbarAcabadoBlancoPreference = store.toolbarAcabadoBlanco
          const sliderForcesPetroleo = toolbarAcabadoBlancoPreference === false
          const sliderForcesBlanco = toolbarAcabadoBlancoPreference === true

          if (!item.universal && item.textures && item.textures.length > 0) {
            const isVitrinaCatalog = catalogID.toUpperCase().startsWith('KABV')
            const hasVitrinaModel = item.textures.some(
              (texture: any) => texture.modelo === 'Vitrina',
            )

            const shouldInheritTextures =
              (!isVitrinaCatalog && !hasVitrinaModel) || (isVitrinaCatalog && hasVitrinaModel)

            if (shouldInheritTextures) {
              const existingTextures = getExistingTexturesFromScene(
                store.items,
                catalogID,
                currentSourceID,
              )

              if (existingTextures && existingTextures.length > 0) {
                // Incluye Armazón (como Puerta). PE se sincroniza luego con idcambioarmazon del destino.
                item.textures = mapExistingTexturesToNewItemByDisplayName(
                  existingTextures,
                  item.textures,
                  item,
                )

                const syncResult = syncZonesWithMappedTextures(
                  item.zones,
                  item.textures,
                  store.items,
                  item.type,
                  item.description,
                )

                item.zones = syncResult.zones
                syncLinkedItemZonesFromTextures(item, {
                  itemId: item.id,
                  catalogID: String(item.catalogID ?? ''),
                  source: 'drawItem:inheritTextures',
                })
                sanitizeDisallowedCostadoOnLinkedItem(item)
              }
            }
          }

          if (!item.universal && isLinkedItem(item)) {
            if (sliderForcesBlanco) {
              item.ArmazonBlanco = true
            } else if (sliderForcesPetroleo) {
              item.ArmazonBlanco = false
            }

            const sourceItemForGola = currentSourceID ? store.items.get(currentSourceID) : null
            if (
              shouldInheritGolaModoOnDraw(
                item as LinkedItem,
                sourceItemForGola && isLinkedItem(sourceItemForGola)
                  ? (sourceItemForGola as LinkedItem)
                  : null,
                store.toolbarGolaActiva,
              )
            ) {
              ;(item as LinkedItem).golaActiva = true
            }
          }

          // Crear linkedSons solo si NO se solicita omitirlos
          if (
            !skipLinkedSons &&
            (backGLBInfo.elementosEnlazados || backGLBInfo.elementosEnlazadosAcabadosPorDefecto)
          ) {
            let data =
              backGLBInfo.elementosEnlazados ?? backGLBInfo.elementosEnlazadosAcabadosPorDefecto
            if (isRbFreeDimensionRegleta(backGLBInfo)) {
              data = resolveRbRegletaDefaultLinkedChildren(data)
              if (!data.some((el) => matchesRbRegletaPreferredEncimera(el))) {
                const apiEnc = (
                  backGLBInfo.elementosEnlazados ??
                  backGLBInfo.elementosEnlazadosAcabadosPorDefecto ??
                  []
                ).find((el: { descripcion?: string }) => /encimera/i.test(el.descripcion ?? ''))
                try {
                  const encRes = await API().api.unidadesGetUnidadPost({
                    codigo: RB_REGLETA_ENCIMERA_CODIGO,
                    date: '2023-05-12',
                    catalogoId: RB_REGLETA_ENCIMERA_CODIGO,
                  })
                  const unidad = encRes?.data
                  if (unidad) {
                    data = [
                      ...data.filter(
                        (el: { descripcion?: string }) => !/encimera/i.test(el.descripcion ?? ''),
                      ),
                      {
                        descripcion: unidad.descripcion ?? RB_REGLETA_ENCIMERA_CODIGO,
                        unidad,
                        posicionY: apiEnc?.posicionY ?? null,
                        posicionX: apiEnc?.posicionX ?? 0,
                        posicionZ: apiEnc?.posicionZ ?? 0,
                        nodoPadre: apiEnc?.nodoPadre ?? 0,
                        nodo: apiEnc?.nodo ?? 1,
                      },
                    ]
                  }
                } catch (e) {
                  // console.warn(
                    // '[RB regleta] No se pudo cargar encimera por defecto',
                    // RB_REGLETA_ENCIMERA_CODIGO,
                    // e,
                  // )
                }
              }
            }
            const loader = await setGLTFLoader()
            const info = await fetchLinkedChildrenInfo(backGLBInfo.idUnidades)

            const orderedOptions: string[] = []
            const nestingLevelArray = [data]
            const optionsObject: Record<string, string> = {}
            const orderMap = new Map()

            const options = await API().api.acabadosGetElementosEnlazadosUnidadAcabados(
              backGLBInfo.idUnidades,
            )
            for (const [parentNode, childNodeObj] of Object.entries(options.data)) {
              for (const [childNode, info] of Object.entries(childNodeObj)) {
                info.forEach((el) => {
                  orderMap.set(el.descripcion, {
                    parentNode: Number(parentNode),
                    option: Number(childNode),
                    visualOrder: el.ordenVisual,
                  })
                })
              }
            }

            // Procesar cada nivel en paralelo, pero los niveles secuencialmente
            for (let i = 0; i < nestingLevelArray.length; i++) {
              // Recopilar todas las promesas para este nivel
              const levelPromises = nestingLevelArray[i].map(async (rawBackData: any) => {
                const linkedID = getId('linkedSon')
                const idElementosEnlazados = rawBackData.idElementosEnlazados
                const itemInfo = getLinkedChildrenInfo(rawBackData, info, idElementosEnlazados)
                const backData = rawBackData.unidad ?? rawBackData[0]

                if (rawBackData.childs?.length) {
                  if (!Array.isArray(nestingLevelArray[i + 1])) {
                    nestingLevelArray[i + 1] = []
                  }
                  nestingLevelArray[i + 1].push(...rawBackData.childs)
                }

                if (
                  isIndependentZocaloLinkedSon({
                    catalogID: backData?.codigo,
                    description: backData?.descripcion,
                    typeOfSon: (itemInfo as { typeOfSon?: string })?.typeOfSon,
                  }) &&
                  !shouldUseIndependentZocaloUnit(item)
                ) {
                  return null
                }

                if (!glb || !catalogID) {
                  return null
                }

                // Crear promesas para carga de GLB y API en paralelo
                let glbPromise: Promise<any>
                if (glbInScene.has(backData?.codigo)) {
                  const cached = glbInScene.get(backData?.codigo)
                  if (cached) {
                    glbPromise = Promise.resolve(cached.glb)
                  } else {
                    glbPromise = loader.loadAsync(backData?.referenciaBlender)
                  }
                } else {
                  glbPromise = loader.loadAsync(backData?.referenciaBlender)
                }

                const linkedUnidadId = Number(
                  rawBackData?.unidad?.idUnidades ?? backData?.idUnidades ?? 0,
                )
                // Si acabados falla, el hijo (p. ej. Encimera B80new del RB805) igual se crea.
                // Antes el rechazo descartaba el linkedSon y el precio no lo recibía.
                const extraInfoPromise =
                  linkedUnidadId > 0
                    ? API()
                        .api.acabadosGetUnidadAcabadosInfo(linkedUnidadId)
                        .catch(() => ({ data: { zonas: [] } }))
                    : Promise.resolve({ data: { zonas: [] } })

                // Ejecutar ambas promesas en paralelo
                const [sonGlbData, extraInfo] = await Promise.all([glbPromise, extraInfoPromise])

                return {
                  linkedID,
                  rawBackData,
                  idElementosEnlazados,
                  itemInfo,
                  backData,
                  depth: i + 1,
                  sonGlbData,
                  extraInfo,
                }
              })

              // Esperar a que todas las promesas de este nivel se completen en paralelo
              const levelResults = await Promise.allSettled(levelPromises)

              // Procesar los resultados de este nivel
              for (const result of levelResults) {
                if (result.status === 'rejected') {
                  console.error('Error procesando hijo:', result.reason)
                  continue
                }

                const childData = result.value
                if (!childData) continue

                const {
                  linkedID,
                  rawBackData,
                  idElementosEnlazados,
                  itemInfo,
                  backData,
                  depth,
                  sonGlbData,
                  extraInfo,
                } = childData

                const linkedSon = newGetDataFromGLB({
                  id: linkedID,
                  glbData: sonGlbData,
                  type: 'linkedSon',
                  catalogID: backData?.codigo,
                  visible: true,
                  backGLBInfo: backData,
                  depth: depth,
                  firstParentID: item.id,
                  itemInfo,
                  extraInfo: {
                    zonas:
                      extraInfo.data?.zonas?.map((z: any) => ({
                        idAcabadoUnidad: z.idAcabadoUnidad,
                        zona: z.zona ?? '',
                      })) ?? [],
                  },
                }) as any

                if (linkedSon.description === 'Tirador') {
                  linkedSon.idElementosEnlazados = idElementosEnlazados
                }

                // El increment se asignará después con renumberItemsHierarchically
                linkedSon.increment = '0'

                const isFregaderoSon =
                  linkedSon.typeOfSon === 'Fregadero' ||
                  linkedSon.description?.toLowerCase().includes('fregadero')
                const isApplianceSon =
                  isFregaderoSon ||
                  linkedSon.typeOfSon === 'Placa' ||
                  linkedSon.description?.toLowerCase().includes('placa') ||
                  linkedSon.typeOfSon === 'Grifo' ||
                  linkedSon.typeOfSon === 'TPCGrifo' ||
                  linkedSon.description?.toLowerCase().includes('grifo') ||
                  linkedSon.typeOfSon === 'Horno' ||
                  linkedSon.description?.toLowerCase().includes('horno') ||
                  linkedSon.description?.toLowerCase().includes('micro')
                if (isApplianceSon) {
                  linkedSon.idElementosEnlazados = idElementosEnlazados
                }
                if (isApplianceSon || linkedSonKeepsApiLocalTransform(linkedSon as any)) {
                  const apiX = rawBackData?.posicionX
                  const apiY = rawBackData?.posicionY
                  const apiZ = rawBackData?.posicionZ
                  if (typeof apiX === 'number') (linkedSon as any).posicionX = apiX
                  if (typeof apiY === 'number') {
                    ;(linkedSon as any).posicionY = apiY
                    ;(linkedSon as any).baseY = apiY
                  }
                  if (typeof apiZ === 'number') (linkedSon as any).posicionZ = apiZ
                }

                if (isEncimeraLinkedSonItem(linkedSon)) {
                  patchEncimeraLinkedTextureIdOnItem(linkedSon, extraInfo?.zonas)
                }

                if (linkedSon.textures && linkedSon.textures.length > 0) {
                  const childCatalogID = linkedSon.catalogID?.toString().toUpperCase() ?? ''
                  const isChildVitrinaCatalog = childCatalogID.startsWith('KABV')
                  const childHasVitrinaModel = linkedSon.textures.some(
                    (texture: any) => texture.modelo === 'Vitrina',
                  )

                  const shouldInheritTextures =
                    (!isChildVitrinaCatalog && !childHasVitrinaModel) ||
                    (isChildVitrinaCatalog && childHasVitrinaModel)

                  if (shouldInheritTextures) {
                    const existingTextures = getExistingTexturesFromScene(
                      store.items,
                      linkedSon.catalogID,
                      currentSourceID,
                    )

                    if (existingTextures) {
                      // Zocalo 4000: forzar zoneDisplayName para casar con lo recogido del origen.
                      if (isIndependentZocaloLinkedSon(linkedSon)) {
                        linkedSon.textures = linkedSon.textures.map((t: any) => ({
                          ...t,
                          zoneDisplayName: 'Zocalo',
                          zoneName: t.zoneName || 'Zocalo',
                          zone: t.zone || 'Zocalo',
                        }))
                      }

                      linkedSon.textures = mapExistingTexturesToNewItemByDisplayName(
                        existingTextures,
                        linkedSon.textures,
                        linkedSon,
                      )

                      const syncResult = syncZonesWithMappedTextures(
                        linkedSon.zones,
                        linkedSon.textures,
                        store.items,
                        linkedSon.type,
                        linkedSon.description,
                      )
                      linkedSon.zones = syncResult.zones
                      syncLinkedItemZonesFromTextures(linkedSon, {
                        itemId: linkedSon.id,
                        catalogID: String(linkedSon.catalogID ?? ''),
                        source: 'drawItem:inheritLinkedSonTextures',
                      })
                      sanitizeDisallowedCostadoOnLinkedItem(linkedSon)

                      if (isEncimeraLinkedSonItem(linkedSon)) {
                        applyEncimeraThicknessMm(linkedSon)
                        if (usesFreeDimensionScaling(item)) {
                          const apiY = rawBackData.posicionY
                          placeLinkedEncimeraY(linkedSon, item, {
                            backendPosicionY: typeof apiY === 'number' ? apiY : undefined,
                            backendPosicionZ:
                              typeof rawBackData.posicionZ === 'number' ? rawBackData.posicionZ : 0,
                          })
                          syncFreeDimensionEncimeraAnchoToParent(linkedSon, item)
                          syncFreeDimensionEncimeraFondoToParent(linkedSon, item)
                          logFreeDimensionEncimeraFondo('drawItem.inheritTextures', linkedSon, item)
                        }

                        if (
                          syncResult.inheritedTexture &&
                          linkedSon.textures &&
                          linkedSon.textures.length > 0
                        ) {
                          const encTex = linkedSon.textures.find((t: any) => t.zone === 'Encimera')
                          if (encTex) {
                            encTex.name = syncResult.inheritedTexture.name
                            encTex.url = syncResult.inheritedTexture.url
                            encTex.description = syncResult.inheritedTexture.description
                            encTex.acabado = syncResult.inheritedTexture.acabado

                            const event = new CustomEvent('textureInherited', {
                              detail: {
                                texture: syncResult.inheritedTexture,
                                elementId: linkedSon.id,
                              },
                            })

                            window.dispatchEvent(event)
                          }
                        }
                      }
                    }
                  }
                }

                if (linkedSon.description === 'Tirador') {
                  const elementosEnlazadosOriginales =
                    backGLBInfo.elementosEnlazados ??
                    backGLBInfo.elementosEnlazadosAcabadosPorDefecto ??
                    []
                  let original = null
                  if (rawBackData && rawBackData.idElementosEnlazados) {
                    original = elementosEnlazadosOriginales.find(
                      (el) => el.idElementosEnlazados === rawBackData.idElementosEnlazados,
                    )
                  } else if (rawBackData && rawBackData.descripcion) {
                    original = elementosEnlazadosOriginales.find(
                      (el) => el.descripcion === rawBackData.descripcion,
                    )
                  }
                  if (original) {
                    if (original.nodo !== undefined) {
                      linkedSon.nodo = original.nodo
                    }
                    replaceTiradorSlotPositions(linkedSon, original)
                  } else {
                    replaceTiradorSlotPositions(linkedSon, rawBackData)
                  }
                  linkedSon.handPosition = 'Centro'

                  const sourceForGola = currentSourceID ? store.items.get(currentSourceID) : null
                  applyTiradorPlacement(linkedSon, item, {
                    hideForGola:
                      isLinkedItem(item) &&
                      shouldHideTiradoresForParent(item as LinkedItem, {
                        toolbarGolaActiva: store.toolbarGolaActiva,
                        sourceItem:
                          sourceForGola && isLinkedItem(sourceForGola)
                            ? (sourceForGola as LinkedItem)
                            : null,
                      }),
                  })
                }

                if (isEncimeraLinkedSonItem(linkedSon) && usesFreeDimensionScaling(item)) {
                  const apiY = rawBackData.posicionY
                  placeLinkedEncimeraY(linkedSon, item, {
                    backendPosicionY: typeof apiY === 'number' ? apiY : undefined,
                    backendPosicionZ:
                      typeof rawBackData.posicionZ === 'number' ? rawBackData.posicionZ : 0,
                  })
                  logFreeDimensionEncimeraFondo('drawItem.placeSon', linkedSon, item)
                  logFreeDimensionFondo('drawItem.parent', item, {
                    childCatalog: linkedSon.catalogID,
                    childThickness: linkedSon.thickness,
                    childWidth: linkedSon.width,
                  })
                }

                if (isIndependentZocaloLinkedSon(linkedSon) && shouldUseIndependentZocaloUnit(item)) {
                  item.hasIndependentZocalo = true
                  item.independentZocaloHeightMm = INDEPENDENT_ZOCALO_HEIGHT_MM
                  item.independentZocaloSonId = linkedID
                  revertIndependentZocaloFloorOffsetIfNeeded(item)
                  raiseParentForIndependentZocalo(item) // no-op: no subir padre
                  const sonScene = sonGlbData?.scene ?? sonGlbData
                  placeLinkedIndependentZocalo(linkedSon, item, {
                    glbSizeMm: sonScene
                      ? vector3ToFreeDimensionGlbBaseMm(getGLBSize(sonScene))
                      : undefined,
                    backGLBInfo: backData as { ancho?: number; alto?: number; fondo?: number },
                    items: get().items,
                  })
                }

                set((store) => {
                  orderedOptions.push(
                    `${linkedSon.parentOption}-${linkedSon.option}-${linkedSon.visualOrder}!${linkedID}`,
                  )
                  // El increment se asignará después con renumberItemsHierarchically
                  linkedSon.increment = '0'
                  optionsObject[linkedSon.option] = linkedID
                  store.items.set(linkedID, linkedSon)
                  store.sceneGLB.set(linkedSon.catalogID, {
                    glb: sonGlbData,
                    backGLBInfo: backData,
                  })
                })
              }
            }

            // Después de procesar todos los hijos, establecer relaciones padre-hijo
            const parentOptionsOrdered = orderedOptions.sort(depthVisualOrderSorter)
            const firstLayer = parentOptionsOrdered.filter((el) => el.startsWith('0-'))

            for (const [parentId, parentValue] of Object.entries(optionsObject)) {
              const findAllChildren = orderedOptions.filter((el) => el.startsWith(`${parentId}-`))
              set((store) => {
                const childSon = store.items.get(parentValue)
                if (!childSon) return
                childSon.childs = new Set(findAllChildren.map((el) => el.split('!')[1]))
              })
            }

            item.childrenOrder = parentOptionsOrdered
            item.childs = new Set(firstLayer.map((el) => el.split('!')[1]))
            item.orderMap = orderMap

            if (options.data && options.data['0'] && options.data['0']['Tirador']) {
              item.tiradorOptions = options.data['0']['Tirador'] as ItemWithOptions[]
            }

            if (options.data && options.data['0'] && options.data['0']['Fregadero']) {
              item.fregaderoOptions = options.data['0']['Fregadero'] as ItemWithOptions[]
            }

            if (options.data && options.data['0']) {
              const grifoKey = Object.keys(options.data['0']).find((key) =>
                key.toLowerCase().includes('grifo'),
              )
              if (grifoKey) {
                ;(item as any).grifoOptions = options.data['0'][grifoKey] as ItemWithOptions[]
              }
            }

            if (options.data && options.data['0']) {
              const placaKey = Object.keys(options.data['0']).find((key) =>
                key.toLowerCase().includes('placa'),
              )
              if (placaKey) {
                ;(item as any).placaOptions = options.data['0'][placaKey] as ItemWithOptions[]
              }
            }
          }

          // MBJ / COL no-DECO: zócalo linkedSon (COL DECO no lleva tira).
          if (
            !skipLinkedSons &&
            item.type === 'linkedItem' &&
            shouldUseIndependentZocaloUnit(item)
          ) {
            let zocaloSonId = item.independentZocaloSonId as string | undefined
            if (!zocaloSonId && item.childs?.size) {
              for (const cid of item.childs) {
                const child = get().items.get(cid as AnyID)
                if (
                  child &&
                  isIndependentZocaloLinkedSon(child as any) &&
                  !(child as { isIndependentZocaloReturn?: boolean }).isIndependentZocaloReturn
                ) {
                  zocaloSonId = String(cid)
                  break
                }
              }
            }

            if (!zocaloSonId) {
              try {
                const zocaloCatalog = INDEPENDENT_ZOCALO_CATALOG_ID
                const sceneGlbMap = get().sceneGLB as Map<
                  string | number,
                  { glb: any; backGLBInfo: any }
                >
                let zocaloBack = sceneGlbMap.get(zocaloCatalog)?.backGLBInfo
                let zocaloGlb = sceneGlbMap.get(zocaloCatalog)?.glb
                if (!zocaloBack || !zocaloGlb) {
                  zocaloBack = await getGLB(sceneGlbMap, zocaloCatalog)
                  if (zocaloBack?.referenciaBlender) {
                    const loader = await setGLTFLoader()
                    zocaloGlb = await loader.loadAsync(zocaloBack.referenciaBlender)
                  } else {
                    console.error(
                      '[drawItem] ZOCALO 4000 sin referenciaBlender',
                      { catalogID: item.catalogID, zocaloBack },
                    )
                  }
                }
                if (zocaloBack && zocaloGlb) {
                  const linkedID = getId('linkedSon')
                  const zocaloExtra: { zonas: { idAcabadoUnidad: number; zona: string }[] } = {
                    zonas: [],
                  }
                  if ((zocaloBack as any).idUnidades) {
                    try {
                      const concha = await API().api.acabadosGetUnidadAcabadosInfo(
                        (zocaloBack as any).idUnidades,
                      )
                      if (concha && Array.isArray(concha.data?.zonas)) {
                        zocaloExtra.zonas = concha.data.zonas as {
                          idAcabadoUnidad: number
                          zona: string
                        }[]
                      }
                    } catch {
                      /* opciones de zona opcionales */
                    }
                  }
                  const linkedSon = newGetDataFromGLB({
                    id: linkedID,
                    glbData: zocaloGlb,
                    type: 'linkedSon',
                    catalogID: (zocaloBack as any).codigo ?? zocaloCatalog,
                    firstParentID: item.id,
                    visible: true,
                    backGLBInfo: zocaloBack,
                    extraInfo: zocaloExtra,
                    itemInfo: {
                      x: 0,
                      y: 0,
                      z: 0,
                      parentOption: 0,
                      option: 9900,
                      visualOrder: 999,
                      typeOfSon: 'Zocalo',
                      description: (zocaloBack as any).descripcion ?? 'Zocalo',
                      handPosition: 'Normal',
                      matrix: new Matrix4().setPosition(0, 0, 0),
                    },
                    canCollide: false,
                  }) as any

                  item.hasIndependentZocalo = true
                  item.independentZocaloHeightMm = INDEPENDENT_ZOCALO_HEIGHT_MM
                  item.independentZocaloSonId = linkedID
                  revertIndependentZocaloFloorOffsetIfNeeded(item)
                  raiseParentForIndependentZocalo(item) // no-op
                  const zocaloScene = zocaloGlb?.scene ?? zocaloGlb
                  placeLinkedIndependentZocalo(linkedSon, item, {
                    glbSizeMm: zocaloScene
                      ? vector3ToFreeDimensionGlbBaseMm(getGLBSize(zocaloScene))
                      : undefined,
                    backGLBInfo: zocaloBack as { ancho?: number; alto?: number; fondo?: number },
                    items: get().items,
                  })

                  if (!item.childs) item.childs = new Set()
                  item.childs.add(linkedID)
                  const orderKey =
                    `0-9900-999!${linkedID}` as `${number}-${number}-${number}!${string}`
                  if (!item.childrenOrder) item.childrenOrder = []
                  item.childrenOrder = [...item.childrenOrder, orderKey]

                  set((store) => {
                    linkedSon.increment = '0'
                    store.items.set(linkedID, linkedSon)
                    const payload = { glb: zocaloGlb, backGLBInfo: zocaloBack }
                    for (const key of independentZocaloSceneGlbKeys(
                      linkedSon.catalogID ?? (zocaloBack as any)?.codigo,
                    )) {
                      store.sceneGLB.set(key, payload)
                    }
                  })
                  zocaloSonId = linkedID
                } else {
                  console.error(
                    `[drawItem] No se pudo cargar ${INDEPENDENT_ZOCALO_CATALOG_ID} para`,
                    item.catalogID,
                    { hasBack: Boolean(zocaloBack), hasGlb: Boolean(zocaloGlb) },
                  )
                }
              } catch (e) {
                console.error('[drawItem] Error creando zócalo independiente:', e)
              }
            } else {
              const existing = get().items.get(zocaloSonId as AnyID) as any
              if (existing) {
                item.hasIndependentZocalo = true
                item.independentZocaloHeightMm = INDEPENDENT_ZOCALO_HEIGHT_MM
                item.independentZocaloSonId = zocaloSonId
                revertIndependentZocaloFloorOffsetIfNeeded(item)
                raiseParentForIndependentZocalo(item) // no-op
                const cached = get().sceneGLB.get(String(existing.catalogID))
                const glbRoot = cached?.glb?.scene ?? cached?.glb
                placeLinkedIndependentZocalo(existing, item, {
                  glbSizeMm: glbRoot
                    ? vector3ToFreeDimensionGlbBaseMm(getGLBSize(glbRoot))
                    : undefined,
                  backGLBInfo: cached?.backGLBInfo as
                    | { ancho?: number; alto?: number; fondo?: number }
                    | undefined,
                  items: get().items,
                })
                set((store) => {
                  store.items.set(zocaloSonId as AnyID, existing)
                })
              }
            }

            // Rinconeros: segunda tira del zócalo en L (retorno).
            if (item.independentZocaloSonId || zocaloSonId) {
              try {
                const zocaloCatalog = INDEPENDENT_ZOCALO_CATALOG_ID
                const sceneGlbMap = get().sceneGLB as Map<
                  string | number,
                  { glb: any; backGLBInfo: any }
                >
                let zocaloBack = sceneGlbMap.get(zocaloCatalog)?.backGLBInfo
                let zocaloGlb = sceneGlbMap.get(zocaloCatalog)?.glb
                if (!zocaloBack || !zocaloGlb) {
                  zocaloBack = await getGLB(sceneGlbMap, zocaloCatalog)
                  if (zocaloBack?.referenciaBlender) {
                    const loader = await setGLTFLoader()
                    zocaloGlb = await loader.loadAsync(zocaloBack.referenciaBlender)
                  }
                }
                if (zocaloBack && zocaloGlb) {
                  // Asegurar padre en el store antes del attach (drawItem muta `item` en vivo).
                  set((store) => {
                    if (item?.id) store.items.set(item.id as AnyID, item)
                  })
                  attachRinconeroIndependentZocaloReturn({
                    get,
                    set,
                    parentId: String(item.id),
                    zocaloGlb,
                    zocaloBack,
                  })
                  // Immer clona el padre en el attach: sincronizar retorno al `item` vivo
                  // o el set final de drawItem lo borra.
                  const parentAfterReturn = get().items.get(item.id as AnyID) as LinkedItem | undefined
                  if (parentAfterReturn) {
                    // Tras set() Immer congela `item`; no mutar — clonar campos del retorno.
                    item = {
                      ...item,
                      hasIndependentZocalo: true,
                      independentZocaloHeightMm:
                        parentAfterReturn.independentZocaloHeightMm ?? INDEPENDENT_ZOCALO_HEIGHT_MM,
                      independentZocaloSonId:
                        parentAfterReturn.independentZocaloSonId ?? item.independentZocaloSonId,
                      independentZocaloReturnSonId:
                        parentAfterReturn.independentZocaloReturnSonId,
                      childs: parentAfterReturn.childs
                        ? new Set(parentAfterReturn.childs)
                        : item.childs,
                      childrenOrder: parentAfterReturn.childrenOrder
                        ? [...parentAfterReturn.childrenOrder]
                        : item.childrenOrder,
                    }
                  }
                }
              } catch (e) {
                // console.warn('[drawItem] Error creando zócalo retorno rinconero:', e)
              }
            }

            // Zócalo 4000: acabado según la puerta. Si no hay zócalo en escena
            // (primer dibujo), se busca el complemento de la puerta.
            if (zocaloSonId) {
              const inheritSource = resolveZocaloInheritSource(
                get().items,
                currentSourceID,
                String(item.id),
              )
              const doorSource = resolvePuertaForZocalo(get().items, currentSourceID, item)
              const sourceItem = inheritSource?.sourceItem ?? doorSource?.parent ?? null
              const sourceZocalo = inheritSource?.sourceZocalo ?? null
              const destZocaloBefore = get().items.get(zocaloSonId as AnyID)
              const srcTex =
                sourceZocalo && Array.isArray(sourceZocalo.textures) && sourceZocalo.textures.length
                  ? sourceZocalo.textures.find(
                      (t: any) =>
                        /z[oó]calo/i.test(String(t.zoneDisplayName ?? '')) ||
                        /z[oó]calo/i.test(String(t.zoneName ?? '')) ||
                        /z[oó]calo/i.test(String(t.zone ?? '')),
                    ) || sourceZocalo.textures[0]
                  : destZocaloBefore?.textures?.[0]
              const doorResolved = doorSource
                ? await resolveZocaloComplementoFromPuerta(
                    doorSource.puertaTex,
                    doorSource.parent,
                  )
                : { complementoName: '', resolvedFrom: '' as const }
              const sourceZocaloName = String(
                srcTex?.complemento || srcTex?.name || srcTex?.description || '',
              ).trim()
              const complementoName = String(
                doorResolved.complementoName ||
                  (looksLikeZocaloCatalogName(sourceZocaloName) ? sourceZocaloName : ''),
              ).trim()
              if (complementoName && typeof get().updateZocaloWithCompleteTexture === 'function') {
                try {
                  await get().updateZocaloWithCompleteTexture(
                    zocaloSonId as AnyID,
                    complementoName,
                    Number(srcTex?.linkedTextureID) || 0,
                    Number(sourceZocalo?.idUnidades ?? destZocaloBefore?.idUnidades) || undefined,
                  )
                  const returnId = (item as LinkedItem).independentZocaloReturnSonId
                  if (returnId) {
                    await get().updateZocaloWithCompleteTexture(
                      returnId as AnyID,
                      complementoName,
                      Number(srcTex?.linkedTextureID) || 0,
                      Number(sourceZocalo?.idUnidades ?? destZocaloBefore?.idUnidades) ||
                        undefined,
                    )
                  }
                } catch {
                  // El acabado de catálogo del zócalo se queda si el update falla.
                }
              }
            }

            // Reposicionar encimera/tiradores y sync ancho/fondo tras crear/ubicar zócalo.
            if (item.hasIndependentZocalo && item.childs?.size) {
              get().updateLinkedSonPositions(String(item.id), 'Ancho')
              get().updateLinkedSonPositions(String(item.id), 'Fondo')
              get().updateLinkedSonPositions(String(item.id), 'Alto')
            }
          }

          if (item.type === 'linkedItem') {
            try {
              const zocaloTexture = item.textures.find((el) => el.zoneDisplayName === 'Zocalo')
              let linkedTextureID =
                zocaloTexture && typeof zocaloTexture.linkedTextureID === 'number'
                  ? zocaloTexture.linkedTextureID
                  : 0

              // MBJ/COL con zócalo hijo (Zocalo 4000): opciones desde el hijo.
              if (!linkedTextureID && item.independentZocaloSonId) {
                const zocaloSon = get().items.get(item.independentZocaloSonId as AnyID)
                const sonTex = zocaloSon?.textures?.[0]
                if (sonTex && typeof sonTex.linkedTextureID === 'number') {
                  linkedTextureID = sonTex.linkedTextureID
                }
              }

              if (linkedTextureID) {
                const api = API()
                const info = await api.api.acabadosGetAcabadoZonaModeloAcabadoInfo(linkedTextureID)
                const data = (info as AcabadoZonaModeloAcabadoInfo)?.data
                const acabados =
                  data && Array.isArray(data)
                    ? data.find((z) => (z?.zona || '').toLowerCase().includes('zocalo'))
                        ?.acabados ||
                      data[0]?.acabados ||
                      []
                    : []

                const idAcabado = acabados[0]?.idAcabadoUnidadModeloAcabado

                if (idAcabado) {
                  const texturasResp = await api.api.acabadosGetTexturas(idAcabado)
                  const texturas = Array.isArray(texturasResp)
                    ? texturasResp
                    : (texturasResp as { data?: ItemWithOptions[] })?.data

                  if (Array.isArray(texturas)) {
                    try {
                      item.zocaloOptions = texturas as ItemWithOptions[]
                    } catch {
                      item = { ...item, zocaloOptions: texturas as ItemWithOptions[] }
                    }
                  }
                }
              }
            } catch (e) {
              // console.warn('[itemActions] Error cargando opciones de zócalo:', e)
            }
          }

          const itemCenter = toVector3([
            item.morphs[0].default / 2,
            item.morphs[1].default / 2,
            item.morphs[2].default / 2,
          ])

          const centerPosition = new Vector3()
          const quaternion = new Quaternion()
          matrix?.decompose(centerPosition, quaternion, new Vector3())

          itemCenter.applyQuaternion(quaternion).add(centerPosition)

          const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
          const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

          // Verificar ANTES de agregar el item si ya existía (para evitar regenerar frontales al editar)
          const storeBeforeAdd = get()
          const itemAlreadyExisted = storeBeforeAdd.items.has(item.id)
          const batchActive = isLinkedVisualBatchActive() || isLateralPlacementBatchActive()

          set((store) => {
            if (!store.sceneGLB.has(catalogID)) {
              store.sceneGLB.set(catalogID, { glb, backGLBInfo })
            }
            store.items.set(item.id, item)
            if (item.type === 'linkedItem' && !batchActive) {
              logFreeDimensionPuertaMaterial(item)
              logFreeDimensionZocaloMaterial(item)
            }
            store.layers[store.currentLayer].items.add(item.id)
             // linkedSon (incluyendo cutkits) no debe robar foco/seleccion del item editado.
            // Frontales automáticos tampoco: cada drawItem dejaba seleccionado el último.
            if (item.type !== 'linkedSon' && !batchActive && !store.isDrawingFrontals) {
              store.layers[store.currentLayer].selected.clear()
              visible && store.layers[store.currentLayer].selected.add(item.id)
              store.currentObjectID = item.id
            }
            store.sceneBoundingBoxes.set(item.id, {
              boundingBox: new OBB(
                toVector3([
                  -item.morphs[2].default / 2,
                  item.morphs[1].default / 2,
                  item.morphs[0].default / 2,
                ]),
                toVector3([
                  item.morphs[0].default / 2,
                  item.morphs[1].default / 2,
                  item.morphs[2].default / 2,
                ]),
                rotationMatrix,
              ),
              type: 'items',
            })
            if (backGLBInfo.colision ?? !backGLBInfo.universal) {
              store.collisionableItems.add(item.id)
            }
          })

          // La posición ya no viene de la matriz de entrada: se resuelve contra el item
          // seleccionado (lateral libre vía collideItems) o al centro de la escena.
          if (autoPlaceFromSelection && item.type !== 'linkedSon') {
            get().placeDrawnItem(item.id, currentSourceID as AnyID)
          }

          // Sync encimera height from inherited acabado when drawing next to a selected item.
          if (currentSourceID && item.type === 'linkedItem') {
            // Case 1: encimera is the item itself (linkedItem whose description is 'Encimera')
            const encimeraIsParent = isEncimeraLinkedSonItem(item as any)
            // Case 2: encimera is a linkedSon child of the new item
            const encimeraChild = encimeraIsParent
              ? null
              : (Array.from(get().items.values()).find(
                  (it) => isEncimeraLinkedSonItem(it) && (it as any).firstParentID === item.id,
                ) as any)
            const targetEncimera = encimeraIsParent ? item : encimeraChild
            if (targetEncimera) {
              get().updateEncimeraHeight((targetEncimera as any).id)
            }
          }

          if (catalogID === 'Frontal') {
            const existingFrontals = Array.from(get().items.values()).filter(
              (existingItem) =>
                (isItem(existingItem) || isLinkedItem(existingItem)) &&
                existingItem.catalogID === 'Frontal' &&
                existingItem.id !== item.id,
            )

            if (existingFrontals.length > 0) {
              try {
                const opcionesFrontales = await getEncimerasFrontales()

                set((store) => {
                  const newFrontal = store.items.get(item.id)
                  if (newFrontal && isLinkedItem(newFrontal)) {
                    newFrontal.OpcionesFrontales = opcionesFrontales
                  }

                  existingFrontals.forEach((frontal) => {
                    const frontalItem = store.items.get(frontal.id)
                    if (frontalItem && isLinkedItem(frontalItem)) {
                      frontalItem.OpcionesFrontales = opcionesFrontales
                    }
                  })
                })
              } catch (error) {
                console.error('❌ Error obteniendo opciones de frontales:', error)
              }
            }
          }

          const hasTiradores =
            item.childs &&
            Array.from(item.childs).some((childId) => {
              const childItem = get().items.get(childId)
              return (
                childItem && childItem.type === 'linkedSon' && childItem.typeOfSon === 'Tirador'
              )
            })

          if (item.type === 'linkedItem' && hasTiradores && !item.herenciaProcessed) {
            set((store) => {
              const currentItem = store.items.get(item.id)
              if (currentItem) {
                store.items.set(item.id, { ...currentItem, herenciaProcessed: true })
              }
            })

            await new Promise((resolve) => setTimeout(resolve, 100))

            const tiradoresDestino = Array.from(item.childs).filter((childId) => {
              const childItem = get().items.get(childId)
              return (
                childItem && childItem.type === 'linkedSon' && childItem.typeOfSon === 'Tirador'
              )
            })

            let tiradorOriginal = null
            let inheritedIdUnidadEnlazada: number | null = null

            const isTiradorSon = (child: any) =>
              Boolean(child) &&
              child.type === 'linkedSon' &&
              !isSinTiradorCatalog(child.catalogID) &&
              (child.description === 'Tirador' ||
                child.typeOfSon === 'Tirador' ||
                String(child.description ?? '')
                  .toLowerCase()
                  .includes('tirador'))

            const pickTiradorFromParent = (storeItem: any) => {
              if (!storeItem || storeItem.type !== 'linkedItem' || storeItem.id === item.id) {
                return false
              }
              const childIds = new Set<string>()
              if (storeItem.childs?.size) {
                for (const childId of storeItem.childs) childIds.add(String(childId))
              }
              for (const [candidateId, candidate] of get().items) {
                if (candidate?.firstParentID === storeItem.id) childIds.add(String(candidateId))
              }
              for (const originalChildId of childIds) {
                const originalChild = get().items.get(originalChildId)
                if (!isTiradorSon(originalChild)) continue

                const sceneOption = item.tiradorOptions?.find(
                  (option: any) => option.descripcion === originalChild.catalogID,
                )
                if (sceneOption?.idUnidadEnlazada) {
                  tiradorOriginal = originalChild
                  inheritedIdUnidadEnlazada = sceneOption.idUnidadEnlazada
                  return true
                }
              }
              return false
            }

            const sourceParent =
              currentSourceID && currentSourceID !== item.id
                ? get().items.get(currentSourceID as AnyID)
                : null
            if (!pickTiradorFromParent(sourceParent)) {
              for (const [, storeItem] of get().items) {
                if (pickTiradorFromParent(storeItem)) break
              }
            }

            const idsWithInheritedSlot = new Set<string>()
            if (tiradorOriginal && inheritedIdUnidadEnlazada) {
              for (const childId of item.childs) {
                const childItem = get().items.get(childId)

                if (
                  childItem &&
                  childItem.type === 'linkedSon' &&
                  childItem.typeOfSon === 'Tirador'
                ) {
                  try {
                    const drawerNode = childItem.option ?? childItem.nodo
                    const matchingOption =
                      item.tiradorOptions?.find(
                        (option: any) =>
                          option.nodo === drawerNode &&
                          option.idUnidadEnlazada === inheritedIdUnidadEnlazada,
                      ) ??
                      item.tiradorOptions?.find(
                        (option: any) =>
                          option.idUnidadEnlazada === inheritedIdUnidadEnlazada,
                      )

                    if (matchingOption) {
                      const tiradorData = await getElementosEnlazadosUnidadAcabados(
                        item.idUnidades,
                        matchingOption.idElementosEnlazados,
                      )

                      set((store) => {
                        const updatedChild = store.items.get(childId)
                        if (updatedChild) {
                          const updatedTirador = {
                            ...updatedChild,

                            catalogID: tiradorData.unidad.codigo,
                            glbPath: tiradorData.unidad.referenciaBlender,
                            referenciaBlender: tiradorData.unidad.referenciaBlender,
                            code: tiradorData.unidad.codigo,
                            referencia: tiradorData.unidad.referencia,
                            idUnidades:
                              matchingOption.idUnidadEnlazada ??
                              tiradorData.unidad?.idUnidades ??
                              updatedChild.idUnidades,
                            linkedID: matchingOption.idElementosEnlazados,
                            idElementosEnlazados: matchingOption.idElementosEnlazados,
                            long:
                              tiradorData.unidad.descripcionLarga ||
                              tiradorData.unidad.descripcion ||
                              tiradorData.unidad.codigo,
                            textures: updatedChild.textures,
                            zones: updatedChild.zones,
                          }
                          replaceTiradorSlotPositions(
                            updatedTirador,
                            tiradorData as Record<string, unknown>,
                          )
                          idsWithInheritedSlot.add(String(childId))

                          const parentItem = store.items.get(item.id)
                          if (parentItem) {
                            applyTiradorPlacement(updatedTirador, parentItem, {
                              hideForGola: shouldHideTiradoresForParent(parentItem as LinkedItem, {
                                toolbarGolaActiva: store.toolbarGolaActiva,
                                sourceItem:
                                  sourceParent && isLinkedItem(sourceParent)
                                    ? (sourceParent as LinkedItem)
                                    : null,
                              }),
                            })
                          }
                          store.items.set(childId, updatedTirador)
                        }
                      })

                      try {
                        const loader = await setGLTFLoader()
                        const glbRef = tiradorData.unidad.referenciaBlender

                        if (glbRef) {
                          const glb = await loader.loadAsync(glbRef)

                          const extraInfo = await API().api.acabadosGetUnidadAcabadosInfo(
                            tiradorData.unidad.idUnidades,
                          )

                          const backGLBInfo = {
                            idUnidades: tiradorData.unidad?.idUnidades ?? 0,
                            linkedID: tiradorData.unidad?.idUnidades ?? 0,
                            active: true,
                            codigo: tiradorData.unidad?.codigo ?? '',
                            referenciaBlender: tiradorData.unidad?.referenciaBlender ?? '',
                            elemento: tiradorData.unidad?.elemento,
                            descripcion: tiradorData.unidad?.descripcion,
                            descripcionLarga: tiradorData.unidad?.descripcionLarga,
                            ancho: tiradorData.unidad?.ancho,
                            fondo: tiradorData.unidad?.fondo,
                            alto: tiradorData.unidad?.alto,
                            mano: tiradorData.unidad?.mano,
                            altura: tiradorData.unidad?.altura,
                            alturaHaciaAbajo: tiradorData.unidad?.alturaHaciaAbajo,
                            colision: tiradorData.unidad?.colision,
                            cota: tiradorData.unidad?.cota,
                            tipoNormalizado: tiradorData.unidad?.tipoNormalizado ?? 'MBJ',
                            tipoUnidNorm: tiradorData.unidad?.tipoUnidNorm ?? '',
                            modificarAncho: tiradorData.unidad?.modificarAncho,
                            modificarFondo: tiradorData.unidad?.modificarFondo,
                            modificarAlto: tiradorData.unidad?.modificarAlto,
                            morphs: tiradorData.unidad?.morphs ?? [],
                            precio: tiradorData.unidad?.precio,
                            acabados: tiradorData.unidad?.acabados ?? [],
                            unidadesLuces: tiradorData.unidad?.unidadesLuces ?? [],
                            elementosEnlazados: tiradorData.unidad?.elementosEnlazados ?? [],
                            referenciaJpgpng: tiradorData.unidad?.referenciaJpgpng,
                            universal: tiradorData.unidad?.universal,
                          } as unknown as BackGLBInfo

                          const processedInfo = newGetDataFromGLB({
                            glbData: glb,
                            backGLBInfo,
                            id: childId,
                            type: 'linkedSon',
                            catalogID: tiradorData.unidad.codigo ?? '',
                            extraInfo: {
                              zonas: Array.isArray(extraInfo.data?.zonas)
                                ? extraInfo.data.zonas.map((z) => ({
                                    idAcabadoUnidad: z.idAcabadoUnidad,
                                    zona: z.zona ?? '',
                                  }))
                                : [],
                            },
                            itemInfo: {},
                          })

                          set((store) => {
                            const updatedChild = store.items.get(childId)
                            if (updatedChild) {
                              Object.assign(updatedChild, {
                                textures: processedInfo.textures,
                                zones: processedInfo.zones,
                              })

                              if (
                                tiradorData.unidad.codigo !== undefined &&
                                tiradorData.unidad.codigo !== null
                              ) {
                                store.sceneGLB.set(String(tiradorData.unidad.codigo), {
                                  glb,
                                  backGLBInfo: tiradorData.unidad,
                                })
                              }
                            }
                          })
                        }
                      } catch (glbError) {
                        console.error('[drawItem] Error cargando GLB:', glbError)
                      }
                    } else {
                      // console.warn(
                        // `[drawItem] No se encontró opción de tirador para idUnidadEnlazada: ${inheritedIdUnidadEnlazada} y nodo/cajón: ${drawerNode}`,
                      // )
                    }
                  } catch (error) {
                    console.error(
                      `[drawItem] Error en llamada API para tirador nodo ${childItem.nodo}:`,
                      error,
                    )
                  }
                }
              }
            }

            set((store) => {
              const parentItem = store.items.get(item.id)
              if (!parentItem) return
              const hideForGola = shouldHideTiradoresForParent(parentItem as LinkedItem, {
                toolbarGolaActiva: store.toolbarGolaActiva,
                sourceItem:
                  sourceParent && isLinkedItem(sourceParent)
                    ? (sourceParent as LinkedItem)
                    : null,
              })
              const parentSlot = findTiradorSlotOnParentElements(
                (backGLBInfo as { elementosEnlazados?: Array<Record<string, unknown>> })
                  .elementosEnlazados ??
                  (
                    backGLBInfo as {
                      elementosEnlazadosAcabadosPorDefecto?: Array<Record<string, unknown>>
                    }
                  ).elementosEnlazadosAcabadosPorDefecto,
              )
              for (const childId of tiradoresDestino) {
                const child = store.items.get(childId)
                if (!child || child.type !== 'linkedSon') continue
                if (
                  !idsWithInheritedSlot.has(String(childId)) &&
                  !hasTiradorSlotPositions(child as any)
                ) {
                  copyTiradorSlotPositions(child as any, parentSlot)
                }
                applyTiradorPlacement(child as any, parentItem as any, { hideForGola })
              }
            })
          }

          // Si el modo de frontales está activo y acabamos de dibujar una unidad junto a una pared,
          // regenerar los frontales para incluir esta nueva unidad. Evitamos recursión si el item es un propio 'Frontal'.
          try {
            const storeSnapshot = get()
            const autoFrontalsEnabled = storeSnapshot.frontalsAutoEnabled ?? true
            // NO regenerar frontales si:
            // 1. El item ya existía (es una edición, no una creación)
            // 2. Es un frontal
            // 3. Es un linkedSon (hijo de un item, como CORTEKITESP, Encimera, etc.)
            // 4. Es un CORTEKITESP específicamente
            const isLinkedSon = item.type === 'linkedSon'
            const isCorteKit = isCorteKitCatalog(catalogID)
            const shouldAutoDrawFrontals =
              autoFrontalsEnabled &&
              storeSnapshot.frontalActive &&
              catalogID !== 'Frontal' &&
              !itemAlreadyExisted && // Solo regenerar frontales si es un item NUEVO, no si se está editando
              !isLinkedSon && // No regenerar si es un hijo (linkedSon)
              !isCorteKit // No regenerar si es un kit de corte

            if (shouldAutoDrawFrontals) {
              // Guardar el ID de la unidad original para mantenerla seleccionada después de regenerar frontales
              const originalUnitId = item.id

              // Altura a usar: si ya existen frontales, reutilizar su altura; si no, usar 50 por defecto
              const existingFrontals = Array.from(storeSnapshot.items.values()).filter(
                (it) => (isItem(it) || isLinkedItem(it)) && (it as any).catalogID === 'Frontal',
              ) as any[]

              const heightFromExisting = existingFrontals.length
                ? Number(existingFrontals[0]?.height) || 50
                : 50

              // Dar tiempo suficiente para que el item quede fijado a la pared/agrupado
              // y esté completamente procesado antes de regenerar frontales
              // NO redibujar frontales si se está dibujando un lateral (LA, LB, LC, CC, CB)
              const catalogIDStr = catalogID?.toString() || ''
              const isLateral =
                catalogIDStr.startsWith('LA') ||
                catalogIDStr.startsWith('LB') ||
                catalogIDStr.startsWith('LC') ||
                catalogIDStr.startsWith('CC') ||
                catalogIDStr.startsWith('CB')
              const isCascada =
                isFreeDimensionLateralCascadaAnchoCatalog(item) ||
                /cascada/i.test(String(item.description ?? catalogIDStr))

              if (!isLateral && !isCascada) {
                setTimeout(async () => {
                  try {
                    // Redibuja todos los frontales en base a los grupos contiguos actuales
                    // Usar force: false para respetar frontalsAutoEnabled, pero asegurar que frontalActive esté activo
                    const currentStore = get()
                    if (currentStore.frontalActive && (currentStore.frontalsAutoEnabled ?? true)) {
                      await get().drawFrontalsFromSnappedGroups(heightFromExisting)
                    }

                    // Esperar un poco más para que los frontales se procesen completamente
                    await new Promise((resolve) => setTimeout(resolve, 300))

                    // Restaurar la selección de la unidad original en lugar del frontal
                    const storeAfterUpdate = get()
                    const originalUnit = storeAfterUpdate.items.get(originalUnitId)
                    if (originalUnit) {
                      // Asegurar que la unidad original esté seleccionada y no el frontal
                      set((store) => {
                        store.currentObjectID = originalUnitId

                        store.layers[store.currentLayer].selected.forEach((selectedId) => {
                          const selectedItem = store.items.get(selectedId)
                          if (selectedItem && (selectedItem as any).catalogID === 'Frontal') {
                            store.layers[store.currentLayer].selected.delete(selectedId)
                          }
                        })
                        // Asegurar que la unidad original esté seleccionada
                        store.layers[store.currentLayer].selected.add(originalUnitId)
                      })
                    }
                  } catch (e) {
                    // console.warn('[drawItem] Auto-draw frontals error:', e)
                    // En caso de error, también restaurar la selección
                    const storeAfterError = get()
                    const originalUnit = storeAfterError.items.get(originalUnitId)
                    if (originalUnit) {
                      set((store) => {
                        store.currentObjectID = originalUnitId
                        store.layers[store.currentLayer].selected.add(originalUnitId)
                      })
                    }
                  }
                }, 600)
              }
            }
          } catch (e) {
            // console.warn('[drawItem] post-draw frontal sync skipped:', e)
          }

          // Si el objeto origen tiene "GO" en su catalogID, cambiar la gola según el armazón
          // PERO NO hacerlo para laterales u otros items que no deberían tener Gola automáticamente
          if (currentSourceID && !item.universal && isLinkedItem(item)) {
            const itemDescription = String(item.description || '').toLowerCase()
            const itemCatalogID = String(item.catalogID || '').toUpperCase()

            // Detectar laterales por descripción o catalogID
            // KAB puede ser MAL (unidades altas) o lateral, pero si tiene " GO" es una unidad Gola válida
            // Solo detectar laterales reales: LA, LB, LC, CC, CB (sin " GO")
            const isLateral =
              (itemDescription.includes('lateral') && !itemCatalogID.includes(' GO')) ||
              (itemCatalogID.startsWith('LA') && !itemCatalogID.includes(' GO')) ||
              (itemCatalogID.startsWith('LB') && !itemCatalogID.includes(' GO')) ||
              (itemCatalogID.startsWith('LC') && !itemCatalogID.includes(' GO')) ||
              (itemCatalogID.startsWith('CC') && !itemCatalogID.includes(' GO')) ||
              (itemCatalogID.startsWith('CB') && !itemCatalogID.includes(' GO'))

            // NO aplicar Gola automáticamente a laterales u otros items específicos
            if (!isLateral) {
              const sourceItem = store.items.get(currentSourceID)
              const storeActions = get()
              const freshItem = get().items.get(item.id)

              if (sourceItem && isLinkedItem(sourceItem) && freshItem && isLinkedItem(freshItem)) {
                const sourceCatalogID = String(sourceItem.catalogID || '')
                const sourceGolaEffective = isGolaEffective(sourceItem as LinkedItem)
                const drawnGolaEffective = isGolaEffective(freshItem as LinkedItem)

                if (sourceGolaEffective && !drawnGolaEffective) {
                  if (
                    isSelfReferencingGolaUnit(freshItem as LinkedItem) &&
                    storeActions.applyLinkedItemGolaModo
                  ) {
                    await storeActions.applyLinkedItemGolaModo(item.id as any, true)
                  } else if (storeActions.updateLinkedItemGola) {
                    const toolbarAcabadoBlancoPreference = store.toolbarAcabadoBlanco
                    const isArmazonBlanco = toolbarAcabadoBlancoPreference === true
                    const isArmazonPetroleo = toolbarAcabadoBlancoPreference === false
                    const armazonBlanco = isArmazonBlanco
                      ? true
                      : isArmazonPetroleo
                        ? false
                        : (freshItem.ArmazonBlanco ?? true)

                    const target = resolveDrawGolaInheritanceTarget(freshItem, sourceCatalogID, {
                      armazonBlanco,
                      sourceGolaEffective,
                    })

                    if (target.strategy !== 'skip') {
                      try {
                        let newUnidadData: Record<string, unknown> | null = null

                        if (target.strategy === 'id') {
                          const response = await API().api.unidadesGetUnidadGet(
                            target.idUnidades,
                            String(target.idUnidades),
                            {
                              headers: {
                                'Accept-Language': 'ES',
                              },
                            },
                          )
                          newUnidadData = response?.data ?? null
                        } else {
                          newUnidadData = await fetchUnidadByCatalog(target.catalogId)
                        }

                        if (newUnidadData) {
                          await storeActions.updateLinkedItemGola(item.id as any, newUnidadData)

                          const storeAfterGola = get()
                          const itemAfterGola = storeAfterGola.items.get(item.id)
                          if (
                            itemAfterGola &&
                            isLinkedItem(itemAfterGola) &&
                            itemAfterGola.textures?.length
                          ) {
                            const existingTextures = getExistingTexturesFromScene(
                              storeAfterGola.items,
                              String(itemAfterGola.catalogID || ''),
                              currentSourceID,
                            )

                            if (existingTextures?.length) {
                              set((s) => {
                                const it = s.items.get(item.id)
                                if (!it || !isLinkedItem(it) || !it.textures?.length) return

                                const mapped = mapExistingTexturesToNewItemByDisplayName(
                                  existingTextures,
                                  it.textures,
                                  it,
                                )
                                const syncResult = syncZonesWithMappedTextures(
                                  it.zones,
                                  mapped,
                                  s.items,
                                  it.type,
                                  it.description,
                                )
                                it.textures = mapped
                                it.zones = syncResult.zones
                                syncLinkedItemZonesFromTextures(it, {
                                  itemId: item.id,
                                  source: 'drawItem:afterGola',
                                })
                                sanitizeDisallowedCostadoOnLinkedItem(it)
                              })
                            }
                          }
                        }
                      } catch (error) {
                        // console.warn('[DrawItem][Gola] Error al heredar variante Gola:', error)
                      }
                    }
                  }
                }

                // Si golaActiva se heredó al crear, sincronizar tiradores (idempotente)
                if (
                  freshItem.golaActiva === true &&
                  isSelfReferencingGolaUnit(freshItem as LinkedItem) &&
                  storeActions.applyLinkedItemGolaModo
                ) {
                  await storeActions.applyLinkedItemGolaModo(item.id as any, true)
                }
              }
            }
          }

          // Armazón PE/blanco: igualar al origen vía idcambioarmazon del DESTINO (prefijo PE de la nueva unidad).
          if (currentSourceID && !item.universal && isLinkedItem(item)) {
            const sourceForArmazon = get().items.get(currentSourceID as AnyID)
            const destForArmazon = get().items.get(item.id as AnyID)
            if (
              sourceForArmazon &&
              isLinkedItem(sourceForArmazon) &&
              destForArmazon &&
              isLinkedItem(destForArmazon)
            ) {
              const sourceIsBlanco = resolveArmazonBlanco(sourceForArmazon as LinkedItem)
              const destIsBlanco = resolveArmazonBlanco(destForArmazon as LinkedItem)
              const idCambioArmazon =
                (destForArmazon as any).idcambioarmazon ||
                (destForArmazon as any).idCambioArmazon

              if (
                sourceIsBlanco !== destIsBlanco &&
                idCambioArmazon &&
                typeof get().updateLinkedItemArmazonOptimized === 'function'
              ) {
                try {
                  const resp = await API().api.unidadesGetUnidadGet(
                    idCambioArmazon as any,
                    String(idCambioArmazon),
                    { headers: { 'Accept-Language': 'ES' } },
                  )
                  if (resp?.data) {
                    await get().updateLinkedItemArmazonOptimized(item.id as any, resp.data)
                  }
                } catch (e) {
                  // console.warn('[drawItem] Error heredando armazón PE/blanco:', e)
                }
              }
            }
          }

          if (
            item.type === 'linkedItem' &&
            item.childs &&
            item.childs.size > 0 &&
            usesFreeDimensionScaling(item)
          ) {
            const parentId = item.id
            setTimeout(() => {
              get().updateLinkedSonPositions(parentId, 'Alto')
            }, 0)
          }

          // Garantizar Zocalo 4000 frente (+ retorno rinconero) tras gola/herencia.
          if (item.type === 'linkedItem' && shouldUseIndependentZocaloUnit(item)) {
            try {
              await migrateLegacyIndependentZocalosOnLoad({
                get,
                set,
                onlyParentIds: [String(item.id)],
              })
              const ensured = get().items.get(item.id as AnyID) as LinkedItem | undefined
              const zocaloKids =
                ensured?.childs &&
                Array.from(ensured.childs).filter((cid) => {
                  const c = get().items.get(cid as AnyID)
                  return c && isIndependentZocaloLinkedSon(c as any)
                })
              if (ensured?.independentZocaloSonId || (zocaloKids && zocaloKids.length)) {
                get().updateLinkedSonPositions(String(item.id), 'Ancho')
                get().updateLinkedSonPositions(String(item.id), 'Fondo')
                get().updateLinkedSonPositions(String(item.id), 'Alto')
              }
            } catch {
              /* zócalo independiente opcional */
            }
          }

          if (item.type === 'linkedItem' && isFroFrontalItem(item)) {
            const liveItems = get().items
            const existingFro = findExistingFroFinishInScene(liveItems, String(item.id))
            const encimera = Array.from(liveItems.values()).find((it) => isEncimeraLinkedSonItem(it))
            const encTex =
              encimera && 'textures' in encimera
                ? encimera.textures?.find(
                    (t) => t.zone === 'Encimera' || t.zoneDisplayName === 'Encimera',
                  ) ?? encimera.textures?.[0]
                : undefined
            let options = (get().frontalesTexturesOptions as any[]) || []
            if (!options.length) {
              const froTex =
                existingFro?.texture ??
                item.textures?.find(
                  (t) =>
                    t.zoneDisplayName === 'Frontal' ||
                    t.zone === 'Frontal' ||
                    t.zoneDisplayName === 'Encimera' ||
                    t.zone === 'Encimera',
                ) ??
                item.textures?.[0]
              const linkedTextureID = Number(froTex?.linkedTextureID)
              options = await fetchPorcelanicas12FrontalTextures(linkedTextureID)
              if (options.length) {
                get().setFrontalesTexturesOptions?.(options)
              }
            }
            const catalogTex = existingFro
              ? pickFrontalTextureByEncimeraDescription(options, existingFro.texture) ?? {
                  nombre: existingFro.texture.name,
                  name: existingFro.texture.name,
                  descripcion: existingFro.texture.description,
                  description: existingFro.texture.description,
                  url: existingFro.texture.url,
                  urlMiniatura: existingFro.texture.img,
                  identificadorTextura: existingFro.texture.id,
                  idTexturas: existingFro.texture.searchID,
                  acabado: existingFro.texture.acabado,
                }
              : resolveFroFrontalTextureFromEncimera(encTex, options)
            if (catalogTex) {
              set((store) => {
                const live = store.items.get(item.id as AnyID)
                if (live && isLinkedItem(live)) applyCatalogOptionToFrontal(live, catalogTex)
              })
            }
          }

          if (item.type === 'linkedItem' && !skipLinkedSons) {
            try {
              await get().syncFregaderoAccessories(String(item.id))
            } catch {
              /* accesorios compacto de fregadero: no bloquear el dibujo */
            }
          }

          resolve(item.id)
        } catch (error) {
          reject(error)
        }
      })()
    })
  },
  addCutKits: () => {
    const store = get()
    const promises = []
    const originalCurrentLayer = store.currentLayer
    const originalCurrentObjectID = store.currentObjectID
    const originalSelected = new Set(store.layers[originalCurrentLayer].selected)

    // Mapa para rastrear los kits de corte existentes por mueble original
    const existingKitsByItem = new Map()

    // Primero identificar los kits de corte existentes basándose en firstParentID
    store.items.forEach((item, id) => {
      if (
        item.catalogID &&
        typeof item.catalogID === 'string' &&
        item.catalogID.startsWith('CORTEKIT') &&
        item.firstParentID
      ) {
        // Este es un kit de corte, añadirlo al mapa por su padre
        if (!existingKitsByItem.has(item.firstParentID)) {
          existingKitsByItem.set(item.firstParentID, [])
        }
        existingKitsByItem.get(item.firstParentID).push(id)
      }
    })

    const shouldTrackCutKit = (item: SceneObjects) => {
      if (!isLinkedItem(item)) return false
      if (isCorteKitCatalog(item.catalogID) || isCorteKitCatalog((item as { codigo?: string }).codigo)) {
        return false
      }
      const catalog = String(item.catalogID ?? '').toUpperCase()
      if (catalog === 'FRONTAL' || catalog.startsWith('ZOCALO')) return false
      if (isFreeDimensionLateralOrCostadoCatalog(item) || isGvMoldingUnit(item)) return false
      if (isDecorationMenuUnit(item)) return false
      return true
    }

    const removeKitsForParent = (parentId: string, kitIds: string[]) => {
      if (kitIds.length === 0) return
      kitIds.forEach((kitId) => {
        set((state) => {
          const kitItem = state.items.get(kitId)
          const kitCatalogID = kitItem?.catalogID
          const kitCodigo = kitItem ? (kitItem as { codigo?: string }).codigo : undefined
          const parentItem = state.items.get(parentId as AnyID)
          if (parentItem && 'childs' in parentItem && parentItem.childs) {
            parentItem.childs.delete(kitId)
          }
          if (parentItem && 'childrenOrder' in parentItem && Array.isArray(parentItem.childrenOrder)) {
            parentItem.childrenOrder = parentItem.childrenOrder.filter(
              (entry) => !String(entry).includes(kitId),
            )
          }
          state.items.delete(kitId)
          state.layers[state.currentLayer].items.delete(kitId)
          state.layers[state.currentLayer].selected.delete(kitId)
          state.sceneBoundingBoxes.delete(kitId)
          state.collisionableItems.delete(kitId)
          purgeSceneGlbForCatalog(state, kitCatalogID, kitCodigo)
        })
      })
      existingKitsByItem.delete(parentId)
    }

    // Recorrer todos los items para añadir o actualizar kits
    store.items.forEach((item, id) => {
      if (!shouldTrackCutKit(item)) return

      const changedDimensions = getChangedCutKitDimensions(item)
      const catalogID = getCutKitCatalogId(changedDimensions)

      if (catalogID) {
        const existingKits = existingKitsByItem.get(id) || []
        const alreadyHasSameKit =
          existingKits.length === 1 &&
          String(store.items.get(existingKits[0])?.catalogID ?? '').toUpperCase() === catalogID
        if (alreadyHasSameKit) return

        removeKitsForParent(id, existingKits)

        const kitMatrix = item.matrix.clone()
        const position = new Vector3()
        const quaternion = new Quaternion()
        const scale = new Vector3()
        kitMatrix.decompose(position, quaternion, scale)
        const finalKitMatrix = new Matrix4().compose(position, quaternion, scale)

        const promise = get()
          .drawItem(
            getId('linkedSon'),
            catalogID,
            '',
            false,
            finalKitMatrix,
            'linkedSon',
            undefined,
            false,
            true,
          )
          .then((newItemId) => {
            set((state) => {
              const kitItem = state.items.get(newItemId)
              const parentItem = state.items.get(id as AnyID)

              if (kitItem) {
                kitItem.firstParentID = id
                kitItem.depth = 1
                kitItem.snappedWall = ''
                kitItem.visible = false
              }

              if (parentItem && 'childs' in parentItem) {
                if (!parentItem.childs) parentItem.childs = new Set()
                parentItem.childs.add(newItemId)
              }

              state.sceneBoundingBoxes.delete(newItemId)
              state.collisionableItems.delete(newItemId)

              state.currentObjectID =
                originalCurrentObjectID && state.items.has(originalCurrentObjectID)
                  ? originalCurrentObjectID
                  : (id as AnyID)
              state.layers[originalCurrentLayer].selected = new Set(originalSelected)
              if (state.layers[originalCurrentLayer].selected.size === 0 && state.items.has(id)) {
                state.layers[originalCurrentLayer].selected.add(id as AnyID)
              }
            })

            return { id: newItemId, originalId: id, catalogID }
          })
          .catch((error) => {
            console.error(`Error añadiendo kit de corte ${catalogID} para ${id}:`, error)
            return null
          })

        promises.push(promise)
      } else {
        removeKitsForParent(id, existingKitsByItem.get(id) || [])
      }
    })

    // Esperar a que todas las promesas se resuelvan
    return Promise.all(promises).then((results) => {
      const validResults = results.filter(Boolean)
      return validResults
    })
  },
  syncFregaderoAccessories: (parentId: string) => {
    const previous = syncFregaderoAccessoriesInFlight.get(parentId)
    const run = (previous ?? Promise.resolve())
      .catch(() => undefined)
      .then(() => {
        const store = get()
        const parent = store.items.get(parentId as AnyID)
        if (!parent || parent.type !== 'linkedItem') return []

        const originalCurrentLayer = store.currentLayer
        const originalCurrentObjectID = store.currentObjectID
        const originalSelected = new Set(store.layers[originalCurrentLayer].selected)

        const isFregaderoSon = (child: { typeOfSon?: string; description?: string } | undefined) =>
          child?.typeOfSon === 'Fregadero' || child?.description === 'Fregadero'

        let hasFregadero = false
        const parentChilds = (parent as LinkedItem).childs
        if (parentChilds) {
          parentChilds.forEach((childId) => {
            const child = store.items.get(childId as AnyID)
            if (!child || itemUsesAccesorioFregaderoCatalog(child as { catalogID?: string })) return
            if (isFregaderoSon(child as { typeOfSon?: string; description?: string })) {
              hasFregadero = true
            }
          })
        }
        store.items.forEach((child) => {
          if ((child as { firstParentID?: string }).firstParentID !== parentId) return
          if (itemUsesAccesorioFregaderoCatalog(child as { catalogID?: string })) return
          if (isFregaderoSon(child as { typeOfSon?: string; description?: string })) {
            hasFregadero = true
          }
        })

        const existing = collectCompactoAccessoriesForParent(store.items, parentId)

        const encimera =
          findEncimeraSonOnUnit(store.items, parent as LinkedItem) ??
          (() => {
            let found: SceneObjects | undefined
            store.items.forEach((child) => {
              if ((child as { firstParentID?: string }).firstParentID !== parentId) return
              if (itemUsesAccesorioFregaderoCatalog(child as { catalogID?: string })) return
              if (isEncimeraLinkedSonItem(child)) found = child
            })
            return found
          })()
        const encimeraAcabado = getEncimeraTextureEntry(
          encimera as { textures?: any[] } | null,
        )?.acabado
        const needAccessories = hasFregadero && isCompactoFinish(encimeraAcabado)

        const deleteAccessory = (accId: string) => {
          set((state) => {
            const accItem = state.items.get(accId)
            const accCatalogID = accItem?.catalogID
            const accCodigo = accItem ? (accItem as { codigo?: string }).codigo : undefined
            const parentItem = state.items.get(parentId as AnyID)
            if (parentItem && 'childs' in parentItem && parentItem.childs) {
              parentItem.childs.delete(accId as AnyID)
            }
            state.items.delete(accId)
            state.layers[state.currentLayer].items.delete(accId)
            state.layers[state.currentLayer].selected.delete(accId)
            state.sceneBoundingBoxes.delete(accId)
            state.collisionableItems.delete(accId)
            purgeSceneGlbForCatalog(state, accCatalogID, accCodigo)
          })
        }

        const keepOneOfEach = () => {
          const leftover = collectCompactoAccessoriesForParent(get().items, parentId)
          leftover.bajoEnc.slice(1).forEach(deleteAccessory)
          leftover.grifo.slice(1).forEach(deleteAccessory)
        }

        if (!needAccessories) {
          existing.bajoEnc.forEach(deleteAccessory)
          existing.grifo.forEach(deleteAccessory)
          return []
        }

        existing.bajoEnc.slice(1).forEach(deleteAccessory)
        existing.grifo.slice(1).forEach(deleteAccessory)

        const accMatrix = parent.matrix.clone()
        const position = new Vector3()
        const quaternion = new Quaternion()
        const scale = new Vector3()
        accMatrix.decompose(position, quaternion, scale)
        const finalMatrix = new Matrix4().compose(position, quaternion, scale)

        const promises: Promise<unknown>[] = []
        const specs: { catalogID: string; existing: string[] }[] = [
          { catalogID: 'COMP-BAJOENC', existing: existing.bajoEnc },
          { catalogID: 'COMP-GRIFO', existing: existing.grifo },
        ]

        specs.forEach(({ catalogID, existing: existingIds }) => {
          if (existingIds.length > 0) return
          const promise = get()
            .drawItem(
              getId('linkedSon'),
              catalogID,
              '',
              false,
              finalMatrix,
              'linkedSon',
              undefined,
              false,
              true,
            )
            .then((newItemId) => {
              set((state) => {
                const accItem = state.items.get(newItemId)
                if (accItem) {
                  accItem.firstParentID = parentId as AnyID
                  accItem.depth = 1
                  accItem.snappedWall = ''
                }
                state.sceneBoundingBoxes.delete(newItemId)
                state.collisionableItems.delete(newItemId)
                state.currentObjectID =
                  originalCurrentObjectID && state.items.has(originalCurrentObjectID)
                    ? originalCurrentObjectID
                    : (parentId as AnyID)
                state.layers[originalCurrentLayer].selected = new Set(originalSelected)
                if (
                  state.layers[originalCurrentLayer].selected.size === 0 &&
                  state.items.has(parentId as AnyID)
                ) {
                  state.layers[originalCurrentLayer].selected.add(parentId as AnyID)
                }
              })
              return { id: newItemId, parentId, catalogID }
            })
            .catch((error) => {
              console.error(`Error añadiendo accesorio ${catalogID} para ${parentId}:`, error)
              return null
            })
          promises.push(promise)
        })

        return Promise.all(promises).then((results) => {
          keepOneOfEach()
          return results.filter(Boolean)
        })
      })
      .finally(() => {
        if (syncFregaderoAccessoriesInFlight.get(parentId) === run) {
          syncFregaderoAccessoriesInFlight.delete(parentId)
        }
      })

    syncFregaderoAccessoriesInFlight.set(parentId, run)
    return run
  },
  replaceItem: (
    selectedItemId: string,
    newCatalogID: string,
    icono: string = '',
    type: SceneObjects['type'] = 'items',
    realCatalogID?: string,
  ): Promise<AnyID> => {
    return new Promise((resolve, reject) => {
      try {
        // 1. Obtener el item seleccionado y guardar sus datos
        const selectedItem = get().items.get(selectedItemId)
        if (!selectedItem) {
          reject(new Error(`No se encontró el item con ID ${selectedItemId}`))
          return
        }

        // Guardar la matriz y otras propiedades importantes
        const matrix = selectedItem.matrix?.clone() || null
        const visible = selectedItem.visible
        const snappedWall = selectedItem.snappedWall
        const neighborIds: AnyID[] = []
        const oldSides = 'snappedSides' in selectedItem ? selectedItem.snappedSides : undefined
        if (oldSides) {
          for (const side of ['left', 'right', 'front', 'back'] as const) {
            const neighborId = oldSides[side]
            if (neighborId && neighborId !== selectedItemId && !isSlabId(neighborId)) {
              neighborIds.push(neighborId)
            }
          }
        }

        // 2. Recoger la jerarquía a borrar, pero dibujar ANTES para heredar
        // acabado/zócalo/tirador de la unidad que se sustituye.
        const itemsSnapshot = get().items
        const idsToDelete = new Set<AnyID>([selectedItemId as AnyID])
        const queue: AnyID[] = [selectedItemId as AnyID]

        while (queue.length > 0) {
          const currentId = queue.shift()
          if (!currentId) continue

          const currentItem = itemsSnapshot.get(currentId as string) as any
          if (!currentItem) continue

          // linkedItem -> linkedSons vía childs
          if (currentItem.childs && currentItem.childs.size > 0) {
            currentItem.childs.forEach((childId: AnyID) => {
              if (idsToDelete.has(childId)) return
              idsToDelete.add(childId)
              queue.push(childId)
            })
          }

          // linkedSons y descendientes por firstParentID
          itemsSnapshot.forEach((candidate: any, candidateId) => {
            if (!candidate || !candidate.firstParentID) return
            if (candidate.firstParentID !== currentId) return
            const normalizedCandidateId = candidateId as AnyID
            if (idsToDelete.has(normalizedCandidateId)) return
            idsToDelete.add(normalizedCandidateId)
            queue.push(normalizedCandidateId)
          })
        }

        // 3. Dibujar el nuevo item en la misma posición, heredando del que se sustituye.
        get()
          .drawItem(
            getId('Items'),
            newCatalogID,
            icono,
            visible,
            matrix,
            type,
            realCatalogID,
            false,
            false,
            selectedItemId as AnyID,
          )
          .then(async (newItemId) => {
            set((store) => {
              releaseSnappedSides(store.items, selectedItemId as AnyID)
              idsToDelete.forEach((idToDelete) => {
                if (idToDelete === newItemId) return
                store.items.delete(idToDelete)
                store.sceneBoundingBoxes.delete(idToDelete)
                store.collisionableItems.delete(idToDelete)
              })

              Object.values(store.layers).forEach((layer) => {
                idsToDelete.forEach((idToDelete) => {
                  if (idToDelete === newItemId) return
                  layer.items.delete(idToDelete)
                  layer.selected.delete(idToDelete)
                })
              })
            })

            // 4. Restaurar la relación con la pared si existía
            if (snappedWall) {
              get().editSnappedWall(newItemId, snappedWall)
            }

            get().setOBB(newItemId)
            const resolved = new Set<AnyID>()
            for (const neighborId of neighborIds) {
              if (!get().items.has(neighborId) || resolved.has(neighborId)) continue
              resolved.add(neighborId)
              get().collideItems(newItemId, neighborId)
            }
            get().collisionableItems.forEach((otherId) => {
              const id = otherId as AnyID
              if (id === newItemId || resolved.has(id)) return
              const currentBox = get().sceneBoundingBoxes.get(newItemId)?.boundingBox
              const otherBox = get().sceneBoundingBoxes.get(id)?.boundingBox
              if (currentBox && otherBox && currentBox.intersectsOBB(otherBox)) {
                get().collideItems(newItemId, id)
              }
            })

            // Tras borrar la unidad antigua: COMP-BAJOENC/COMP-GRIFO solo si la nueva tiene fregadero
            try {
              await get().syncFregaderoAccessories(String(newItemId))
            } catch {
              /* accesorios compacto de fregadero: no bloquear la sustitución */
            }

            // 5. Devolver el ID del nuevo item
            resolve(newItemId)
          })
          .catch((error) => {
            reject(error)
          })
      } catch (error) {
        reject(error)
      }
    })
  },
  setOBB: (id: AnyID) => {
    set((store) => {
      const item = store.items.get(id) as Item | LinkedItem
      if (!isItem(item) && !isLinkedItem(item)) return
      const centerPosition = toVector3()
      const quaternion = toQuaternion()
      item.matrix.decompose(centerPosition, quaternion, toVector3())
      const zocaloLift = 0
      const totalHeight = item.height
      const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
      itemCenter.applyQuaternion(quaternion).add(centerPosition)
      const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
      const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
      const half = new Vector3(item.width / 2, item.height / 2, item.thickness / 2)
      store.sceneBoundingBoxes.set(id, {
        boundingBox: new OBB(itemCenter, half, rotationMatrix),
        type: 'items',
      })
      logDimFlow({
        file: 'store/itemActions.ts',
        fn: 'setOBB',
        phase: 'obb.update',
        reason:
          'OBB halfSize = width/2, (height+zócalo)/2, thickness/2 del store. Centro = matrix + halfSize rotado.',
        snapshot: {
          ...buildDimFlowSnapshot(item),
          boundingBoxHalfSize: { x: half.x, y: half.y, z: half.z },
          extra: {
            zocaloLift,
            totalHeight,
            center: {
              x: Number(itemCenter.x.toFixed(2)),
              y: Number(itemCenter.y.toFixed(2)),
              z: Number(itemCenter.z.toFixed(2)),
            },
          },
        },
      })
    })
  },
  setOBBScene: () => {
    set((store) => {
      store.items.forEach((item, id) => {
        if (!isItem(item) && !isLinkedItem(item)) return
        const centerPosition = toVector3()
        const quaternion = toQuaternion()
        item.matrix.decompose(centerPosition, quaternion, toVector3())
        const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
        itemCenter.applyQuaternion(quaternion).add(centerPosition)
        const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
        const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
        store.sceneBoundingBoxes.set(id, {
          boundingBox: new OBB(
            itemCenter,
            new Vector3(item.width / 2, item.height / 2, item.thickness / 2),
            rotationMatrix,
          ),
          type: 'items',
        })
      })

      // Calcular OBB para ItemGroups: AABB en espacio mundo desde las matrices de los hijos
      store.items.forEach((item, id) => {
        if (item.type !== 'itemGroup') return
        const group = item as ItemGroup
        if (!group.matrix || !group.children.size) return

        const obbResult = computeItemGroupOBB(group.children, store.items, group.matrix)
        if (!obbResult) return

        store.sceneBoundingBoxes.set(id, {
          boundingBox: new OBB(obbResult.center, obbResult.halfSize, obbResult.rotation),
          type: 'items',
        })
      })
    })
  },

  // Actualizar la OBB de un ItemGroup específico calculando directamente desde las matrices de los hijos
  setItemGroupOBB: (groupId: AnyID) => {
    set((store) => {
      const group = store.items.get(groupId) as ItemGroup | undefined
      if (!group || group.type !== 'itemGroup' || !group.matrix || !group.children.size) return

      const obbResult = computeItemGroupOBB(group.children, store.items, group.matrix)
      if (!obbResult) return

      store.sceneBoundingBoxes.set(groupId, {
        boundingBox: new OBB(obbResult.center, obbResult.halfSize, obbResult.rotation),
        type: 'items',
      })

      // Sincronizar width/height/thickness del grupo con las dimensiones de la OBB
      // para que cualquier lógica de colisión que lea esos campos obtenga valores correctos
      group.width = obbResult.halfSize.x * 2
      group.height = obbResult.halfSize.y * 2
      group.thickness = obbResult.halfSize.z * 2
    })
  },
  setSceneGLB: (catalogID: string, scene: any) => {
    set((store) => {
      store.sceneGLB.set(catalogID, { glb: scene, glbData: {} })
    })
  },
  setItemPasteId: (id: string) => {
    set((store) => {
      store.itemsCopyId = id
    })
  },
  editItem: <T extends Item>(id: string, config?: Partial<Record<keyof T, T[keyof T]>>) => {
    if (config) {
      set((store) => {
        const items = store.items
        if (id === 'selected') {
          const ids = store.layers[store.currentLayer].selected
          ids.forEach((id) => {
            const item = store.items.get(id) as Item
            if (!item) return
            for (const [property, value] of Object.entries(config)) {
              item[property] = value
            }
            items.set(id, item)
          })
        } else {
          const item = store.items.get(id) as Item
          if (!item) return

          const matrix = item.matrix as Matrix4
          const centerPosition = new Vector3()
          const quaternion = new Quaternion()
          matrix.decompose(centerPosition, quaternion, new Vector3())

          const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])

          itemCenter.applyQuaternion(quaternion).add(centerPosition)

          // Convertir la rotación del quaternion a una Matrix4 y extraer la Matrix3
          const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
          const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

          const mode = store.mode

          // Aplicar restricción de altura en modo 3D cuando se actualiza y o matrix
          let restrictedY: number | undefined = undefined
          if (
            (config.y !== undefined || config.matrix !== undefined) &&
            (mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS')
          ) {
            // Obtener el Y actual: de config.y o de la matriz si viene en config.matrix
            let currentY = config.y as number | undefined
            if (currentY === undefined && config.matrix !== undefined) {
              const matrixPos = toVector3()
              ;(config.matrix as Matrix4).decompose(matrixPos, toQuaternion(), toVector3())
              currentY = matrixPos.y
            }
            if (currentY === undefined) {
              currentY = item.y
            }

            restrictedY = currentY

            // Restringir mínimo a 0 siempre
            restrictedY = Math.max(0, restrictedY)

            // Si el item tiene altura, aplicar restricción máxima
            if (item.height) {
              // Obtener altura máxima de la pared
              let maxWallHeight: number | null = null

              // Si el item está pegado a una pared, usar esa pared
              if (item.snappedWall) {
                const wall = store.items.get(item.snappedWall) as Walls
                if (isWall(wall)) {
                  maxWallHeight = wall.height
                }
              } else {
                // Si no está pegado, obtener la altura máxima de todas las paredes
                let maxHeight = 0
                store.items.forEach((sceneObject) => {
                  if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                    maxHeight = sceneObject.height
                  }
                })
                maxWallHeight = maxHeight > 0 ? maxHeight : null
              }

              // Restringir máximo considerando la altura del mueble
              if (maxWallHeight !== null) {
                const maxYAllowed = maxWallHeight - item.height
                restrictedY = Math.min(restrictedY, maxYAllowed)
              }
            }

            // Actualizar config.y y config.matrix con el valor restringido
            if (config.y !== undefined) {
              config.y = restrictedY as T[keyof T]
            }
            if (config.matrix !== undefined) {
              const matrix = config.matrix as Matrix4
              const matrixPos = toVector3()
              const matrixRot = toQuaternion()
              const matrixScale = toVector3()
              matrix.decompose(matrixPos, matrixRot, matrixScale)
              const restrictedMatrix = new Matrix4()
                .makeRotationFromQuaternion(matrixRot)
                .setPosition(matrixPos.x, restrictedY, matrixPos.z)
              config.matrix = restrictedMatrix as T[keyof T]
            }
          }

          for (const [property, value] of Object.entries(config)) {
            item[property] = value
          }

          // Si se aplicó restricción pero no se actualizó y directamente, actualizarlo ahora
          if (restrictedY !== undefined && config.y === undefined) {
            item.y = restrictedY
            // También actualizar la matriz si existe
            if (item.matrix) {
              const matrixPos = toVector3()
              const matrixRot = toQuaternion()
              const matrixScale = toVector3()
              item.matrix.decompose(matrixPos, matrixRot, matrixScale)
              item.matrix.setPosition(matrixPos.x, restrictedY, matrixPos.z)
            }
          }

          // CORRECCIÓN ABSOLUTA: asegurar que item.y NUNCA sea negativo en modo 3D o alzados
          // Esta es la última línea de defensa - se ejecuta SIEMPRE después de cualquier actualización
          if (mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS') {
            if (item.y < 0) {
              item.y = 0
              // Actualizar la matriz también para mantener consistencia
              const matrixPos = toVector3()
              const matrixRot = toQuaternion()
              const matrixScale = toVector3()
              item.matrix.decompose(matrixPos, matrixRot, matrixScale)
              item.matrix.setPosition(matrixPos.x, 0, matrixPos.z)
            }
          }

          // Recalcular itemCenter si se aplicó restricción
          if (restrictedY !== undefined) {
            const finalMatrix = item.matrix as Matrix4
            const finalCenterPosition = toVector3()
            const finalQuaternion = toQuaternion()
            finalMatrix.decompose(finalCenterPosition, finalQuaternion, toVector3())
            const finalItemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
            finalItemCenter.applyQuaternion(finalQuaternion).add(finalCenterPosition)
            itemCenter.copy(finalItemCenter)
          }

          items.set(id, item)
          store.sceneBoundingBoxes.set(id, {
            boundingBox: new OBB(
              itemCenter,
              toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
              rotationMatrix,
            ),
            type: 'items',
          })
        }
      })
    }
  },
  editItemPosition: (id: string, x: number, z: number, y?: number) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      if (!item.visible) item.visible = true
      item.matrix = item.matrix.clone().setPosition(x, item.y, z)
    })
  },
  editItemHeight: (height: number, id?: string) => {
    set((store) => {
      const mode = store.mode

      if (id) {
        const item = store.items.get(id) as Item
        if (!item) return

        // En modo alzados o 3D, restringir entre 0 y altura máxima considerando la altura del mueble
        if (mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS' || mode === 'MODE_3D_VIEW') {
          height = Math.max(0, height)

          // Obtener altura máxima de la pared
          let maxWallHeight: number | null = null

          // Si el item está pegado a una pared, usar esa pared
          if (item.snappedWall && item.height) {
            const wall = store.items.get(item.snappedWall) as Walls
            if (isWall(wall)) {
              maxWallHeight = wall.height
            }
          } else if (item.height) {
            // Si no está pegado, obtener la altura máxima de todas las paredes
            let maxHeight = 0
            store.items.forEach((sceneObject) => {
              if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                maxHeight = sceneObject.height
              }
            })
            maxWallHeight = maxHeight > 0 ? maxHeight : null
          }

          // Restringir máximo considerando la altura del mueble
          // Si la pared tiene altura 2500 y el mueble tiene altura 800, el máximo Y es 2500 - 800 = 1700
          if (maxWallHeight !== null && item.height) {
            const maxYAllowed = maxWallHeight - item.height
            height = Math.min(height, maxYAllowed)
          }
        }

        const pos = toVector3()
        const rotation = toQuaternion()
        const scale = toVector3()
        item.matrix.decompose(pos, rotation, scale)

        // CORRECCIÓN FINAL: asegurar que height nunca sea negativo (última línea de defensa)
        const finalHeight = Math.max(0, height)

        // Para luces, NO reconstruir la matriz - solo cambiar la posición Y
        // Esto preserva la rotación exacta que ya está en la matriz
        if (item.type === 'lights') {
          const rotationAntes = item.rotation

          // console.log('🔧 editItemHeight - LUZ (solo cambiando Y):', {
          //   id: item.id,
          //   catalogID: item.catalogID,
          //   rotationFromStore: item.rotation,
          //   heightAntes: pos.y,
          //   heightNuevo: finalHeight,
          // })

          // Solo cambiar la posición Y sin reconstruir la matriz completa
          // Esto preserva la rotación exacta
          item.matrix.setPosition(pos.x, finalHeight, pos.z)
          item.y = finalHeight

          // Asegurar que la rotación NO cambie
          if (rotationAntes !== item.rotation) {
            // console.error('🔧 editItemHeight - ERROR: La rotación cambió!', {
            //   rotationAntes,
            //   rotationDespues: item.rotation,
            // })
            // Restaurar la rotación original
            item.rotation = rotationAntes
          }

          // console.log('🔧 editItemHeight - DESPUÉS:', {
          //   id: item.id,
          //   rotationEnStore: item.rotation,
          //   y: item.y,
          // })
        } else {
          // Para otros items, reconstruir la matriz normalmente
          item.matrix.makeRotationFromQuaternion(rotation)
          item.matrix.setPosition(pos.x, finalHeight, pos.z)
          item.matrix.scale(scale)
          item.y = finalHeight
        }
      } else {
        const selectedItems = store.layers[store.currentLayer].selected
        selectedItems.forEach((id) => {
          const item = store.items.get(id) as Item
          if (!item) return

          let finalHeight = height

          // En modo alzados o 3D, restringir entre 0 y altura máxima considerando la altura del mueble
          if (mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS' || mode === 'MODE_3D_VIEW') {
            finalHeight = Math.max(0, finalHeight)

            // Obtener altura máxima de la pared
            let maxWallHeight: number | null = null

            // Si el item está pegado a una pared, usar esa pared
            if (item.snappedWall && item.height) {
              const wall = store.items.get(item.snappedWall) as Walls
              if (isWall(wall)) {
                maxWallHeight = wall.height
              }
            } else if (item.height) {
              // Si no está pegado, obtener la altura máxima de todas las paredes
              let maxHeight = 0
              store.items.forEach((sceneObject) => {
                if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                  maxHeight = sceneObject.height
                }
              })
              maxWallHeight = maxHeight > 0 ? maxHeight : null
            }

            // Restringir máximo considerando la altura del mueble
            // Si la pared tiene altura 2500 y el mueble tiene altura 800, el máximo Y es 2500 - 800 = 1700
            if (maxWallHeight !== null && item.height) {
              const maxYAllowed = maxWallHeight - item.height
              finalHeight = Math.min(finalHeight, maxYAllowed)
            }
          }

          const pos = toVector3()
          item.matrix.decompose(pos, toQuaternion(), toVector3())
          // CORRECCIÓN FINAL: asegurar que finalHeight nunca sea negativo (última línea de defensa)
          const correctedHeight = Math.max(0, finalHeight)
          item.matrix.setPosition(pos.x, correctedHeight, pos.z)
          item.y = correctedHeight
        })
      }
    })
  },
  incrementItemZ: (id: AnyID, z: number) => {
    const axis = 'z'
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()
      const rotation = toQuaternion()

      item.matrix = addItemOffset(item.matrix, z, axis)
      item.matrix.decompose(pos, rotation, toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.z = pos.z
      item.y = pos.y
      item.x = pos.x

      // Si es una luz, actualizar también el campo rotation desde la matriz
      if (item.type === 'lights') {
        const euler = new Euler().setFromQuaternion(rotation, 'YXZ')
        let rotationDegrees = euler.y * (180 / Math.PI)
        rotationDegrees = ((rotationDegrees % 360) + 360) % 360
        rotationDegrees = Math.round(rotationDegrees)

        // Si obtenemos 270 (que es -90) pero la store tiene 0 o no está definida, usar 90
        // Esto corrige la inconsistencia entre makeRotationY(radians(90)) y editItemRotation que usa radians(-rotation)
        if (
          rotationDegrees === 270 &&
          (item.rotation === 0 || item.rotation === undefined || item.rotation === null)
        ) {
          rotationDegrees = 90
        }

        item.rotation = rotationDegrees
      }
    })
  },
  incrementItemY: (selectedItems: Set<string>, y: number) => {
    const axis = 'y'
    set((store) => {
      const mode = store.mode

      selectedItems.forEach((id) => {
        const item = store.items.get(id) as Item
        if (!item) return

        const pos = toVector3()

        item.matrix = addItemOffset(item.matrix, y, axis)
        item.matrix.decompose(pos, toQuaternion(), toVector3())

        let newY = pos.y

        // En modo alzados o 3D, restringir entre 0 y altura máxima considerando la altura del mueble
        if (mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS' || mode === 'MODE_3D_VIEW') {
          // Restringir mínimo a 0 siempre
          newY = Math.max(0, newY)

          // Si el item tiene altura, aplicar restricción máxima
          if (item.height) {
            // Obtener altura máxima de la pared
            let maxWallHeight: number | null = null

            // Si el item está pegado a una pared, usar esa pared
            if (item.snappedWall) {
              const wall = store.items.get(item.snappedWall) as Walls
              if (isWall(wall)) {
                maxWallHeight = wall.height
              }
            } else {
              // Si no está pegado, obtener la altura máxima de todas las paredes
              let maxHeight = 0
              store.items.forEach((sceneObject) => {
                if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                  maxHeight = sceneObject.height
                }
              })
              maxWallHeight = maxHeight > 0 ? maxHeight : null
            }

            // Restringir máximo considerando la altura del mueble
            // Si la pared tiene altura 2500 y el mueble tiene altura 800, el máximo Y es 2500 - 800 = 1700
            if (maxWallHeight !== null) {
              const maxYAllowed = maxWallHeight - item.height
              newY = Math.min(newY, maxYAllowed)
            }
          }
        }

        // CORRECCIÓN FINAL: asegurar que newY nunca sea negativo (última línea de defensa)
        const finalY = Math.max(0, newY)

        item.matrix.setPosition(pos.x, finalY, pos.z)
        item.z = pos.z
        item.y = finalY
        item.x = pos.x

        // Si es una luz, NO recalcular la rotación desde la matriz
        // La rotación debe mantenerse igual cuando solo se cambia la posición Y
        // Solo actualizar si la rotación no está definida en la store
        if (item.type === 'lights') {
          if (item.rotation === undefined || item.rotation === null) {
            // Solo calcular desde la matriz si no está definida
            const rotation = toQuaternion()
            const scale = toVector3()
            item.matrix.decompose(pos, rotation, scale)
            const euler = new Euler().setFromQuaternion(rotation, 'YXZ')
            let rotationDegrees = euler.y * (180 / Math.PI)
            rotationDegrees = ((rotationDegrees % 360) + 360) % 360
            rotationDegrees = Math.round(rotationDegrees)

            // Si obtenemos 270 (que es -90) pero la store tiene 0 o no está definida, usar 90
            // Esto corrige la inconsistencia entre makeRotationY(radians(90)) y editItemRotation que usa radians(-rotation)
            if (
              rotationDegrees === 270 &&
              (item.rotation === 0 || item.rotation === undefined || item.rotation === null)
            ) {
              rotationDegrees = 90
            }

            item.rotation = rotationDegrees
          }
          // Si la rotación ya está definida en la store, mantenerla (no recalcular)
        }
      })
    })
  },
  incrementItemX: (id: AnyID, x: number) => {
    const axis = 'x'
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()
      const rotation = toQuaternion()

      item.matrix = addItemOffset(item.matrix, x, axis)
      item.matrix.decompose(pos, rotation, toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.z = pos.z
      item.y = pos.y
      item.x = pos.x

      // Si es una luz, actualizar también el campo rotation desde la matriz
      if (item.type === 'lights') {
        const euler = new Euler().setFromQuaternion(rotation, 'YXZ')
        let rotationDegrees = euler.y * (180 / Math.PI)
        rotationDegrees = ((rotationDegrees % 360) + 360) % 360
        rotationDegrees = Math.round(rotationDegrees)

        // Si obtenemos 270 (que es -90) pero la store tiene 0 o no está definida, usar 90
        // Esto corrige la inconsistencia entre makeRotationY(radians(90)) y editItemRotation que usa radians(-rotation)
        if (
          rotationDegrees === 270 &&
          (item.rotation === 0 || item.rotation === undefined || item.rotation === null)
        ) {
          rotationDegrees = 90
        }

        item.rotation = rotationDegrees
      }
    })
  },
  editItemRotation: (id: string, rotation: number) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return
      // Extraer la posición actual de la matriz
      const position = toVector3()
      const scale = toVector3()
      item.matrix.decompose(position, new Quaternion(), scale)
      // Crear nueva matriz con la rotación especificada y mantener posición y escala
      item.matrix.makeRotationY(radians(-rotation))
      item.matrix.setPosition(position)
      item.matrix.scale(scale)
      // Actualizar el campo rotation en el item
      item.rotation = rotation
    })
  },

  setItemVisible: (id: string) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return
      if (!item.visible) item.visible = true
    })
  },
  editItemZone: <T extends Item>(id: string, config?: Partial<Record<keyof T, T[keyof T]>>) => {
    set((store) => {
      if (!config) return
      const item = store.items.get(id) as Item

      if (!item) return

      const property = Object.keys(config)[0]

      if (config[property].value && config[property].id) {
        item.textures.forEach((prop: { zone: string }, propIndex: number) => {
          if (property === prop.zone) {
            //@ts-expect-error
            item.textures[propIndex].value = config[property].value
            //@ts-expect-error
            item.textures[propIndex].id = config[property].id
            item.textures[propIndex].idTexturas = config[property].idTexturas
            item.textures[propIndex].idGruposDeTexturas = config[property].idGruposDeTexturas
            item.textures[propIndex].nombre = config[property].nombre
          }
        })
      }

      Object.entries(item.zones).forEach(([zoneID, zone]: Zone, zoneIndex: number) => {
        console
        if (property === zone.material?.name) {
          const {
            color,
            roughness,
            mirror,
            repeatX,
            repeatY,
            metalness,
            opacity,
            envMapIntensity,
            displacement,
            displacementMap,
            bumpScale,
            map,
          } = config[property]

          const material = Object.values(item.zones)[zoneIndex].material

          const finalRepeatX = repeatX //>= 0.001 ? repeatX / 1000 : repeatX
          const finalRepeatY = repeatY //>= 0.001 ? repeatY / 1000 : repeatY

          item.zones[zoneID].material = {
            ...item.zones[zoneID].material,
            name: property,
            color: color ? (validateHex(color) ? color : '#FFFFFF') : material.color,
            roughness: roughness ?? material.roughness,
            mirror: mirror ?? material.mirror,
            repeatX: finalRepeatX ?? material.repeatX,
            repeatY: finalRepeatY ?? material.repeatY,
            map: map ?? material.map,
            metalness: metalness ?? material.metalness,
            opacity: opacity ?? material.opacity,
            envMapIntensity: envMapIntensity ?? material.envMapIntensity,
            displacement: displacement ?? material.displacement,
            displacementMap: displacementMap ?? material.displacementMap,
            bumpScale: bumpScale ?? material.bumpScale,
            transparent: opacity < 1 ? true : false,
            depthWrite: opacity < 1 ? false : true,
            scale: toVector3([1, 1, 1]),
          }
        }
      })
    })
  },
  editGLBSize: (id: string, sizeVector: Vector3) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return
      item.glbSize = sizeVector
    })
  },
  editSnappedWall: (id: string, wallID?: string) => {
    set((store) => {
      const item = store.items.get(id) as Item
      const wall = store.items.get(wallID) as Walls
      if (!(isItem(item) || !isLinkedItem(item)) || !isWall(wall) || !id || !wallID) return
      item.snappedWall = wallID
      if (item.tipoNormalizado === 'MAL' && wall.highItems) {
        wall.highItems.add(item.id)
      }
      if ((item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') && wall.lowItems) {
        wall.lowItems && wall.lowItems.add(item.id)
      }
      const area = wall.areas
      if (!item.tipoNormalizado || !dictionary[item.tipoNormalizado]) return
      area.forEach((areaID) => {
        const currArea = store.areas.get(areaID)
        if (isArea(currArea)) {
          currArea.sides.forEach((singleWall) => {
            if (singleWall === wallID) return // console.log('Same wall found', { wallID, singleWall }) // Si es la misma wall que está snapeando pasamos
            const singleWallObject = store.items.get(singleWall) as Walls
            if (isWall(singleWall)) {
              // Sino, buscamos si cualquier otra pared tiene este objeto pegado
              singleWallObject[dictionary[item.tipoNormalizado]].delete(id)
            }
          })
        }
      })
    })
  },
  removeSnappedWall: (id: string) => {
    set((store) => {
      const selectedIds = store.layers[store.currentLayer].selected as Set<AnyID>
      const targets = new Set<AnyID>()
      if (id) targets.add(id as AnyID)
      selectedIds?.forEach((selectedId) => targets.add(selectedId))

      targets.forEach((targetId) => {
        const item = store.items.get(targetId) as Item | LinkedItem
        const wall = store.items.get(item?.snappedWall ?? '') as Walls
        if (!item || (!isItem(item) && !isLinkedItem(item))) return
        // Al despegar, liberar también las caras registradas (item↔item / item↔pared)
        releaseSnappedSides(store.items, targetId)
        if (!isWall(wall)) return

        if (item.tipoNormalizado === 'MAL') {
          wall.highItems.delete(item.id)
        }
        if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
          wall.lowItems.delete(item.id)
        }
        item.snappedWall = ''
      })
    })
  },
  removeSnappedPartition: (id: string) => {
    set((store) => {
      const selectedIds = store.layers[store.currentLayer].selected as Set<AnyID>
      const targets = new Set<AnyID>()
      if (id) targets.add(id as AnyID)
      selectedIds?.forEach((selectedId) => targets.add(selectedId))

      targets.forEach((targetId) => {
        const item = store.items.get(targetId) as Item | LinkedItem
        const partition = store.items.get(item?.snappedWall ?? '') as Partition
        if (!item || (!isItem(item) && !isLinkedItem(item))) return
        releaseSnappedSides(store.items, targetId)
        if (!isPartition(partition)) return

        // console.log('[removeSnappedPartition]', {
        //   targetId,
        //   snappedWall: item.snappedWall,
        //   tipo: item.tipoNormalizado,
        // })

        if (item.tipoNormalizado === 'MAL') {
          partition.highItems.delete(item.id)
        }
        if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
          partition.lowItems.delete(item.id)
        }
        item.snappedWall = ''
      })
    })
  },
  editItemAngle: (id: AnyID, angle: number) => {
    set((store) => {
      const item = store.items.get(id)
      if (!item) return

      // Manejar ItemGroups
      if (item.type === 'itemGroup') {
        const position = toVector3()
        item.matrix.decompose(position, new Quaternion(), toVector3())
        item.matrix.makeRotationY(radians(-angle))
        item.matrix.setPosition(position)
        const newMatrix = item.matrix.clone()
        const updatedItem = {
          ...item,
          matrix: newMatrix,
        }
        store.items.set(id, updatedItem)
        // Actualizar la OBB del grupo después de cambiar su rotación
        get().setItemGroupOBB(id)
        return
      }

      // Manejar Items normales y LinkedItems
      if (!isItem(item) && !isLinkedItem(item)) return
      const position = toVector3()
      item.matrix.decompose(position, new Quaternion(), toVector3())
      item.matrix.makeRotationY(radians(-angle))
      item.matrix.setPosition(position)
      const newMatrix = item.matrix
      const updatedItem = {
        ...item,
        matrix: newMatrix,
      }
      store.items.set(id, updatedItem)

      // Girar en Y invalida el registro de caras: el giro es sobre el origen de la matriz
      // (esquina, no centro), así que el mueble se va a otro sitio Y con otra orientación,
      // y las etiquetas que traía dejan de corresponder con la geometría. `left` pasa a ser
      // `back`, el vecino de al lado deja de tocarse... Es el caso de montar una isla:
      // copiar un mueble al lado y girar la copia a 270 para dejarlas espalda contra
      // espalda. Se suelta el registro del girado (y las referencias recíprocas de sus
      // vecinos) y se vuelve a levantar desde la geometría: lo que siga tocándose se
      // registra otra vez, con la cara que le toca ahora, y el contacto nuevo aparece como
      // enlace de profundidad (front/back) — que es lo que convierte dos filas en isla.
      // rebuildSnappedSides no bastaba solo: no reetiqueta lo ya registrado, así que el
      // `left` viejo tapaba el `back` nuevo y el par seguía contando como una sola fila.
      releaseSnappedSides(store.items, id)
      rebuildSnappedSides(store.items, getSnapCandidates(store.items.values()))
    })
  },
  editItemAngleX: (id: AnyID, angle: number) => {
    set((store) => {
      const item = store.items.get(id) as Item | LinkedItem
      if (!isItem(item) && !isLinkedItem(item)) return
      const position = toVector3()
      item.matrix.decompose(position, new Quaternion(), toVector3())
      item.matrix.makeRotationX(radians(-angle))
      item.matrix.setPosition(position)
      const newMatrix = item.matrix
      const updatedItem = {
        ...item,
        matrix: newMatrix,
      }
      store.items.set(id, updatedItem)
    })
  },
  editItemAngleZ: (id: AnyID, angle: number) => {
    set((store) => {
      const item = store.items.get(id) as Item | LinkedItem
      if (!isItem(item) && !isLinkedItem(item)) return
      const position = toVector3()
      item.matrix.decompose(position, new Quaternion(), toVector3())
      item.matrix.makeRotationZ(radians(-angle))
      item.matrix.setPosition(position)
      const newMatrix = item.matrix
      const updatedItem = {
        ...item,
        matrix: newMatrix,
      }
      store.items.set(id, updatedItem)
    })
  },
  setIsBlocked: (blocked: boolean, id: AnyID) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return
      item.isBlocked = blocked
    })
  },
  setCollision: (collision: boolean, id: AnyID) => {
    set((store) => {
      const item = store.items.get(id) as Item
      const collisionableItems = store.collisionableItems
      if (!item) return
      item.canCollide = collision
      if (collisionableItems.has(id)) {
        collisionableItems.delete(id)
      } else {
        collisionableItems.add(id)
      }
    })
  },
  removeCollision: (collision: boolean, id: string, isShiftKey: boolean) => {
    set((store) => {
      const item = store.items.get(id)
      const collisionableItems = store.collisionableItems
      if (isShiftKey) {
        if (!item) return
        item.canCollide = collision
        collisionableItems.delete(id)
      } else {
        collisionableItems.add(id)
        item.canCollide = !collision
      }
    })
  },
  /**
   * Decide dónde queda un item recién dibujado sin depender de una matriz de entrada.
   * Prueba las caras del item de referencia (el que estaba seleccionado) en orden y se
   * queda con la primera que admita el item:
   *
   *   DERECHA → IZQUIERDA → FRONTAL → centro de la escena
   *
   * Una cara se descarta si ya tiene vecino (isPlacementSideTaken), si al item no le
   * queda pared donde caber por ese lado (overflowsReferenceWall) o si collideItems no
   * encuentra hueco. Sin referencia utilizable se va directo al centro de la escena.
   *
   * Los rinconeros no juegan con los dos laterales: solo con el del giro en L y el
   * frontal (ver getPlacementSides), el resto de sus caras van contra pared.
   *
   * El pegado fino lo hace collideItems: aquí solo se pre-coloca el item de la cara
   * elegida, porque collideItems no rota ni desplaza lateralmente, solo corrige contra la
   * cara tocada.
   */
  /**
   * Sienta un pequeño electrodoméstico o decoración sobre el tope (encimera o
   * cuerpo de un bajo). Sin `hostId` busca esa unidad bajo la huella XZ.
   * Altos, columnas y laterales no sientan: el arrastre los manda al suelo.
   */
  seatSmallApplianceOnHost: (applianceId: AnyID, hostId?: AnyID, centerOnUnit = false) => {
    const items = get().items
    const appliance = items.get(applianceId)
    if (!isSmallCountertopAppliance(appliance)) return false

    const host = hostId ? items.get(hostId) : findHostUnitUnderAppliance(appliance, items)
    if (!host || !isEncimeraHostUnit(host, items)) return false

    const pose = computeSeatedAppliancePose(appliance, host, items, { centerOnUnit })
    placeItemAt(set, applianceId, pose.position, pose.quaternion)
    return true
  },
  /**
   * Tras un swap de placa (o fregadero), vuelve a sentar los pequeños que
   * estaban sobre ese mueble para que el Y coincida con el nuevo tope.
   */
  reseatSmallAppliancesOnHost: (hostId: AnyID) => {
    const items = get().items
    const host = items.get(hostId)
    if (!host || !isEncimeraHostUnit(host, items)) return
    for (const pose of collectReseatPosesOnHost(host, items)) {
      placeItemAt(set, pose.id, pose.position, pose.quaternion)
    }
  },
  reseatSmallAppliancesOnLinkedSon: (linkedSonId: AnyID) => {
    const son = get().items.get(linkedSonId)
    const parentId = son && 'firstParentID' in son ? son.firstParentID : undefined
    if (!parentId) return
    get().reseatSmallAppliancesOnHost(parentId)
  },
  /**
   * Arrastre libre de pequeños: XZ suelto, Y según placa/fregadero/encimera/suelo.
   * No atraviesa paredes ni sale de la estancia.
   */
  constrainSmallApplianceDrag: (
    applianceId: AnyID,
    proposed?: { x: number; y: number; z: number },
  ) => {
    const items = get().items
    const appliance = items.get(applianceId)
    if (!isSmallCountertopAppliance(appliance)) return
    const current = new Vector3()
    appliance.matrix.decompose(current, new Quaternion(), new Vector3())
    const target = proposed ? new Vector3(proposed.x, proposed.y, proposed.z) : current
    const result = resolveSmallApplianceFreeDrag(appliance, items, target)
    if (!result) return
    placeItemAt(set, applianceId, result.position, result.quaternion)
  },
  placeDrawnItem: (itemId: AnyID, referenceId?: AnyID) => {
    const drawn = get().items.get(itemId)
    if (!drawn || (!isItem(drawn) && !isLinkedItem(drawn))) return

    const alignGvZocaloToNeighbor = (collidedId?: AnyID) => {
      set((store) => {
        const parent = store.items.get(itemId) as LinkedItem | undefined
        if (parent) syncGvIndependentZocaloToRun(parent, store.items, collidedId)
      })
      get().setOBB(itemId)
    }

    const candidate = referenceId && referenceId !== itemId ? get().items.get(referenceId) : null
    let reference =
      candidate && (isItem(candidate) || isLinkedItem(candidate)) ? candidate : undefined

    if (isDecorationMenuUnit(drawn)) {
      const quaternion = new Quaternion()
      drawn.matrix.decompose(new Vector3(), quaternion, new Vector3())

      if (reference) {
        const referenceIsRinconero = getRinconeroFreeSide(reference) !== null
        let hasFreeLateralWithoutRoom = false
        for (const side of getPlacementSides(get, reference)) {
          if (isPlacementSideTaken(get, reference, side, drawn)) continue
          if (side === 'front' && !referenceIsRinconero && !hasFreeLateralWithoutRoom) break
          if (side === 'left' || side === 'right') hasFreeLateralWithoutRoom = true
          const { origin, quaternion: sideQuat } = getSidePlacement(drawn, reference, side)
          origin.y = getFloorApplianceYMm(drawn)
          if (doesPosePenetrateSolids(drawn, origin, sideQuat, get().items)) continue
          placeItemAt(set, itemId, origin, sideQuat)
          return
        }
      }

      const origin = getSceneCenterXZ(get).sub(
        new Vector3(drawn.width / 2, 0, drawn.thickness / 2).applyQuaternion(quaternion),
      )
      origin.y = getFloorApplianceYMm(drawn)
      placeItemAt(
        set,
        itemId,
        findNonPenetratingPosition(drawn, get().items, origin, quaternion),
        quaternion,
      )
      return
    }

    if (isSmallCountertopAppliance(drawn)) {
      if (reference && isEncimeraHostUnit(reference, get().items)) {
        if (get().seatSmallApplianceOnHost(itemId, reference.id, true)) return
      }

      const quaternion = new Quaternion()
      const current = new Vector3()
      drawn.matrix.decompose(current, quaternion, new Vector3())
      const origin = reference
        ? current
        : getSceneCenterXZ(get).sub(
            new Vector3(drawn.width / 2, 0, drawn.thickness / 2).applyQuaternion(quaternion),
          )
      origin.y = getFloorApplianceYMm(drawn)
      placeItemAt(
        set,
        itemId,
        findNonPenetratingPosition(drawn, get().items, origin, quaternion),
        quaternion,
      )
      return
    }

    if (reference) {
      const drawnTipo = (drawn as LinkedItem).tipoNormalizado
      const referenceTipo = (reference as LinkedItem).tipoNormalizado

      // MAL seleccionado + bajo nuevo: el bajo se engancha al final del tramo bajo de
      // esa pared, no al alto.
      if (referenceTipo === 'MAL' && (drawnTipo === 'MBJ' || drawnTipo === 'COL')) {
        reference = findLowGroupEndForMal(get, reference as LinkedItem) ?? reference
      }

      // Apilado vertical (alto sobre bajo, bajo bajo alto): no hay lateral en juego.
      const isVerticalPair =
        !isGvMoldingUnit(drawn) &&
        ((drawnTipo === 'MAL' && (reference as LinkedItem).tipoNormalizado === 'MBJ') ||
          ((drawnTipo === 'MBJ' || drawnTipo === 'COL') &&
            (reference as LinkedItem).tipoNormalizado === 'MAL'))
      if (isVerticalPair) {
        stackItemOverReference(get, set, itemId, reference)
        snapItemToSlab(get, set, itemId, reference.snappedWall as AnyID, true)
        alignGvZocaloToNeighbor(reference.id)
        return
      }

      // El frontal de un mueble normal es un recurso, no una cara más: solo se prueba
      // cuando algún lateral estaba libre pero el item no llegó a caber (sin tramo de
      // pared o sin hueco). Con los dos laterales ocupados el item se va al centro de la
      // escena. En un rinconero no aplica: ahí el frontal SÍ es una cara suya de pleno
      // derecho, la que arranca el tramo perpendicular.
      const referenceIsRinconero = getRinconeroFreeSide(reference) !== null
      let hasFreeLateralWithoutRoom = false

      for (const side of getPlacementSides(get, reference)) {
        if (isPlacementSideTaken(get, reference, side, drawn)) continue
        if (side === 'front' && !referenceIsRinconero && !hasFreeLateralWithoutRoom) break
        // Se llega aquí con el lateral libre: si no acaba colocando es por falta de sitio.
        if (side === 'left' || side === 'right') hasFreeLateralWithoutRoom = true
        if (overflowsReferenceWall(get, reference, side, drawn)) continue
        alignItemToSide(get, set, itemId, reference, side)
        if (!get().collideItems(itemId, reference.id)) continue
        // Un vecino lateral sigue la misma pared que la referencia; el que sale por el
        // frontal de un rinconero arranca el tramo de la pared perpendicular (solo si el
        // rinconero hace esquina: suelto a mitad de pared se queda pegado a su frontal).
        snapItemToSlab(
          get,
          set,
          itemId,
          side === 'front'
            ? ((getRinconeroPerpendicularWall(get, reference) ?? '') as AnyID)
            : (reference.snappedWall as AnyID),
        )
        alignGvZocaloToNeighbor(reference.id)
        return
      }
    }

    if (reference && getRinconeroHandedness(reference.catalogID)) {
      // console.warn('[KBR-DIAG] placeDrawnItem: ninguna cara admitió el snap → centro', {
      //   referenceCatalog: reference.catalogID,
      //   drawnCatalog: (drawn as LinkedItem).catalogID,
      //   sidesTried: getPlacementSides(get, reference),
      //   doorRunSide: getRinconeroFreeSide(reference),
      // })
    }

    // Centro de la escena: el que se centra es el item, y su origen de matriz es la
    // esquina, así que hay que descontar media huella (la Y sigue siendo la de catálogo).
    const quaternion = new Quaternion()
    drawn.matrix.decompose(new Vector3(), quaternion, new Vector3())
    const origin = getSceneCenterXZ(get).sub(
      new Vector3(drawn.width / 2, 0, drawn.thickness / 2).applyQuaternion(quaternion),
    )
    origin.y = drawn.y
    placeItemAt(set, itemId, origin)
    alignGvZocaloToNeighbor()
  },
  collideItems: (itemId: AnyID, itemId1: AnyID) => {
    // Resultado del snap: false cuando el snap se bloquea (cara ocupada) para que el
    // componente que arrastra no congele el item y siga el movimiento libre.
    const moving = get().items.get(itemId)
    if (isSmallCountertopAppliance(moving)) return false
    const snapResult = { snapped: false }
    set((store) => {
      const item = store.items.get(itemId) as LinkedItem
      const item1 = store.items.get(itemId1) as LinkedItem
      if (!item || !item1) return
      const rinconeroHand = getRinconeroHandedness(item1.catalogID)
      const isRinconero = rinconeroHand != null
      // Free-dim (regletas RA/RB/RC, laterales…): morph.default es la ref GLB de escala, no el ancho activo.
      const itemWidth = getCollisionWidthMm(item)
      const item1Width = getCollisionWidthMm(item1)
      const item1Matrix = item1.matrix.clone()
      const item1Pos = new Vector3()
      const item1Quat = new Quaternion()
      item1Matrix.decompose(item1Pos, item1Quat, new Vector3())

      // Variable que puede ser modificada por la lógica especial de apilamiento MAL/MBJ
      let newPosition

      // Obtener la posición actual del item para casos especiales (MAL sobre MBJ/MAL, MBJ bajo MAL)
      const itemCurrentPos = new Vector3()
      const itemCurrentQuat = new Quaternion()
      item.matrix.decompose(itemCurrentPos, itemCurrentQuat, new Vector3())

      // Detectar si item1 es KARB (arp, arb) - estos usan regleta directa, NO invertida
      const isKARB = isKARBCatalog(item1.catalogID)
      const reachMm = isRinconero ? getRinconeroRegletaFrontReachMm(item1) : 0
      const item1RegletaRealValue = isRinconero
        ? Math.max(0, reachMm - (item1.thickness || 0))
        : 0

      // CASO ESPECIAL: MAL sobre MBJ o MBJ bajo MAL - preservar posición horizontal actual
      // Para todos los demás casos (MBJ con MBJ, MAL con MAL, COL con COL, MBJ con COL, MAL con COL):
      // usar la misma lógica simple de colisión lateral
      const isMALOverMBJ = item.tipoNormalizado === 'MAL' && item1.tipoNormalizado === 'MBJ'
      const isMBJUnderMAL = item.tipoNormalizado === 'MBJ' && item1.tipoNormalizado === 'MAL'

      // Los itemGroups no participan en el registro de caras: se mueven como bloque.
      const canRegisterSnappedSides = item.type !== 'itemGroup' && item1.type !== 'itemGroup'

      // Topes al deslizarse por una cara: cualquier item colisionable ya apoyado en ella
      // frena el desplazamiento lateral en vez de dejar que el arrastrado lo atraviese.
      // Se usa el mismo conjunto que el arrastre (collisionableItems), así que los items
      // con la colisión desactivada y los hijos de un grupo no cuentan como tope.
      const obstacleBoxes: OBB[] = []
      store.collisionableItems.forEach((otherId) => {
        if (otherId === itemId || otherId === itemId1) return
        const box = store.sceneBoundingBoxes.get(otherId as AnyID)?.boundingBox
        if (box) obstacleBoxes.push(box)
      })

      // Snap tipo pouff (portado de atemporal): los items/linkedItems normales se pegan
      // a la primera cara del OBB de item1 que encuentra el centro del item arrastrado
      // (izq/der/frente/atrás indistintamente) y NUNCA rotan: solo se desplazan a lo
      // largo de la normal de esa cara hasta quedar a tope. TODOS los pares pasan por
      // esta misma rama ahora (rinconero-anchor vs normal, rinconero vs rinconero, y
      // rinconero arrastrado vs normal): la única diferencia, cuando item1 es rinconero,
      // es que sus caras trasera y lateral-no-L quedan siempre fuera de juego (como si ya
      // estuvieran contra pared) y su cara tocada (frontal o el lateral del giro en L)
      // suma el saliente de la regleta a su extensión. Cuando el rinconero es el item
      // ARRASTRADO y item1 es normal, no hay ninguna cara especial que imponer (item1 no
      // es rinconero), así que se comporta exactamente como cualquier item normal. Solo
      // el apilamiento vertical MAL/MBJ queda fuera, con la lógica antigua de más abajo.
      if (!isMALOverMBJ && !isMBJUnderMAL) {
        const itemPos = new Vector3()
        const itemQuat = new Quaternion()
        item.matrix.decompose(itemPos, itemQuat, new Vector3())

        // itemPos/item1Pos son la esquina inferior-trasera-izquierda (origen de la
        // matriz), no el centro: desplazar por la mitad de cada dimensión rotada
        // para obtener el centro real del OBB.
        const item1Center = getObbCenter(
          item1Pos,
          item1Quat,
          item1Width,
          item1.height || 0,
          item1.thickness || 0,
        )
        const itemCenterCurrent = getObbCenter(
          itemPos,
          itemQuat,
          itemWidth,
          item.height || 0,
          item.thickness || 0,
        )

        // Un lateral ya ocupado no admite otro vecino. En un rinconero valen el tramo de
        // puerta y el frontal (giro en L): la trasera va contra pared, y el lateral del
        // giro solo se ofrece si el rinconero no hace esquina (suelto a mitad de pared).
        // Abortar cuando getTouchedFace devolvía 'back' dejaba el item metido dentro al
        // arrastrar por la pared por encima del rinconero.
        const liveBlocked = (side: SnapSide) => {
          const occupant = item1.snappedSides?.[side]
          return Boolean(occupant && occupant !== itemId && store.items.has(occupant))
        }
        let touched = rinconeroHand
          ? getRinconeroTouchedFace(
              rinconeroHand,
              item1Quat,
              item1Center,
              item1Width / 2,
              (item1.thickness || 0) / 2,
              itemCenterCurrent,
              liveBlocked(getRinconeroDoorRunSide(rinconeroHand)),
              liveBlocked(getRinconeroLTurnSide(rinconeroHand)) ||
                isRinconeroAtWallCorner(get, item1),
            )
          : getTouchedFace(
              item1Quat,
              item1Center,
              item1Width / 2,
              (item1.thickness || 0) / 2,
              itemCenterCurrent,
              {
                left: liveBlocked('left'),
                right: liveBlocked('right'),
              },
            )

        const item1XAxisDetect = new Vector3(1, 0, 0).applyQuaternion(item1Quat)
        const item1ZAxisDetect = new Vector3(0, 0, 1).applyQuaternion(item1Quat)

        // Giro en L: misma rotación y ras del extremo que getSidePlacement / el I.
        if (isRinconero && rinconeroHand && !touched.onXFace && touched.side === 'front') {
          itemQuat.copy(
            item1Quat
              .clone()
              .multiply(
                new Quaternion().setFromAxisAngle(
                  new Vector3(0, 1, 0),
                  rinconeroHand === 'right' ? -Math.PI / 2 : Math.PI / 2,
                ),
              ),
          )
          const halfAlongRefX = (item.thickness || 0) / 2
          const targetLocalX =
            (rinconeroHand === 'right' ? 1 : -1) * (item1Width / 2 - halfAlongRefX)
          const centerNow = getObbCenter(
            itemPos,
            itemQuat,
            itemWidth,
            item.height || 0,
            item.thickness || 0,
          )
          const currentLocalX = item1XAxisDetect.dot(
            new Vector3(centerNow.x - item1Center.x, 0, centerNow.z - item1Center.z),
          )
          itemPos.add(item1XAxisDetect.clone().multiplyScalar(targetLocalX - currentLocalX))
          const centerAligned = getObbCenter(
            itemPos,
            itemQuat,
            itemWidth,
            item.height || 0,
            item.thickness || 0,
          )
          touched = {
            ...touched,
            axis: item1ZAxisDetect,
            projection: item1ZAxisDetect.dot(
              new Vector3(centerAligned.x - item1Center.x, 0, centerAligned.z - item1Center.z),
            ),
            extent: (item1.thickness || 0) / 2,
          }
        }

        // Soporte del OBB del item proyectado sobre la normal de la cara tocada.
        const faceNormal = touched.axis.clone().multiplyScalar(touched.sign)
        const itemXAxis = new Vector3(1, 0, 0).applyQuaternion(itemQuat)
        const itemZAxis = new Vector3(0, 0, 1).applyQuaternion(itemQuat)
        const itemExtent =
          (itemWidth / 2) * Math.abs(itemXAxis.dot(faceNormal)) +
          ((item.thickness || 0) / 2) * Math.abs(itemZAxis.dot(faceNormal))

        // Solo el frontal de un rinconero tiene la regleta que sobresale del plano
        // nominal de item1.thickness: se suma a la extensión de esa cara. El lateral
        // del giro en L no lleva regleta, se toca con el plano OBB puro. El hueco total
        // reproduce EXACTO el de la rama antigua (item1.thickness/2 + item.thickness/2 +
        // regleta + ajuste KARB): la rama antigua forzaba la rotación del item para que
        // su grosor quedara siempre alineado con la normal, así que sumaba item.thickness/2
        // en crudo. Aquí el item no rota, así que itemExtent (proyección real de su grosor
        // sobre la normal) puede ser algo menor si su rotación actual no coincide del todo
        // con la del rinconero; se compensa esa diferencia para no perder separación.
        // La separación calibrada con la regleta corresponde a un item de 600 mm de
        // ancho apoyado en el frontal. Solo los más estrechos la recortan, con la regla
        // de 3 medida en el de 450 (150 mm menos de ancho → 75 mm menos de hueco), o sea
        // medio mm por cada mm de diferencia; de 600 en adelante todos mantienen el hueco
        // de 600. Nunca baja de 0, para que un item muy estrecho no acabe metido dentro
        // del rinconero.
        const rinconeroFrontWidthAdjust =
          isRinconero && !touched.onXFace
            ? Math.min(0, itemWidth - RINCONERO_FRONT_REFERENCE_WIDTH_MM) *
              RINCONERO_FRONT_WIDTH_GAP_RATIO
            : 0
        const item1ExtraExtent =
          isRinconero && !touched.onXFace
            ? Math.max(
                0,
                item1RegletaRealValue +
                  (isKARB ? -50 : 0) +
                  rinconeroFrontWidthAdjust +
                  Math.max(0, (item.thickness || 0) / 2 - itemExtent),
              )
            : 0

        // Posición del item pegado a tope contra `face`: corrige SOLO a lo largo de la
        // normal de esa cara (el item nunca rota ni se desplaza lateralmente). Las dos
        // caras opuestas comparten eje, así que itemExtent vale para ambas.
        const getFaceSnapPosition = (face: TouchedFace) => {
          const correction =
            face.sign * (face.extent + item1ExtraExtent + itemExtent) - face.projection
          const position = itemPos.clone().add(face.axis.clone().multiplyScalar(correction))
          position.y = item.y
          return position
        }

        const item1XAxisUnit = new Vector3(1, 0, 0).applyQuaternion(item1Quat)
        const item1ZAxisUnit = new Vector3(0, 0, 1).applyQuaternion(item1Quat)

        // Posición final sobre `face`: a tope contra ella y frenada por el primer tope
        // que encuentre al deslizarse. `null` si no cabe entre dos topes.
        const getStoppedFaceSnapPosition = (face: TouchedFace) => {
          const position = getFaceSnapPosition(face)
          // El frontal del rinconero es el arranque del retorno, no un tramo deslizante:
          // deslizar en X mete la unidad en el hueco de la L (el derecho, contra la
          // pared de esquina, era el que más lo sufría).
          if (isRinconero && !face.onXFace) return position
          // Al deslizarse por un lateral el item recorre el fondo de item1; por el
          // frente/trasera recorre su ancho.
          const tangent = face.onXFace ? item1ZAxisUnit : item1XAxisUnit
          const stopOffset = getSlideStopOffset({
            center: getObbCenter(position, itemQuat, item.width, item.height, item.thickness),
            tangent,
            normal: face.axis.clone().multiplyScalar(face.sign),
            halfTangent:
              (itemWidth / 2) * Math.abs(itemXAxis.dot(tangent)) +
              ((item.thickness || 0) / 2) * Math.abs(itemZAxis.dot(tangent)),
            halfNormal: itemExtent,
            halfHeight: (item.height || 0) / 2,
            obstacles: obstacleBoxes,
            tolerance: SNAP_FREE_SPACE_TOLERANCE_MM,
          })
          if (stopOffset === null) return null
          return position.add(tangent.clone().multiplyScalar(stopOffset))
        }

        // Los laterales que devuelve getTouchedFace están libres por construcción (los
        // ocupados se bloquearon arriba). Si en la cara elegida no hay hueco donde
        // pararse, se prueba la opuesta antes de abortar el snap. Un rinconero nunca
        // prueba la opuesta: su única cara Z válida es la frontal y sus laterales ya
        // vienen filtrados por getRinconeroTouchedFace (tramo de puerta, y el del giro
        // solo fuera de esquina).
        const faceCandidates: TouchedFace[] =
          isRinconero || touched.onXFace
            ? [touched]
            : [
                touched,
                {
                  ...touched,
                  side: OPPOSITE_SIDE[touched.side],
                  sign: (touched.sign * -1) as 1 | -1,
                },
              ]
        let chosenFace: TouchedFace | undefined
        let faceSnapPosition: Vector3 | undefined
        // El giro en L no puede depender del pouff (solo traslación): en el derecho
        // se acercaba al retorno y se quedaba con hueco. Misma pose que drawItem / el I.
        if (isRinconero && rinconeroHand && !touched.onXFace && touched.side === 'front') {
          const placed = getSidePlacement(item, item1, 'front')
          itemQuat.copy(placed.quaternion)
          chosenFace = {
            side: 'front',
            sign: 1,
            onXFace: false,
            axis: item1ZAxisDetect,
            projection: 0,
            extent: (item1.thickness || 0) / 2,
          }
          faceSnapPosition = placed.origin
        }
        for (const face of faceCandidates) {
          if (chosenFace && faceSnapPosition) break
          const occupantId = item1.snappedSides?.[face.side]
          const occupant =
            occupantId && occupantId !== itemId && store.items.has(occupantId)
              ? store.items.get(occupantId)
              : undefined
          // Paredes y tabiques ocupan la cara entera: no dejan hueco donde colarse.
          if (occupant?.type === 'walls' || occupant?.type === 'partition') continue
          const position = getStoppedFaceSnapPosition(face)
          if (!position) continue
          chosenFace = face
          faceSnapPosition = position
          break
        }
        // Ninguna cara admite el item: se bloquea el snap y el item sigue el arrastre libre.
        if (!chosenFace || !faceSnapPosition) return

        const itemScale = new Vector3()
        item.matrix.decompose(new Vector3(), new Quaternion(), itemScale)
        item.matrix.compose(faceSnapPosition, itemQuat, itemScale)
        item.x = faceSnapPosition.x
        item.y = faceSnapPosition.y
        item.z = faceSnapPosition.z
        item.rotation = radToDeg(new Euler().setFromQuaternion(itemQuat).y)

        const faceSnapCenter = getObbCenter(
          faceSnapPosition,
          itemQuat,
          item.width,
          item.height,
          item.thickness,
        )

        // Registrar caras ocupadas en ambos sentidos: la cara tocada de item1 queda
        // ocupada por item, y la cara de item que mira hacia item1 queda ocupada por item1.
        // La cara de item1 conserva a su primer ocupante cuando comparte hueco con otro
        // item: la adyacencia del segundo queda registrada en su propia cara hacia item1.
        if (canRegisterSnappedSides) {
          releaseSnappedSides(store.items, itemId)
          const item1SnapSide = chosenFace.side
          if (!liveBlocked(item1SnapSide)) {
            setSnappedSide(item1, item1SnapSide, itemId)
          }
          setSnappedSide(item, getSideFacing(itemQuat, faceSnapCenter, item1Center), itemId1)
        }

        // Heredar la pared: en un tramo recto es la misma que item1; en el frontal de
        // un rinconero en esquina es la pared perpendicular (el otro brazo de la L). Si
        // el rinconero está suelto a mitad de pared no hay pared que heredar.
        if (item1.snappedWall && item.type !== 'itemGroup') {
          const inheritWallId = (
            isRinconero && !chosenFace.onXFace
              ? getRinconeroPerpendicularWall(get, item1)
              : chosenFace.onXFace
                ? item1.snappedWall
                : undefined
          ) as AnyID | undefined
          const wall = inheritWallId ? (store.items.get(inheritWallId) as Walls | undefined) : undefined
          if (inheritWallId && wall) {
            item.snappedWall = inheritWallId
            if (item.tipoNormalizado === 'MAL') {
              wall.highItems.add(itemId)
            }
            if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
              wall.lowItems.add(itemId)
            }
            if (canRegisterSnappedSides) {
              const wallSide = getSideFacingSlab(itemQuat, faceSnapCenter, wall)
              if (wallSide && !item.snappedSides?.[wallSide]) {
                setSnappedSide(item, wallSide, inheritWallId)
              }
            }
          }
        }

        // Solo se mueve el GV que se está colocando. Si el apoyo es al revés
        // (una columna nueva contra un GV ya puesto), el GV no debe seguir la
        // pose intermedia: la columna todavía va centrada en el fondo de 25 mm
        // y el snap a la pared llega después, así que el GV se quedaba adelantado.
        if (isGvMoldingUnit(item)) syncGvIndependentZocaloToRun(item, store.items, itemId1)

        const alignedPos = new Vector3()
        const alignedQuat = new Quaternion()
        item.matrix.decompose(alignedPos, alignedQuat, new Vector3())
        const alignedCenter = getObbCenter(
          alignedPos,
          alignedQuat,
          item.width,
          item.height,
          item.thickness,
        )
        const faceSnapRotation = new Matrix3().setFromMatrix4(
          new Matrix4().makeRotationFromQuaternion(alignedQuat),
        )
        store.sceneBoundingBoxes.set(itemId, {
          boundingBox: new OBB(
            alignedCenter,
            toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
            faceSnapRotation,
          ),
          type: 'items',
        })

        snapResult.snapped = true
        return
      }

      // Único caso que llega aquí: apilamiento vertical MAL/MBJ (de cualquier pareja de
      // items). Rinconero-como-ancla, rinconero-vs-rinconero y rinconero-arrastrado-vs-
      // normal ya se resuelven (o bloquean) en la rama pouff de arriba, que siempre
      // retorna. Mantener la posición X/Z actual del item; solo se ajusta la Y debajo.
      newPosition = itemCurrentPos.clone()

      // Ajustar posición Y según tipoNormalizado
      // Solo casos de apilamiento vertical:
      // - MAL sobre MBJ: colocar encima del MBJ
      // - MBJ bajo MAL: colocar debajo del MAL (misma Y base)
      // Para todos los demás casos (MBJ con MBJ, MAL con MAL, COL con COL, MBJ con COL, MAL con COL):
      // mantener la posición Y del item (van al lado)
      // const originalY = newPosition.y
      if (isMALOverMBJ) {
        // MAL sobre MBJ: posición Y = base del MBJ + altura del MBJ
        newPosition.y = item1Pos.y + (item1.height || 0)
      } else {
        // MBJ bajo MAL: misma posición Y base que el MAL
        newPosition.y = item1Pos.y
      }

      // item1 nunca es rinconero aquí, así que no hay giro en L: el item conserva la
      // rotación de item1.
      const finalQuaternion = item1Quat
      const itemCenter = new Vector3(item.width / 2, item.height / 2, item.thickness / 2)
      itemCenter.applyQuaternion(finalQuaternion).add(newPosition)
      const rotationMatrix = new Matrix3().setFromMatrix4(
        new Matrix4().makeRotationFromQuaternion(finalQuaternion),
      )
      item.matrix.makeRotationFromQuaternion(finalQuaternion)
      // Usar newPosition.y para MAL sobre MBJ y MBJ bajo MAL
      item.matrix.setPosition(newPosition.x, newPosition.y, newPosition.z)

      item.x = newPosition.x
      item.y = newPosition.y // Actualizar item.y para mantener consistencia
      item.z = newPosition.z

      const eulerRotation = new Euler().setFromQuaternion(finalQuaternion)
      item.rotation = radToDeg(eulerRotation.y)
      store.sceneBoundingBoxes.set(itemId, {
        boundingBox: new OBB(
          itemCenter,
          toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
          rotationMatrix,
        ),
        type: 'items',
      })
      // El apilamiento vertical MAL/MBJ no ocupa ninguna de las 4 caras del plano XZ, así
      // que no registra snappedSides (a diferencia de la rama pouff de arriba).

      // ItemGroups no deben heredar snappedWall ni agregarse a las paredes
      if (item1.snappedWall && item.type !== 'itemGroup') {
        const wall = store.items.get(item1.snappedWall) as Walls
        item.snappedWall = item1.snappedWall
        if (item.tipoNormalizado === 'MAL') {
          wall.highItems.add(itemId)
        }
        if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
          wall.lowItems.add(itemId)
        }
      }
      snapResult.snapped = true
    })
    return snapResult.snapped
  },
  applyToAllItems: (originalID: string) => {
    set((store) => {
      const items = store.items
      const itemOriginal = items.get(originalID)

      if (!itemOriginal || !originalID) {
        console.error('No se encontró el item original o el ID original es inválido.')
        return
      }

      items.forEach((value, key) => {
        if (value?.idUnidades === itemOriginal.idUnidades && key !== originalID) {
          const originalZones = itemOriginal.zones
          const targetZones = value.zones

          const originalTextures: Textures = itemOriginal.textures
          const targetTextures: Textures = value.textures

          if (itemOriginal.type === 'walls' && value.type === 'partition') {
            Object.keys(originalZones).forEach((zoneName) => {
              if (targetZones[zoneName]) {
                targetZones[zoneName] = { ...originalZones[zoneName] }
              }
            })

            const updatedTextures: Textures = targetTextures.map((targetTexture) => {
              const originalTexture = originalTextures.find(
                (t) => t.zoneName === targetTexture.zoneName,
              )
              return originalTexture ? { ...targetTexture, ...originalTexture } : targetTexture
            })

            value.textures = updatedTextures
          } else {
            value.zones = { ...originalZones }

            const updatedTextures: Textures = targetTextures.map((targetTexture) => {
              const originalTexture = originalTextures.find(
                (t) => t.zoneName === targetTexture.zoneName,
              )
              return originalTexture ? { ...targetTexture, ...originalTexture } : targetTexture
            })

            value.textures = updatedTextures
          }
        }
      })
    })
  },
  getInitialAngleDegrees: (itemId: AnyID) => {
    const items = get().items
    const item = items.get(itemId)
    if (!item) {
      throw new Error(`Item with ID ${itemId} not found`)
    }

    const x = toVector3()
    const y = toVector3()
    const z = toVector3()

    // Extraer las bases de la matriz del item
    item.matrix.extractBasis(x, y, z)

    // Calcular el ángulo de rotación en el eje X
    const xRot = toVector3([1, 0, 0]).angleTo(x)

    // Calcular el producto cruzado
    const crossProduct = toVector3([1, 0, 0]).cross(x)

    // Calcular el ángulo inicial en grados
    const initialAngleDegrees = (degrees(xRot * -Math.sign(crossProduct.y)) + 360) % 360

    return initialAngleDegrees
  },
  /**
   * Rellena las caras (snappedSides) que no escribió nadie, desde la geometría.
   *
   * El registro de caras lo escriben `collideItems` y `projectItemOnSlabFaces`, y hay
   * items que no pasan por ninguno de los dos: el duplicado, por ejemplo, entra con
   * matriz explícita (sin `autoPlaceFromSelection`), así que llega a la escena pegado al
   * original pero sin vecino registrado, y `getSnapGroups` lo ve como un grupo de uno:
   * la encimera no se fusiona con el tramo y su UV no se recalcula.
   *
   * Misma pasada idempotente que la hidratación de proyecto: no toca lo ya registrado,
   * solo limpia referencias muertas y rellena caras libres. Ver rebuildSnappedSides.
   */
  hydrateSnappedSides: () => {
    set((store) => {
      rebuildSnappedSides(store.items, getSnapCandidates(store.items.values()))
    })
  },
  getContItems: () => {
    const validItems = getSnapCandidates(get().items.values())

    const isKBRItem = (item: LinkedItem) => item.catalogID?.toString().toUpperCase().includes('KBR')

    // Detección única de grupos: quién toca a quién sale SOLO del registro de caras
    // (snappedSides), sin volver a medir geometría, y de ahí sale también la forma del
    // grupo —línea, L, U, isla, cruz…— (ver getSnapGroups). Los items que llegan sin
    // registro (proyectos antiguos, ids remapeados al importar) los rellena
    // rebuildSnappedSides al cargar, así que aquí no hay respaldo geométrico que mantener.
    const snapGroups = getSnapGroups(validItems)
    const byId = new Map<AnyID, LinkedItem>(validItems.map((item) => [item.id, item]))

    const toEntry = (
      item: LinkedItem,
      orderIndex: number,
      group: SnapGroup,
      rowIndex: number,
      segmentIndex: number,
    ) => {
      const slots = group.runSlots.get(item.id) ?? {}
      return {
        id: item.id,
        catalogID: item.catalogID,
        tipoNormalizado: item.tipoNormalizado,
        isKBR: isKBRItem(item),
        position: { x: item.x, y: item.y, z: item.z },
        width: item.width,
        height: item.height,
        thickness: item.thickness,
        rotation: get().getInitialAngleDegrees(item.id),
        orderIndex,
        type: item.type,
        description:
          'description' in item
            ? (item as { description?: string }).description || 'Unknown'
            : 'Unknown',
        /**
         * Extremos a lo ancho: libres = ahí no sigue la fila, o sea principio/final de
         * grupo. Sale de los extremos de fila, NO de `snappedSides.left/right`: un
         * rinconero continúa la fila por su cara frontal y su extremo derecho está
         * ocupado aunque `snappedSides.right` esté vacío.
         */
        leftFree: !slots.left,
        rightFree: !slots.right,
        /** Grupo completo al que pertenece la fila, y qué forma tiene ese grupo */
        groupId: group.id,
        groupShape: group.shape,
        /** Fila dentro del grupo: rowCount > 1 significa que el grupo es una isla */
        rowIndex,
        rowCount: group.rows.length,
        /** Tramo recto dentro de la fila: una L tiene dos, una línea uno */
        segmentIndex,
      }
    }

    // Un grupo de salida por FILA, no por grupo conexo: encimeras, laterales y frontales
    // trabajan sobre tramos ordenados a lo ancho, y una isla o una cruz no tienen un orden
    // lineal único. La topología del grupo entero viaja en cada entrada (groupId,
    // groupShape, rowIndex) para quien necesite tratarlo como una sola pieza.
    const resultGroups = snapGroups.flatMap((group) =>
      group.rows.map((row, rowIndex) => {
        const segmentOf = new Map<AnyID, number>()
        row.segments.forEach((segment, segmentIndex) =>
          segment.items.forEach((id) => segmentOf.set(id, segmentIndex)),
        )
        return row.items
          .map((id) => byId.get(id))
          .filter((item): item is LinkedItem => Boolean(item))
          .map((item, orderIndex) =>
            toEntry(item, orderIndex, group, rowIndex, segmentOf.get(item.id) ?? 0),
          )
      }),
    )

    return {
      groups: resultGroups,
      totalGroups: resultGroups.length,
      totalItemsInGroups: resultGroups.reduce((sum, group) => sum + group.length, 0),
    }
  },
  getContiguousItems: () => {
    const contItems = get().getContItems()

    // Reparto por familia: MAL (+COL) por un lado, MBJ (+COL) por otro; COL puede
    // aparecer en las dos. El orden ya viene calculado por getContItems.
    //
    // Los rinconeros ya NO se sacan a un grupo aparte, y tampoco se excluyen de las
    // familias: son el pivote de una L, y separarlos partía el tramo en trozos sueltos
    // (mueble | rinconero | mueble), así que una L acababa con cuatro frontales en vez
    // de uno por brazo. Con la fila entera, cada brazo sale de sus tramos rectos
    // (segmentIndex, ver getSnapGroups) y el rinconero cae en el suyo.
    const malFamilyGroups: typeof contItems.groups = []
    const mbjFamilyGroups: typeof contItems.groups = []

    contItems.groups.forEach((group) => {
      malFamilyGroups.push(
        ...deriveContItemRuns(
          group,
          (entry) => entry.tipoNormalizado !== 'MBJ',
          () => true,
        ),
      )
      mbjFamilyGroups.push(
        ...deriveContItemRuns(
          group,
          (entry) => entry.tipoNormalizado !== 'MAL',
          () => true,
        ),
      )
    })

    const sortedGroups = [...malFamilyGroups, ...mbjFamilyGroups]

    return {
      groups: sortedGroups,
      totalGroups: sortedGroups.length,
      totalItemsInGroups: sortedGroups.reduce((sum, group) => sum + group.length, 0),
    }
  },
  getContiguousItemsForLaterals: () => {
    const contItems = get().getContItems()
    const items = get().items

    // MAL/COL (y resto no bajos) en un grupo; MBJ solo en el otro. A
    // diferencia de getContiguousItems, KBR no se aísla y COL no se duplica
    // en las dos familias. Quién cubre una junta entre familias lo decide
    // `shouldHostLateral`, no este split.
    const malFamilyGroups: typeof contItems.groups = []
    const mbjFamilyGroups: typeof contItems.groups = []

    contItems.groups.forEach((group) => {
      malFamilyGroups.push(
        ...deriveContItemRuns(group, (entry) => entry.tipoNormalizado !== 'MBJ', () => true),
      )
      mbjFamilyGroups.push(
        ...deriveContItemRuns(group, (entry) => entry.tipoNormalizado === 'MBJ', () => true),
      )
    })

    // Partir por huecos reales: cada cluster independiente (A | B | C) debe
    // recibir sus propios extremos, aunque compartan pared.
    const sortedGroups = [...malFamilyGroups, ...mbjFamilyGroups].flatMap((group) =>
      splitOrderedGroupByRealGap(group, items, LATERAL_GROUP_GAP_MM).map((run) =>
        run.map((entry, orderIndex) => ({ ...entry, orderIndex })),
      ),
    )

    return {
      groups: sortedGroups,
      totalGroups: sortedGroups.length,
      totalItemsInGroups: sortedGroups.reduce((sum, group) => sum + group.length, 0),
    }
  },
  getContItemsForEncimeras: () => {
    const items = get().items
    const contItems = get().getContItems()

    // Encimeras: MAL nunca participa, y solo se consideran contiguos tramos
    // de tipoNormalizado idéntico (MBJ con MBJ, COL con COL) — nunca se
    // fusiona MBJ con COL aunque se toquen, porque cambian alto/fondo.
    const sortedGroups: typeof contItems.groups = []
    contItems.groups.forEach((group) => {
      sortedGroups.push(
        ...deriveContItemRuns(
          group,
          (entry) => entry.tipoNormalizado !== 'MAL',
          (prev, curr) => prev.tipoNormalizado === curr.tipoNormalizado,
        ),
      )
    })

    // El ancho de encimeras respeta el morph "Ancho" (valor por defecto), no
    // el width bruto (importante para regletas con width = máximo).
    const groupsWithEncimeraWidth = sortedGroups.map((group) =>
      group.map((entry) => {
        const item = items.get(entry.id) as LinkedItem | undefined
        const width = item?.morphs?.find((morph) => morph.name === 'Ancho')?._default ?? entry.width
        return { ...entry, width }
      }),
    )

    return {
      groups: groupsWithEncimeraWidth,
      totalGroups: groupsWithEncimeraWidth.length,
      totalItemsInGroups: groupsWithEncimeraWidth.reduce((sum, group) => sum + group.length, 0),
    }
  },
  deleteFrontals: () => {
    const itemsToDelete: AnyID[] = []
    get().items.forEach((item, id) => {
      if ((isItem(item) || isLinkedItem(item)) && item.catalogID === 'Frontal') {
        itemsToDelete.push(id)
      }
    })

    itemsToDelete.forEach((id) => {
      set((store) => {
        store.items.delete(id)
        store.layers[store.currentLayer].items.delete(id)
        store.layers[store.currentLayer].selected.delete(id)
        store.sceneBoundingBoxes.delete(id)
        store.collisionableItems.delete(id)
      })
    })

    // No apagar frontalActive a mitad de un redibujado: delete + create es un solo ciclo.
    if (itemsToDelete.length > 0 && !get().isDrawingFrontals) {
      const remainingFrontals = Array.from(get().items.values()).filter(
        (item) => (isItem(item) || isLinkedItem(item)) && isFrontalCatalog(item.catalogID),
      )
      if (remainingFrontals.length === 0) {
        set((store) => {
          store.frontalActive = false
        })
      }
    }

    return itemsToDelete.length
  },
  drawFrontalsFromSnappedGroups: async (
    height: number,
    options?: { force?: boolean; unitId?: string },
  ) => {
    const autoEnabled = options?.force ? true : (get().frontalsAutoEnabled ?? true)
    if (!autoEnabled) {
      return
    }
    const isDrawingFrontals = get().isDrawingFrontals
    if (isDrawingFrontals) {
      return
    }

    set((store) => {
      store.isDrawingFrontals = true
    })

    const originalCurrentObjectID = get().currentObjectID
    const originalSelected = new Set(get().layers[get().currentLayer].selected)
    const frontalFinishSnapshots = snapshotFrontalFinishes(get().items)
    const onlyUnitId = options?.unitId

    try {
      set((store) => {
        reconcileSnappedSides(store.items, getSnapCandidates(store.items.values()))
        rebuildSnappedSides(store.items, getSnapCandidates(store.items.values()))
      })

      if (onlyUnitId) {
        const hostPreview = get().getContiguousItems()
        const unitGroup = hostPreview.groups.find((group) =>
          group.some((item) => item.id === onlyUnitId),
        )
        const hosts = (unitGroup ?? [])
          .map((entry) => get().items.get(entry.id))
          .filter(
            (item): item is LinkedItem =>
              Boolean(item) && isLinkedItem(item) && item.tipoNormalizado === 'MBJ',
          )
        const idsToDelete: AnyID[] = []
        get().items.forEach((item, id) => {
          if (!(isItem(item) || isLinkedItem(item)) || !isFrontalCatalog(item.catalogID)) return
          if (frontalBelongsToHosts(item as LinkedItem, hosts)) idsToDelete.push(id)
        })
        idsToDelete.forEach((id) => {
          set((store) => {
            store.items.delete(id)
            store.layers[store.currentLayer].items.delete(id)
            store.layers[store.currentLayer].selected.delete(id)
            store.sceneBoundingBoxes.delete(id)
            store.collisionableItems.delete(id)
          })
        })
      } else {
        get().deleteFrontals()
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      const unitsWithSuffixes = mapUnitsWithSuffixes(get().items)

      const contiguousResult = get().getContiguousItems()
      const items = get().items as Map<AnyID, LinkedItem>

      const applyFrontalPlacementY = (frontalId: AnyID, newY: number) => {
        set((store) => {
          const f = store.items.get(frontalId)
          if (!f || !('matrix' in f) || !f.matrix) return
          ;(f as LinkedItem).y = newY
          const p = new Vector3()
          const q = new Quaternion()
          const s = new Vector3()
          f.matrix.decompose(p, q, s)
          f.matrix.setPosition(p.x, newY, p.z)
        })
      }

      /** Un frontal por racha. Misma altura en todo el tramo → una sola llamada (caso actual). */
      const drawLinearFrontalForRun = async (
        run: { id: AnyID; width: number }[],
        extraWidthMm = 0,
        matrixOverride?: Matrix4,
        options?: { widthFromHosts?: boolean; snappedWall?: AnyID },
      ) => {
        if (run.length === 0) return
        const liveItems = get().items as Map<AnyID, LinkedItem>
        const anchorItem = liveItems.get(getFrontalRunAnchor(liveItems, run).id) as
          | LinkedItem
          | undefined
        if (!anchorItem) return

        const runWidth =
          options?.widthFromHosts === false
            ? extraWidthMm
            : run.reduce((total, entry) => {
                const item = liveItems.get(entry.id) as LinkedItem | undefined
                return total + getFrontalHostWidthMm(item, entry.width)
              }, extraWidthMm)
        if (runWidth <= 0) return

        const frontalY = getRunFrontalWorktopYMm(liveItems, run, 0)
        const startPose = getFrontalRunStartPose(liveItems, run)
        const frontalMatrix = matrixOverride
          ? setFrontalMatrixWorktopY(matrixOverride.clone(), frontalY)
          : startPose
            ? composeFrontalWorldMatrix(startPose, frontalY)
            : setFrontalMatrixWorktopY(anchorItem.matrix.clone(), frontalY)


        const drawnFrontalId = await get().drawItem(
          getId('linkedItem'),
          'Frontal',
          '',
          true,
          frontalMatrix,
          'items',
          undefined,
          false,
        )
        if (!get().items.get(drawnFrontalId)) return

        get().editMorph(drawnFrontalId, 'Ancho', runWidth)
        get().editMorph(drawnFrontalId, 'Alto', height)
        get().editItem(drawnFrontalId, { width: runWidth, height })
        applyFrontalPlacementY(drawnFrontalId, frontalY)
        set((store) => {
          const frontal = store.items.get(drawnFrontalId)
          if (frontal && 'snappedWall' in frontal) {
            frontal.snappedWall = options?.snappedWall ?? anchorItem.snappedWall
          }
          store.collisionableItems.delete(drawnFrontalId)
          if (frontal && isLinkedItem(frontal)) {
            const restored = applyClosestFrontalFinish(frontal, frontalFinishSnapshots)
            if (!restored) {
              const hostEncimera = findEncimeraSonOnUnit(liveItems, anchorItem)
              const encTex =
                hostEncimera && 'textures' in hostEncimera
                  ? hostEncimera.textures?.find(
                      (t) => t.zone === 'Encimera' || t.zoneDisplayName === 'Encimera',
                    ) ?? hostEncimera.textures?.[0]
                  : undefined
              const catalogTex = resolveFroFrontalTextureFromEncimera(
                encTex,
                store.frontalesTexturesOptions as any[],
              )
              if (catalogTex) applyCatalogOptionToFrontal(frontal, catalogTex)
            }
          }
        })
        get().setOBB(drawnFrontalId)
      }

      for (const group of contiguousResult.groups) {
        if (onlyUnitId && !group.some((item) => item.id === onlyUnitId)) {
          continue
        }
        const hasMBJ = group.some((item) => {
          const linkedItem = items.get(item.id) as LinkedItem
          return linkedItem && linkedItem.tipoNormalizado === 'MBJ'
        })

        if (!hasMBJ) {
          continue
        }

        const sceneItems = items as Map<AnyID, SceneObjects>

        // Rinconeros de la fila. Puede haber más de uno (una U tiene dos esquinas), así
        // que no vale quedarse con el primero: cada uno aporta su propio giro en L.
        const corners = group.flatMap((entry) => {
          const item = items.get(entry.id)
          const hand = getRinconeroHandedness(entry.catalogID)
          if (!item || !isLinkedItem(item) || !hand) return []
          const position = new Vector3()
          const quaternion = new Quaternion()
          item.matrix.decompose(position, quaternion, new Vector3())
          const layoutWidth = getFrontalHostWidthMm(item, entry.width)
          // Esquina del giro: extremo del ancho por el que el retorno arranca, en la pared.
          const corner =
            hand === 'right'
              ? position
                  .clone()
                  .add(new Vector3(1, 0, 0).applyQuaternion(quaternion).multiplyScalar(layoutWidth))
              : position.clone()
          return [
            {
              entry,
              item,
              hand,
              position,
              quaternion,
              layoutWidth,
              corner,
              returnLengthMm: getRinconeroReturnFrontalLengthMm(item, items),
              returnWall: getRinconeroPerpendicularWall(get, item) ?? undefined,
              worktopY: getUnitFrontalWorktopYMm(items, item),
            },
          ]
        })
        type CornerInfo = (typeof corners)[number]

        if (corners.length === 0) {
          // Tramo lineal: una racha si todas las unidades tienen la misma altura;
          // si no, un frontal por cada tramo consecutivo de la misma altura.
          // Hueco real (unidad separada) → frontal propio, aunque snappedSides
          // siga apuntando al vecino.
          for (const chunk of splitFrontalArmByRealGap(sceneItems, group)) {
            const runs = getFrontalRunsForWallGroup(items, chunk)
            for (const run of runs) {
              await drawLinearFrontalForRun(run)
            }
          }
          continue
        }


        /** Frontal del retorno que nace en la esquina del rinconero, mirando al giro. */
        const buildCornerArmMatrix = (
          corner: CornerInfo,
          runWidthNoExtra: number,
          extraMm: number,
        ) => {
          const scale = new Vector3()
          corner.item.matrix.decompose(new Vector3(), new Quaternion(), scale)
          const turn = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            corner.hand === 'right' ? -Math.PI / 2 : Math.PI / 2,
          )
          const quaternion = corner.quaternion.clone().multiply(turn)
          const position = corner.corner.clone()
          if (corner.hand === 'left') {
            // El izquierdo corre hacia la esquina: el origen queda al otro extremo.
            const xAxis = new Vector3(1, 0, 0).applyQuaternion(quaternion)
            position.add(xAxis.multiplyScalar(-(extraMm + runWidthNoExtra)))
          }
          return new Matrix4().compose(position, quaternion, scale)
        }

        /** Rinconeros cuyo retorno ya lleva frontal (fundido con un tramo o solo). */
        const returnDrawn = new Set<AnyID>()

        const drawReturnOnly = async (corner: CornerInfo) => {
          returnDrawn.add(corner.item.id)
          await drawLinearFrontalForRun(
            [{ id: corner.item.id, width: 0 }],
            corner.returnLengthMm,
            buildCornerArmMatrix(corner, 0, corner.returnLengthMm),
            { widthFromHosts: false, snappedWall: corner.returnWall },
          )
        }

        /**
         * Un tramo recto del brazo, fundido con los giros de los rinconeros de OTROS
         * tramos que le lleguen por cada extremo: el frontal nace en la esquina del
         * rinconero del arranque y se alarga hasta la esquina del rinconero del final.
         * Un rinconero a distinta altura de encimera no se funde: lleva su retorno aparte.
         */
        const drawRunWithCorners = async (
          run: { id: AnyID; width: number }[],
          foreignCorners: CornerInfo[],
          /** Pared del retorno del brazo, para los hosts que aún no han heredado la suya */
          armFallbackWall?: AnyID,
        ) => {
          const attached = foreignCorners.filter(
            (corner) =>
              !returnDrawn.has(corner.item.id) &&
              isFrontalRunAttachedToRinconero(items, corner.item, run),
          )
          const startPose = getFrontalRunStartPose(items, run)
          const firstHost = items.get(run[0]?.id) as LinkedItem | undefined
          const hostWall = (firstHost?.snappedWall || undefined) as AnyID | undefined
          if (attached.length === 0 || !startPose) {
            await drawLinearFrontalForRun(run, 0, undefined, {
              snappedWall: hostWall ?? armFallbackWall,
            })
            return
          }

          const hostsWidth = sumFrontalRunWidthMm(items, run)
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(startPose.quaternion)
          const startPoint = startPose.position.clone()
          const hosts = run
            .map((entry) => items.get(entry.id) as LinkedItem | undefined)
            .filter((unit): unit is LinkedItem => Boolean(unit) && !isFrontalRegletaHost(unit))
          const alongOf = (unit: LinkedItem) => {
            const position = new Vector3()
            unit.matrix.decompose(position, new Quaternion(), new Vector3())
            return new Vector3().subVectors(position, startPoint).dot(xAxis)
          }
          // Largo real del tramo (incluye los huecos pequeños entre hosts que el chunk ya
          // dio por pegados): de esquina a esquina el frontal no puede quedarse corto.
          const runWidth = hosts.reduce(
            (max, unit) => Math.max(max, alongOf(unit) + getFrontalHostWidthMm(unit, unit.width)),
            hostsWidth,
          )
          const endPoint = startPoint.clone().add(xAxis.clone().multiplyScalar(runWidth))
          const distanceXZ = (a: Vector3, b: Vector3) => Math.hypot(a.x - b.x, a.z - b.z)

          // Cada rinconero enganchado cae al extremo del tramo que tenga más cerca.
          let startCorner: CornerInfo | undefined
          let endCorner: CornerInfo | undefined
          const leftovers: CornerInfo[] = []
          for (const corner of attached) {
            const toStart = distanceXZ(corner.corner, startPoint)
            const toEnd = distanceXZ(corner.corner, endPoint)
            if (toStart <= toEnd) {
              if (!startCorner || toStart < distanceXZ(startCorner.corner, startPoint)) {
                if (startCorner) leftovers.push(startCorner)
                startCorner = corner
              } else leftovers.push(corner)
            } else if (!endCorner || toEnd < distanceXZ(endCorner.corner, endPoint)) {
              if (endCorner) leftovers.push(endCorner)
              endCorner = corner
            } else leftovers.push(corner)
          }

          const runY = getRunFrontalWorktopYMm(items, run, startCorner?.worktopY ?? 0)
          const sameWorktop = (corner: CornerInfo) =>
            Math.abs(corner.worktopY - runY) <= FRONTAL_HEIGHT_RUN_EPS_MM
          if (startCorner && !sameWorktop(startCorner)) {
            leftovers.push(startCorner)
            startCorner = undefined
          }
          if (endCorner && !sameWorktop(endCorner)) {
            leftovers.push(endCorner)
            endCorner = undefined
          }
          for (const corner of leftovers) await drawReturnOnly(corner)

          // Hosts de cada extremo, por posición a lo largo del tramo (no por orden de lista).
          const startHost = hosts.reduce<LinkedItem | undefined>(
            (best, unit) => (!best || alongOf(unit) < alongOf(best) ? unit : best),
            undefined,
          )
          const endHost = hosts.reduce<LinkedItem | undefined>(
            (best, unit) => (!best || alongOf(unit) > alongOf(best) ? unit : best),
            undefined,
          )
          const extraStart = startCorner
            ? getRinconeroReturnExtraToHostMm(startCorner.item, startHost, startCorner.returnLengthMm)
            : 0
          const extraEnd = endCorner
            ? getRinconeroReturnExtraToHostMm(endCorner.item, endHost, endCorner.returnLengthMm)
            : 0

          // Con esquina al arranque el frontal nace en ella con la orientación del tramo;
          // si solo hay esquina al final, nace en el tramo y se alarga hasta ella.
          const matrixOverride = startCorner
            ? new Matrix4().compose(
                startCorner.corner.clone(),
                startPose.quaternion.clone(),
                new Vector3(1, 1, 1),
              )
            : undefined
          const wall = startCorner?.returnWall ?? endCorner?.returnWall


          if (!startCorner && !endCorner) {
            await drawLinearFrontalForRun(run, 0, undefined, {
              snappedWall: hostWall ?? wall ?? armFallbackWall,
            })
            return
          }
          if (startCorner) returnDrawn.add(startCorner.item.id)
          if (endCorner) returnDrawn.add(endCorner.item.id)
          // drawLinearFrontalForRun suma los anchos de los hosts: el extra lleva los giros
          // y la diferencia hasta el largo real del tramo.
          await drawLinearFrontalForRun(
            run,
            extraStart + extraEnd + (runWidth - hostsWidth),
            matrixOverride,
            { snappedWall: wall },
          )
        }

        // Cada tramo recto de la fila (segmentIndex) es un brazo. Si algún rinconero de
        // otro tramo engancha en él por el giro, es un retorno: cuentan los MBJ aunque no
        // hayan heredado pared todavía (y puede ser a la vez el brazo de puerta de su
        // propio rinconero: en una cocina cerrada en anillo lo son todos). Si no, es una
        // fila normal de hosts de pared.
        const segmentIndexes = [...new Set(group.map((entry) => entry.segmentIndex))].sort(
          (a, b) => a - b,
        )
        for (const segmentIndex of segmentIndexes) {
          const armItems = group.filter((entry) => entry.segmentIndex === segmentIndex)
          const ownCorners = corners.filter((corner) => corner.entry.segmentIndex === segmentIndex)
          const foreignCorners = corners.filter(
            (corner) => corner.entry.segmentIndex !== segmentIndex,
          )
          const returnOf = foreignCorners.filter((corner) =>
            isFrontalRunAttachedToRinconero(items, corner.item, armItems),
          )
          const isReturnArm = returnOf.length > 0
          const chunks = splitFrontalArmByRealGap(sceneItems, armItems)

          const drawnIds = new Set<AnyID>()
          for (const chunk of chunks) {
            const runs = isReturnArm
              ? getFrontalRunsForRinconeroReturnArm(items, chunk)
              : getFrontalRunsForWallGroup(items, chunk)
            for (const run of runs) {
              run.forEach((entry) => drawnIds.add(entry.id))
              await drawRunWithCorners(run, foreignCorners, returnOf[0]?.returnWall)
            }
          }
          // Un rinconero sin run en su brazo (sin hosts de pared alrededor) lleva el suyo.
          for (const corner of ownCorners) {
            if (drawnIds.has(corner.item.id)) continue
            await drawLinearFrontalForRun([{ id: corner.item.id, width: corner.layoutWidth }])
          }
        }

        // Retornos que no se han fundido con ningún tramo: frontal solo del giro.
        for (const corner of corners) {
          if (!returnDrawn.has(corner.item.id)) await drawReturnOnly(corner)
        }
      }

      const hasDrawnFrontals = Array.from(get().items.values()).some(
        (item) => (isItem(item) || isLinkedItem(item)) && isFrontalCatalog(item.catalogID),
      )
      set((store) => {
        store.frontalActive = hasDrawnFrontals
      })

      // Restaurar sufijos (si es necesario) después de procesar todos los grupos
      if (!onlyUnitId && unitsWithSuffixes.size > 0) {
        setTimeout(async () => {
          for (const [unitId, suffix] of unitsWithSuffixes) {
            const unit = get().items.get(unitId)
            if (unit && unit.type === 'linkedItem') {
              const currentCatalogID = unit.catalogID.toString()
              const baseCatalogID = getBaseCatalogID(currentCatalogID)
              const expectedCatalogID = buildCatalogIDWithSuffix(baseCatalogID, suffix)

              if (currentCatalogID !== expectedCatalogID) {
                try {
                  set((s) => {
                    const newItems = new Map(s.items)
                    const item = { ...newItems.get(unitId) }

                    if (item && item.type === 'linkedItem') {
                      item.catalogID = expectedCatalogID

                      if (item.glbPath) {
                        item.glbPath = updateGlbPathWithSuffix(item.glbPath, suffix)
                      }

                      if (item.referencia) {
                        item.referencia = updateReferenciaWithSuffix(item.referencia, suffix)
                      }

                      newItems.set(unitId, item)
                    }

                    return {
                      items: newItems,
                    }
                  })
                } catch (error) {
                  console.error(`Error restaurando sufijo para unidad ${unitId}:`, error)
                }
              }
            }
          }
        }, 1000)
      }
    } finally {
      set((store) => {
        store.isDrawingFrontals = false
        const layer = store.layers[store.currentLayer]
        layer.selected.clear()
        originalSelected.forEach((id) => {
          const kept = store.items.get(id)
          if (!kept || !('catalogID' in kept)) return
          if (isFrontalCatalog(kept.catalogID)) return
          layer.selected.add(id)
        })
        const original = store.items.get(originalCurrentObjectID)
        const originalIsFrontal =
          original && 'catalogID' in original && isFrontalCatalog(original.catalogID)
        if (original && !originalIsFrontal) {
          store.currentObjectID = originalCurrentObjectID
          if (layer.selected.size === 0) layer.selected.add(originalCurrentObjectID)
        } else if (layer.selected.size > 0) {
          store.currentObjectID = layer.selected.values().next().value as AnyID
        }
      })
    }
  },
  /**
   * Tras cambiar Alto/Fondo/Ancho de un mueble: reescala y reposiciona los laterales
   * que ya están pegados a sus extremos (sin borrarlos ni pedir otro catálogo).
   * Usa siempre furniture.width / height / thickness vivos (free-dim).
   */
  syncLateralsAfterFurnitureDimensionChange: (furnitureId: AnyID) => {
    const furniture = get().items.get(furnitureId)
    if (!furniture || !isLinkedItem(furniture)) return 0
    if (isFreeDimensionLateralOrCostadoCatalog(furniture)) return 0

    const tipo = String(furniture.tipoNormalizado ?? '').toUpperCase()
    if (tipo !== 'MBJ' && tipo !== 'COL' && tipo !== 'MAL') return 0

    const furniturePos = new Vector3()
    const furnitureQuat = new Quaternion()
    furniture.matrix.decompose(furniturePos, furnitureQuat, new Vector3())
    const xAxis = new Vector3(1, 0, 0).applyQuaternion(furnitureQuat)
    // Ancho vivo del LinkedItem (no morph.default de catálogo).
    const width = furniture.width || 0
    const leftAnchor = furniturePos.clone().addScaledVector(xAxis, -19)
    const rightAnchor = furniturePos.clone().addScaledVector(xAxis, width)
    const heightOffset = 0
    const hostId = String(furnitureId)

    let updated = 0
    for (const [id, item] of get().items) {
      if (!isLinkedItem(item) || !item.visible) continue
      if (!isFreeDimensionLateralOrCostadoCatalog(item)) continue

      const lateralPos = new Vector3()
      item.matrix.decompose(lateralPos, new Quaternion(), new Vector3())
      if (Math.abs(lateralPos.y - furniturePos.y) > 400) continue

      // Preferir laterales anclados a este mueble al colocarlos. Evita que un sync
      // global robe laterales de otros grupos en la misma pared.
      // Fallback geométrico (proyectos antiguos): solo extremos actuales ±120 mm.
      const hostedHere = String(item.lateralHostFurnitureId ?? '') === hostId
      const dLeft = lateralPos.distanceTo(leftAnchor)
      const dRight = lateralPos.distanceTo(rightAnchor)
      if (!hostedHere && dLeft > 120 && dRight > 120) continue

      const isLeft = dLeft <= dRight
      applyLateralSceneDimensionsToItem(get, set, id, furniture)

      // MAL: heightOffset 0 — el lateral debe compartir el mismo Y base que el mueble
      // (antes +20 dejaba el LA sobresaliendo por arriba).
      // KBR/KARB/KALB: la matriz la pone calculateKBR/KARBLateralMatrix; getRelativeMatrix
      // los saca del brazo de regleta (“bien y luego mal”).
      const catalogUpper = String(furniture.catalogID ?? '').toUpperCase()
      const isRinconeroFurniture =
        /KBR|KARB|KALB/.test(catalogUpper) ||
        ['ARP', 'ALP', 'BRP', 'BLP', 'ARB', 'ALB', 'BRB', 'BLB'].some((p) =>
          catalogUpper.includes(p),
        )
      if (!isRinconeroFurniture) {
        const freshFurniture =
          (get().items.get(furnitureId) as LinkedItem | undefined) ?? furniture
        const liveWidth = freshFurniture.width || width
        const lateralMatrix = getRelativeMatrix(
          freshFurniture.matrix,
          isLeft ? -19 : liveWidth,
          freshFurniture,
          heightOffset,
        )
        const pos = new Vector3()
        lateralMatrix.decompose(pos, new Quaternion(), new Vector3())
        set((store) => {
          const lateral = store.items.get(id)
          if (!lateral || !isLinkedItem(lateral)) return
          lateral.matrix = lateralMatrix.clone()
          lateral.x = pos.x
          lateral.y = pos.y
          lateral.z = pos.z
          if (!lateral.lateralHostFurnitureId) {
            lateral.lateralHostFurnitureId = furnitureId
          }
        })
        get().setOBB(id)
      }

      updated++
    }
    return updated
  },

  deleteLaterales: () => {
    const itemsToDelete: AnyID[] = []
    const lateralIds = new Set<AnyID>()

    get().items.forEach((item, id) => {
      if ((isItem(item) || isLinkedItem(item)) && item.catalogID) {
        const catalogID = item.catalogID.toString()
        if (
          catalogID.startsWith('LA') ||
          catalogID.startsWith('LB') ||
          catalogID.startsWith('LC') ||
          catalogID.startsWith('CC') ||
          catalogID.startsWith('CB')
        ) {
          itemsToDelete.push(id)
          lateralIds.add(id)
        }
      }
    })

    if (lateralIds.size > 0) {
      get().items.forEach((item, id) => {
        if (!item || (item as any).type !== 'linkedSon') return
        const firstParentID = (item as any).firstParentID as AnyID | undefined
        if (!firstParentID) return
        if (!lateralIds.has(firstParentID)) return

        itemsToDelete.push(id)
      })
    }

    if (itemsToDelete.length > 0) {
      set((store) => {
        for (const id of itemsToDelete) {
          releaseSnappedSides(store.items, id)
          store.items.forEach((wallCandidate) => {
            if (isWall(wallCandidate)) {
              wallCandidate.lowItems?.delete(id)
              wallCandidate.highItems?.delete(id)
            } else if (isPartition(wallCandidate)) {
              wallCandidate.lowItems?.delete(id)
            } else if (isHiddenWall(wallCandidate)) {
              wallCandidate.lowItems?.delete(id)
            }
          })
          store.items.delete(id)
          store.layers[store.currentLayer].items.delete(id)
          store.layers[store.currentLayer].selected.delete(id)
          store.sceneBoundingBoxes.delete(id)
          store.collisionableItems.delete(id)
        }
      })
    }

    return itemsToDelete.length
  },
  updateFrontalsForUnit: (unitId: string) => {
    const items = get().items
    const unit = items.get(unitId)

    if (!unit) return
    if ((isItem(unit) || isLinkedItem(unit)) && isFrontalCatalog(unit.catalogID)) return
    if (get().isDrawingFrontals) return

    const hasFrontals = Array.from(items.values()).some(
      (item) => (isItem(item) || isLinkedItem(item)) && isFrontalCatalog(item.catalogID),
    )
    if (!hasFrontals) return

    const height = Number(get().getFrontalHeight()) || 50
    void get().drawFrontalsFromSnappedGroups(height, { force: true, unitId })
  },
  drawLateralesForGroups: async (options?: { force?: boolean }) => {
    const autoEnabled = options?.force ? true : (get().lateralsAutoEnabled ?? true)
    if (!autoEnabled) {
      return
    }

    const executeDraw = async () => {
      const originalCurrentObjectID = get().currentObjectID
      const originalSelected = new Set(get().layers[get().currentLayer].selected)

      try {
        get().deleteLaterales()

        // El registro de caras es lo único que decide dónde va un lateral, y llega aquí
        // con lo que escribió el arrastre: caras etiquetadas por convenio en vez de por
        // posición, y giros de rinconero que nunca se registraron porque no tocan cara con
        // cara. Se cuadra con la geometría ANTES de agrupar; si no, la fila se parte en la
        // esquina y los extremos equivocados se llevan lateral.
        set((store) => {
          const reconciled = reconcileSnappedSides(
            store.items,
            getSnapCandidates(store.items.values()),
          )
          // Y las caras que directamente no llegó a escribir nadie (item movido después de
          // pegarse, ensanchado hasta tocar al vecino, proyecto viejo…).
          const filled = rebuildSnappedSides(store.items, getSnapCandidates(store.items.values()))
          if (reconciled.relabeled.length || reconciled.linked.length || filled) {
            // console.log(
            //   `🪵 [laterales] caras cuadradas con la geometría: ${reconciled.relabeled.length} recolocada(s), ${reconciled.linked.length} giro(s) de rinconero, ${filled} contacto(s) nuevo(s)`,
            //   reconciled,
            // )
          }
        })

        const { groups } = get().getContiguousItemsForLaterals()
        const items = get().items
        const placedLateralSides = new Set<string>()
        /** Laterales dibujados en esta pasada → re-aplicar dims al final (LinkedEditor). */
        const placedLateralsThisRun: Array<{ lateralId: AnyID; furnitureId: AnyID }> = []

        const lateralSideKey = (itemId: string, isLeftSide: boolean) =>
          `${itemId}:${isLeftSide ? 'left' : 'right'}`

        const claimLateralSide = (itemId: string, isLeftSide: boolean): boolean => {
          const key = lateralSideKey(itemId, isLeftSide)
          if (placedLateralSides.has(key)) return false
          placedLateralSides.add(key)
          return true
        }

        // Extremos a lo ancho de cada mueble: por dónde sigue la fila. Es lo único que
        // decide si un lado es principio o final de grupo, y por tanto si lleva lateral.
        const runSlots = new Map<AnyID, { left?: AnyID; right?: AnyID }>()
        for (const snapGroup of getSnapGroups(getSnapCandidates(items.values()))) {
          for (const [id, slots] of snapGroup.runSlots) runSlots.set(id, slots)
        }

        // ─────────── Diagnóstico (poner a false para silenciar) ───────────
        // Vuelca: cómo ve la escena (extremos de fila + caras registradas), qué grupos monta,
        // qué decide en cada lado de cada mueble y qué laterales acaba dibujando.
        const DEBUG_LATERALES = false
        const dbgLabel = (id?: AnyID | string | null): string => {
          if (!id) return '—'
          const target = items.get(id as AnyID) ?? get().items.get(id as AnyID)
          if (!target || !isLinkedItem(target)) return `¿?(${String(id).slice(-6)})`
          return `${target.catalogID}#${String(id).slice(-6)}`
        }
        /** Motivo por el que un lado concreto se quedó sin lateral, rellenado por processLateral*. */
        const dbgSkipReasons = new Map<string, string>()
        const dbgSkip = (itemId: AnyID | string, isLeftSide: boolean, reason: string) => {
          if (DEBUG_LATERALES) dbgSkipReasons.set(lateralSideKey(String(itemId), isLeftSide), reason)
        }
        const dbgDecisions: Array<Record<string, unknown>> = []
        const dbgLateralSide = new Map<AnyID, 'left' | 'right'>()

        // Punto medio del extremo a lo ancho, con la MISMA referencia que usa el dibujo del
        // lateral: origen del item = borde izquierdo, +X local = a lo ancho.
        const dbgEdgePoint = (it: LinkedItem, isLeftSide: boolean): Vector3 => {
          const pos = new Vector3()
          const quat = new Quaternion()
          it.matrix.decompose(pos, quat, new Vector3())
          if (isLeftSide) return pos
          return pos.add(new Vector3(1, 0, 0).applyQuaternion(quat).multiplyScalar(it.width || 0))
        }
        // Qué hay pegado DE VERDAD a ese extremo, medido a pelo. Contrastar con runSlots:
        // si aquí sale mueble y en runSlots no, falta el registro de caras y el lateral
        // acaba dibujado en mitad del tramo.
        const dbgGeoNeighbor = (
          it: LinkedItem,
          isLeftSide: boolean,
          pool: LinkedItem[],
        ): { id: AnyID; dist: number } | null => {
          const edge = dbgEdgePoint(it, isLeftSide)
          let best: { id: AnyID; dist: number } | null = null
          for (const other of pool) {
            if (other.id === it.id) continue
            const otherEdge = dbgEdgePoint(other, !isLeftSide)
            const dist = Math.hypot(edge.x - otherEdge.x, edge.z - otherEdge.z)
            if (dist < 60 && (!best || dist < best.dist)) best = { id: other.id, dist }
          }
          return best
        }

        if (DEBUG_LATERALES) {
          const scene = [...items.values()].filter(
            (it): it is LinkedItem => isLinkedItem(it) && it.visible && it.catalogID !== 'Frontal',
          )
          const groupOf = new Map<string, number>()
          groups.forEach((group, i) => group.forEach((e) => groupOf.set(String(e.id), i)))

          console.log(
            `🪵 [laterales] escena: ${scene.length} mueble(s) → ${groups.length} grupo(s) para laterales`,
          )
          groups.forEach((group, i) => {
            const head = group[0]
            console.log(
              `🪵 [laterales] grupo ${i}: ${group.length} item(s) · groupId=${head?.groupId} · shape=${head?.groupShape} · fila ${head?.rowIndex}/${head?.rowCount} · [${group.map((e) => dbgLabel(e.id)).join(' | ')}]`,
            )
          })

          console.log(
            '🪵 [laterales] extremos por mueble — run.* = lo que cree el código, geo.* = lo que hay pegado de verdad:',
          )
          console.table(
            scene.map((it) => {
              const slots = runSlots.get(it.id) ?? {}
              const sides = (it.snappedSides ?? {}) as Record<string, AnyID | undefined>
              const geoLeft = dbgGeoNeighbor(it, true, scene)
              const geoRight = dbgGeoNeighbor(it, false, scene)
              const grupo = groupOf.get(String(it.id))
              const avisos: string[] = []
              // Vecino real que el código no ve → lateral en mitad del tramo.
              if (geoLeft && !slots.left) avisos.push('left: pegado pero run vacío')
              if (geoRight && !slots.right) avisos.push('right: pegado pero run vacío')
              // Vecino de otro grupo: extremo de subgrupo. Si es familia cubriente, cede.
              if (slots.left && groupOf.get(String(slots.left)) !== grupo)
                avisos.push('left: vecino en otro grupo')
              if (slots.right && groupOf.get(String(slots.right)) !== grupo)
                avisos.push('right: vecino en otro grupo')
              return {
                item: dbgLabel(it.id),
                grupo: grupo ?? '—',
                tipo: it.tipoNormalizado,
                'run.left': dbgLabel(slots.left),
                'geo.left': geoLeft ? `${dbgLabel(geoLeft.id)} (${Math.round(geoLeft.dist)}mm)` : '—',
                'run.right': dbgLabel(slots.right),
                'geo.right': geoRight
                  ? `${dbgLabel(geoRight.id)} (${Math.round(geoRight.dist)}mm)`
                  : '—',
                'snap.left': dbgLabel(sides.left),
                'snap.right': dbgLabel(sides.right),
                'snap.front': dbgLabel(sides.front),
                'snap.back': dbgLabel(sides.back),
                ancho: it.width,
                x: Math.round(it.x),
                z: Math.round(it.z),
                rot: Math.round(get().getInitialAngleDegrees(it.id)),
                regleta: it.regleta ?? 0,
                pared: it.snappedWall ? String(it.snappedWall).slice(-6) : '—',
                '⚠': avisos.join(' · ') || '',
              }
            }),
          )
        }

        // Extremo de fila + responsabilidad (shouldHostLateral): si una familia
        // cubriente (COL) comparte la cara, el costado es suyo; el resto cede.
        // snappedSides.left/right a secas no vale (rinconero encadena por frontal).
        const itemShouldHostLateral = (
          item: LinkedItem,
          isLeftSide: boolean,
          groupIds?: Set<string>,
        ) => shouldHostLateral({ item, isLeftSide, groupIds, runSlots, items })


    // Listar los items COL y MBJ en la escena
    const colItems: Array<{ id: string; catalogID: string; height: number }> = []
    const mbjItems: Array<{ id: string; catalogID: string; height: number }> = []
    for (const [id, item] of items) {
      if (!isLinkedItem(item)) continue
      if (!item.visible) continue
      if (item.catalogID === 'Frontal') continue
      if (item.tipoNormalizado === 'COL') {
        colItems.push({
          id,
          catalogID: item.catalogID?.toString() || 'unknown',
          height: item.height || 0,
        })
      } else if (item.tipoNormalizado === 'MBJ') {
        mbjItems.push({
          id,
          catalogID: item.catalogID?.toString() || 'unknown',
          height: item.height || 0,
        })
      }
    }

    // Función helper para obtener el valor REAL (invertido) de regleta para cálculos geométricos
    const getRegletaRealValue = (item: LinkedItem): number => {
      if (!item.regleta) return 0
      const regletaMorph = item.morphs?.find((m) => m.name === 'Regleta')
      if (!regletaMorph) return item.regleta
      // El valor está invertido: si regleta = 20, el valor real es 120; si regleta = 120, el valor real es 20
      const storedValue = item.regleta
      if (storedValue >= regletaMorph.max) return regletaMorph.min
      if (storedValue <= regletaMorph.min) return regletaMorph.max
      return regletaMorph.max + regletaMorph.min - storedValue
    }

    // Función helper para obtener el valor invertido de regleta (igual que en collideItems)
    const getRegletaInvertedValue = (value: number, morph: Morphs | undefined): number => {
      if (!morph) return value
      if (value >= morph.max) return morph.min
      if (value <= morph.min) return morph.max
      return morph.max + morph.min - value
    }

    // true = pegado por left (isFirstItem), false = por right, null = no pegado a regleta.
    // Cubre KBR (regleta invertida) y KARB (regleta directa); no registra snappedSides.
    const getLadoPegadoARegleta = (item: LinkedItem): boolean | null => {
      const itemPos = new Vector3()
      item.matrix.decompose(itemPos, new Quaternion(), new Vector3())

      for (const [id, otherItem] of items) {
        if (id === item.id) continue
        if (!isLinkedItem(otherItem)) continue
        if (otherItem.catalogID === 'Frontal') continue
        if (!otherItem.visible) continue

        const otherIsKBR = otherItem.catalogID?.toString().toUpperCase().includes('KBR')
        const otherIsKAR = otherItem.catalogID?.toString().toUpperCase().includes('KARB')
        if ((!otherIsKBR && !otherIsKAR) || !otherItem.regleta) continue

        const otherPos = new Vector3()
        const otherQuat = new Quaternion()
        otherItem.matrix.decompose(otherPos, otherQuat, new Vector3())

        const otherCatalog = otherItem.catalogID?.toString().toLowerCase() ?? ''
        const otherIsLeftRinconero = otherCatalog.endsWith('i')

        const otherItemXAxis = new Vector3(1, 0, 0).applyQuaternion(otherQuat)
        const directionMultiplier =
          otherItemXAxis.dot(new Vector3(itemPos.x - otherPos.x, 0, itemPos.z - otherPos.z)) < 0
            ? -1
            : 1

        const otherRegletaMorph = otherItem.morphs?.find((m) => m.name === 'Regleta')
        // KAR usa regleta directa (no invertida), KBR usa regleta invertida
        const otherRegletaValue = otherIsKAR
          ? otherItem.regleta || 0
          : getRegletaInvertedValue(otherItem.regleta || 0, otherRegletaMorph)

        if (!otherIsLeftRinconero && directionMultiplier > 0) {
          // Rinconero derecho: el item queda pegado a la regleta por su lado IZQUIERDO
          const expectedPosition = otherPos
            .clone()
            .add(otherItemXAxis.clone().multiplyScalar(directionMultiplier * otherItem.width))
          const zAxis = new Vector3(0, 0, 1).applyQuaternion(otherQuat)
          expectedPosition.add(
            zAxis.multiplyScalar((otherItem.thickness || 0) + otherRegletaValue),
          )
          if (expectedPosition.distanceTo(itemPos) < 50) return true
        } else if (otherIsLeftRinconero && directionMultiplier < 0) {
          // Rinconero izquierdo: el item queda pegado a la regleta por su lado DERECHO
          const expectedPosition = otherPos.clone()
          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            Math.PI / 2,
          )
          const adjustedQuat = otherQuat.clone().multiply(rotationAdjustment)
          const rinconeroOffset =
            (otherItem.thickness || 0) + otherRegletaValue + (item.width || 0)
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(adjustedQuat)
          expectedPosition.add(xAxis.multiplyScalar(-rinconeroOffset))
          if (expectedPosition.distanceTo(itemPos) < 50) return false
        }
      }

      return null
    }

    // Función para encontrar la unidad pegada a la regleta de un KARB/KBR
    const findUnitSnappedToRegleta = (
      rinconero: LinkedItem,
      items: Map<string, SceneObjects>,
    ): LinkedItem | null => {
      const rinconeroPos = new Vector3()
      const rinconeroQuat = new Quaternion()
      rinconero.matrix.decompose(rinconeroPos, rinconeroQuat, new Vector3())

      const isKBR = rinconero.catalogID?.toString().toUpperCase().includes('KBR')
      const isKAR = rinconero.catalogID?.toString().toUpperCase().includes('KARB')
      if ((!isKBR && !isKAR) || !rinconero.regleta) return null

      const catalog = rinconero.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = catalog.endsWith('i')
      const isRightRinconero = !isLeftRinconero

      for (const [id, otherItem] of items) {
        if (id === rinconero.id) continue
        if (!isLinkedItem(otherItem)) continue
        if (otherItem.catalogID === 'Frontal') continue
        if (!otherItem.visible) continue

        const otherPos = new Vector3()
        const otherQuat = new Quaternion()
        otherItem.matrix.decompose(otherPos, otherQuat, new Vector3())

        const item1XAxis = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
        const directionMultiplier =
          item1XAxis.dot(new Vector3(otherPos.x - rinconeroPos.x, 0, otherPos.z - rinconeroPos.z)) <
          0
            ? -1
            : 1

        const regletaMorph = rinconero.morphs?.find((m) => m.name === 'Regleta')
        const regletaValue = isKAR
          ? rinconero.regleta || 0
          : getRegletaInvertedValue(rinconero.regleta || 0, regletaMorph)

        let expectedPosition: Vector3

        if (isRightRinconero && directionMultiplier > 0) {
          expectedPosition = rinconeroPos
            .clone()
            .add(item1XAxis.multiplyScalar(directionMultiplier * rinconero.width))
          const zAxis = new Vector3(0, 0, 1).applyQuaternion(rinconeroQuat)
          const rinconeroOffset = (rinconero.thickness || 0) + regletaValue
          expectedPosition.add(zAxis.multiplyScalar(rinconeroOffset))
        } else if (isLeftRinconero && directionMultiplier < 0) {
          expectedPosition = rinconeroPos.clone()
          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            Math.PI / 2,
          )
          const adjustedQuat = rinconeroQuat.clone().multiply(rotationAdjustment)
          const rinconeroOffset = (rinconero.thickness || 0) + regletaValue + (otherItem.width || 0)
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(adjustedQuat)
          expectedPosition.add(xAxis.multiplyScalar(-rinconeroOffset))
        } else {
          continue
        }

        const distance = expectedPosition.distanceTo(otherPos)
        if (distance <= 60) {
          return otherItem as LinkedItem
        }
      }

      return null
    }

    // Función para verificar si dos items están alineados en la misma pared
    const areItemsAlignedOnWall = (
      item1: LinkedItem,
      item2: LinkedItem,
      side: 'left' | 'right',
      getRegletaRealValue: (item: LinkedItem) => number,
    ): boolean => {
      // Extraer posiciones y rotaciones
      const pos1 = new Vector3()
      const quat1 = new Quaternion()
      item1.matrix.decompose(pos1, quat1, new Vector3())

      const pos2 = new Vector3()
      const quat2 = new Quaternion()
      item2.matrix.decompose(pos2, quat2, new Vector3())

      // Verificar si comparten la misma pared
      if (item1.snappedWall && item2.snappedWall && item1.snappedWall !== item2.snappedWall) {
        return false
      }

      const sceneBoundingBoxes = get().sceneBoundingBoxes
      const item1OBB = sceneBoundingBoxes.get(item1.id)
      const item2OBB = sceneBoundingBoxes.get(item2.id)

      if (item1OBB && item2OBB) {
        // Usar OBB reales para calcular límites más precisos
        const obb1 = item1OBB.boundingBox
        const obb2 = item2OBB.boundingBox

        // Obtener los puntos de las esquinas de las OBB proyectadas en el plano XZ
        const getOBBBoundsXZ = (obb: OBB) => {
          const corners = [
            new Vector3(-1, 0, -1),
            new Vector3(1, 0, -1),
            new Vector3(-1, 0, 1),
            new Vector3(1, 0, 1),
          ]
          const worldCorners = corners.map((corner) => {
            const worldCorner = corner.clone()
            // Multiplicar cada componente por el halfSize correspondiente
            worldCorner.x *= obb.halfSize.x
            worldCorner.y *= obb.halfSize.y
            worldCorner.z *= obb.halfSize.z
            worldCorner.applyMatrix3(obb.rotation)
            worldCorner.add(obb.center)
            return worldCorner
          })

          const xValues = worldCorners.map((c) => c.x)
          const zValues = worldCorners.map((c) => c.z)
          const minX = Math.min(...xValues)
          const maxX = Math.max(...xValues)
          const minZ = Math.min(...zValues)
          const maxZ = Math.max(...zValues)

          return { minX, maxX, minZ, maxZ, center: obb.center }
        }

        const bounds1 = getOBBBoundsXZ(obb1)
        const bounds2 = getOBBBoundsXZ(obb2)

        // Verificar superposición en el eje relevante usando límites OBB
        if (side === 'left') {
          // Para lateral izquierdo, verificar si el item2 está a la izquierda del item1
          // El lateral estaría en bounds1.minX - 19/2, verificar si bounds2.maxX está cerca
          const lateralX = bounds1.minX - 9.5 // 19/2
          const distanceLeft = Math.abs(bounds2.maxX - lateralX)
          // Verificar también si hay superposición en el eje Z (profundidad)
          const zOverlap = bounds1.minZ <= bounds2.maxZ + 20 && bounds1.maxZ >= bounds2.minZ - 20
          return distanceLeft < 30 && zOverlap // Tolerancia más estricta: 30 unidades
        } else {
          // Para lateral derecho, verificar si el item2 está a la derecha del item1
          // El lateral estaría en bounds1.maxX + 19/2, verificar si bounds2.minX está cerca
          const lateralX = bounds1.maxX + 9.5 // 19/2
          const distanceRight = Math.abs(bounds2.minX - lateralX)
          // Verificar también si hay superposición en el eje Z (profundidad)
          const zOverlap = bounds1.minZ <= bounds2.maxZ + 20 && bounds1.maxZ >= bounds2.minZ - 20
          return distanceRight < 30 && zOverlap // Tolerancia más estricta: 30 unidades
        }
      }

      // Fallback: Calcular los límites de cada item usando el valor REAL de regleta
      const getItemBounds = (item: LinkedItem, pos: Vector3, quat: Quaternion) => {
        const forward = new Vector3(0, 0, 1).applyQuaternion(quat)
        const right = new Vector3(1, 0, 0).applyQuaternion(quat)
        const center = pos.clone()
        const width = item.width
        // Usar el valor REAL (invertido) de regleta para cálculos geométricos
        const isKBR = item.catalogID?.toString().toUpperCase().includes('KBR')
        const regletaRealValue = isKBR && item.regleta ? getRegletaRealValue(item) : 0
        const depth = item.thickness + regletaRealValue

        return {
          front: center.clone().add(forward.multiplyScalar(depth / 2)),
          back: center.clone().add(forward.multiplyScalar(-depth / 2)),
          left: center.clone().add(right.multiplyScalar(-width / 2)),
          right: center.clone().add(right.multiplyScalar(width / 2)),
          center: center,
        }
      }

      const bounds1 = getItemBounds(item1, pos1, quat1)
      const bounds2 = getItemBounds(item2, pos2, quat2)

      // Verificar superposición en el eje relevante con tolerancia más estricta
      if (side === 'left') {
        // Para lateral izquierdo, verificar si el item2 está a la izquierda del item1
        const distanceLeft = bounds1.left.distanceTo(bounds2.right)
        // Verificar también superposición en profundidad
        const depthOverlap =
          Math.abs(bounds1.front.z - bounds2.back.z) < 50 ||
          Math.abs(bounds1.back.z - bounds2.front.z) < 50 ||
          (bounds1.front.z >= bounds2.back.z - 20 && bounds1.back.z <= bounds2.front.z + 20)
        return distanceLeft < 30 && depthOverlap // Tolerancia más estricta: 30 unidades
      } else {
        // Para lateral derecho, verificar si el item2 está a la derecha del item1
        const distanceRight = bounds1.right.distanceTo(bounds2.left)
        // Verificar también superposición en profundidad
        const depthOverlap =
          Math.abs(bounds1.front.z - bounds2.back.z) < 50 ||
          Math.abs(bounds1.back.z - bounds2.front.z) < 50 ||
          (bounds1.front.z >= bounds2.back.z - 20 && bounds1.back.z <= bounds2.front.z + 20)
        return distanceRight < 30 && depthOverlap // Tolerancia más estricta: 30 unidades
      }
    }

    for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
      const group = groups[groupIndex]
      // if (group.length === 0) continue

          // Extremos de fila + shouldHostLateral (familia cubriente cede/asume la junta).
          // El catálogo solo decide CÓMO se dibuja, nunca SI toca.
      const groupIds = new Set(group.map((entry) => String(entry.id)))
      for (const entry of group) {
        const item = items.get(entry.id) as LinkedItem
        if (!item || !isLinkedItem(item)) continue

            const catalogUpper = item.catalogID?.toString().toUpperCase() ?? ''
            // Separar KALB (en L) de KARB (rectos en esquina, igual que KBR)
            const isKALB = catalogUpper.includes('KALB')
            const isKARB = catalogUpper.includes('KARB')
            const isKBR = Boolean(entry.isKBR)

            for (const isLeftSide of [true, false]) {
              const dbgKey = lateralSideKey(String(item.id), isLeftSide)
              dbgSkipReasons.delete(dbgKey)
              const dbgDrawnBefore = placedLateralsThisRun.length
              let dbgRama: string
              // El lateral de regleta de un KARB lo procesa OTRO mueble: el motivo se guarda
              // con la clave de ese otro item.
              let dbgReasonKey = dbgKey

              if (!itemShouldHostLateral(item, isLeftSide, groupIds)) {
                const covering =
                  !isCoveringFamily(getCabinetFamily(item))
                    ? findAdjacentCabinetOnSide({
                        item,
                        isLeftSide,
                        items,
                        runSlots,
                        family: COVERING_FAMILIES,
                      })
                    : null
                if (covering) {
                  dbgSkip(
                    item.id,
                    isLeftSide,
                    `${getCabinetFamily(covering)} ${dbgLabel(covering.id)} cubre esta interfaz`,
                  )
                  dbgRama = `interfaz cubierta por ${dbgLabel(covering.id)} → sin lateral`
                } else {
                  // Extremo ocupado: el único lateral que sigue tocando ahí es el de la regleta
                  // de un KARB, que no va en el propio rinconero sino en el extremo libre del
                  // mueble que lleva pegado.
                  const isLeftRinconero = (item.catalogID?.toString().toLowerCase() ?? '').endsWith(
                    'i',
                  )
                  const unitPegadaARegleta =
                    isKARB && item.regleta && isLeftRinconero === isLeftSide
                      ? findUnitSnappedToRegleta(item, items)
                      : null
                  if (unitPegadaARegleta) {
                    dbgReasonKey = lateralSideKey(String(unitPegadaARegleta.id), isLeftSide)
                    dbgSkipReasons.delete(dbgReasonKey)
                    await processLateralKARB(unitPegadaARegleta, isLeftSide)
                    dbgRama = `extremo ocupado · KARB regleta → lateral sobre ${dbgLabel(unitPegadaARegleta.id)}`
                  } else {
                    dbgRama = `extremo ocupado por ${dbgLabel(runSlots.get(item.id)?.[isLeftSide ? 'left' : 'right'])} → sin lateral`
                  }
                }
              } else if (isKALB) {
                await processLateralKALB(item, isLeftSide)
                dbgRama = 'extremo libre → processLateralKALB'
              } else if (isKARB) {
                await processLateralKARB(item, isLeftSide)
                dbgRama = 'extremo libre → processLateralKARB'
              } else if (isKBR) {
                await processLateralKBR(item, isLeftSide)
                dbgRama = 'extremo libre → processLateralKBR'
              } else {
                await processLateral(item, isLeftSide, groupIds)
                dbgRama = 'extremo libre → processLateral'
              }

              if (DEBUG_LATERALES) {
                const nuevos = placedLateralsThisRun.slice(dbgDrawnBefore)
                for (const n of nuevos) {
                  dbgLateralSide.set(n.lateralId, isLeftSide ? 'left' : 'right')
                }
                dbgDecisions.push({
                  grupo: groupIndex,
                  item: dbgLabel(item.id),
                  lado: isLeftSide ? 'left' : 'right',
                  libre: itemShouldHostLateral(item, isLeftSide, groupIds),
                  'ocupado por': dbgLabel(runSlots.get(item.id)?.[isLeftSide ? 'left' : 'right']),
                  rama: dbgRama,
                  lateral: nuevos.length
                    ? nuevos
                        .map((n) => `${dbgLabel(n.lateralId)} en ${dbgLabel(n.furnitureId)}`)
                        .join(' + ')
                    : '—',
                  motivo:
                    dbgSkipReasons.get(dbgReasonKey) ??
                    (nuevos.length ? '' : 'descartado sin motivo registrado'),
                })
              }
        }
      }
    }

    if (DEBUG_LATERALES) {
      console.log('🪵 [laterales] decisión por mueble y lado:')
      console.table(dbgDecisions)
      console.log(`🪵 [laterales] dibujados ${placedLateralsThisRun.length} lateral(es):`)
      console.table(
        placedLateralsThisRun.map(({ lateralId, furnitureId }) => ({
          lateral: dbgLabel(lateralId),
          mueble: dbgLabel(furnitureId),
          lado: dbgLateralSide.get(lateralId) ?? '?',
        })),
      )
    }

    // LinkedEditor: tras redimensionar y activar laterales, forzar alto/fondo del mueble
    // (drawItem deja catálogo base; apply puede correr antes de que el GLB asiente morphs).
    const reapplyPlacedLateralDimensions = () => {
      for (const { lateralId, furnitureId } of placedLateralsThisRun) {
        const furniture = get().items.get(furnitureId)
        if (furniture && isLinkedItem(furniture)) {
          applyLateralSceneDimensionsToItem(get, set, lateralId, furniture)
        }
      }
    }
    reapplyPlacedLateralDimensions()
    if (placedLateralsThisRun.length > 0) {
      await new Promise((resolve) => setTimeout(resolve, 80))
      reapplyPlacedLateralDimensions()
    }

    async function drawLateralLinkedItem(
      item: LinkedItem,
      catalogID: string,
      lateralMatrix: Matrix4,
      context: string,
    ): Promise<AnyID | null> {
      // Dims frescas del store (tras modificar alto/fondo en sidebar).
      const furniture =
        (get().items.get(item.id) as LinkedItem | undefined) ?? item
      const tipo = String(furniture.tipoNormalizado ?? '').toUpperCase()
      const fallbacks =
        tipo === 'MBJ'
          ? getMbjLateralCatalogFallbacks(catalogID)
          : tipo === 'MAL'
            ? getMalLateralCatalogFallbacks(catalogID)
            : tipo === 'COL'
              ? ['CC23060', 'CC24060', 'CC14060', 'CC15060', 'LC13860', 'LC14860'].filter(
                  (id) => id !== catalogID.toUpperCase(),
                )
              : []
      const candidates = [catalogID, ...fallbacks]

      for (const candidateId of candidates) {
        const includeLinkedSons = freeDimensionUsesLb8060StyleAltoOffset({ catalogID: candidateId })
        try {
          const lateralId = await get().drawItem(
            getId('linkedItem'),
            candidateId,
            '',
            true,
            lateralMatrix,
            'items',
            undefined,
            false,
            !includeLinkedSons,
            furniture.id,
          )
          if (lateralId) {
            placedLateralsThisRun.push({ lateralId, furnitureId: furniture.id })
            set((store) => {
              const lat = store.items.get(lateralId)
              if (lat && isLinkedItem(lat)) {
                lat.lateralHostFurnitureId = furniture.id
              }
            })
            applyLateralSceneDimensionsToItem(get, set, lateralId, furniture)
            return lateralId
          }
        } catch (error: unknown) {
          if (candidateId === candidates[candidates.length - 1]) {
            console.error(
              `❌ Error al dibujar lateral ${candidateId} para mueble ${furniture.id} (${furniture.catalogID})${context ? ` [${context}]` : ''}:`,
              error,
            )
          }
        }
      }
      return null
    }

    /**
     * Procesa un lateral para un mueble específico
     */
    async function processLateral(
      item: LinkedItem,
      isFirstItem: boolean,
      groupIds?: Set<string>,
    ) {
      // Cara ocupada o cubierta por otra unidad: no dibujar en este mueble.
      if (!itemShouldHostLateral(item, isFirstItem, groupIds)) {
        dbgSkip(item.id, isFirstItem, 'shouldHostLateral=false (recheck en processLateral)')
        return
      }

          // Que la fila continúe / quién cubre lo resuelve el llamador (shouldHostLateral).
          // Aquí solo el caso pegado a regleta KBR/KARB: en ese lado no se dibuja lateral
          // (va en el extremo opuesto).
      const ladoPegadoARegleta = getLadoPegadoARegleta(item)
      if (ladoPegadoARegleta !== null && ladoPegadoARegleta === isFirstItem) {
        dbgSkip(item.id, isFirstItem, 'lado pegado a la regleta de un rinconero')
        return
      }

      // Dims vivas del store (free-dim: width real, no morph.default de catálogo).
      const furniture =
        (get().items.get(item.id) as LinkedItem | undefined) ?? item
      const liveWidth = furniture.width || 0

      // Para rinconeros (KBR, KALB), siempre permitir laterales incluso en esquinas
      // Para otros muebles, verificar si hay espacio disponible en la pared
      const isRinconero =
        furniture.catalogID?.toString().toUpperCase().includes('KBR') ||
        furniture.catalogID?.toString().toUpperCase().includes('KALB')
      if (!isRinconero && !hasAvailableSpace(furniture, isFirstItem)) {
        dbgSkip(item.id, isFirstItem, 'hasAvailableSpace=false (sin hueco contra la pared)')
        return
      }
      const lateralConfig = buildLateralConfig(furniture, isFirstItem)
      if (!lateralConfig) {
        dbgSkip(item.id, isFirstItem, 'buildLateralConfig=null (sin catálogo de lateral)')
        return
      }
      const { catalogID, heightOffset } = lateralConfig

      const isKBR = furniture.catalogID?.toString().toUpperCase().includes('KBR')
      let lateralMatrix: Matrix4

      if (isKBR && furniture.regleta) {
        // Decidir cuál lateral es el de regleta:
        // - KBR...I (izquierdo): el PRIMER lateral (isFirstItem === true) es el de regleta
        // - KBR...  (derecho) : el SEGUNDO lateral (isFirstItem === false) es el de regleta
        const catalog = furniture.catalogID?.toString().toLowerCase() ?? ''
        const isLeftRinconero = catalog.endsWith('i')
        const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem

        if (isRegletaLateral) {
          lateralMatrix = calculateKBRLateralMatrix(furniture, isFirstItem, heightOffset)
        } else {
          const offset = isFirstItem ? -19 : liveWidth
          lateralMatrix = getRelativeMatrix(furniture.matrix, offset, undefined, heightOffset)
        }
      } else {
        // No es KBR: lógica estándar (el lado pegado a regleta ya se descartó arriba)
        const offset = isFirstItem ? -19 : liveWidth
        lateralMatrix = getRelativeMatrix(furniture.matrix, offset, furniture, heightOffset)
      }

      if (!claimLateralSide(furniture.id, isFirstItem)) {
        dbgSkip(item.id, isFirstItem, 'lado ya reclamado antes en esta pasada')
        return
      }

      const lateralId = await drawLateralLinkedItem(
        furniture,
        catalogID,
        lateralMatrix,
        'processLateral',
      )
      if (!lateralId) {
        dbgSkip(item.id, isFirstItem, `drawItem falló para ${catalogID}`)
        return
      }

      // Actualizar x, z del lateral desde la matriz (como los elementos enlazados)
      const finalPosition = new Vector3()
      lateralMatrix.decompose(finalPosition, new Quaternion(), new Vector3())
      get().editItem(lateralId, { x: finalPosition.x, z: finalPosition.z })

      if (furniture.tipoNormalizado === 'MAL') {
        get().editItemHeight(furniture.y, lateralId)
      }

      if (furniture.snappedWall && !isKBR) {
        get().editSnappedWall(lateralId, furniture.snappedWall)
        setTimeout(() => {
          const lateralItem = get().items.get(lateralId)
          if (lateralItem && lateralItem.matrix) {
            // skipContiguityCheck: no empujar el lateral al final del grupo MBJ
            get().projectItemOnWall(
              lateralId,
              furniture.snappedWall,
              lateralItem.matrix,
              true,
            )
          }
        }, 100)
      }
    }

    async function processLateralKBR(item: LinkedItem, isFirstItem: boolean) {
      const furniture =
        (get().items.get(item.id) as LinkedItem | undefined) ?? item
      const liveWidth = furniture.width || 0
      const lateralConfig = buildLateralConfig(furniture, isFirstItem)
      if (!lateralConfig) {
        dbgSkip(item.id, isFirstItem, 'buildLateralConfig=null (KBR)')
        return
      }
      const { catalogID, heightOffset } = lateralConfig
      let lateralMatrix: Matrix4

      const catalog = furniture.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = catalog.endsWith('i')
      const isRegletaLateral =
        !!furniture.regleta && (isLeftRinconero ? isFirstItem : !isFirstItem)

      if (isRegletaLateral) {
        // Extremo de la regleta / brazo en L (donde termina la encimera).
        lateralMatrix = calculateKBRLateralMatrix(furniture, isFirstItem, heightOffset)
      } else {
        const offset = isFirstItem ? -19 : liveWidth
        lateralMatrix = getRelativeMatrix(
          furniture.matrix,
          offset,
          furniture.regleta ? undefined : furniture,
          heightOffset,
        )
      }

      if (!claimLateralSide(furniture.id, isFirstItem)) {
        dbgSkip(item.id, isFirstItem, 'lado ya reclamado antes en esta pasada (KBR)')
        return
      }

      const lateralId = await drawLateralLinkedItem(
        furniture,
        catalogID,
        lateralMatrix,
        'processLateralKBR',
      )
      if (!lateralId) {
        dbgSkip(item.id, isFirstItem, `drawItem falló para ${catalogID} (KBR)`)
        return
      }

      const finalPosition = new Vector3()
      lateralMatrix.decompose(finalPosition, new Quaternion(), new Vector3())
      get().editItem(lateralId, { x: finalPosition.x, z: finalPosition.z })

      if (furniture.tipoNormalizado === 'MAL') {
        get().editItemHeight(furniture.y, lateralId)
      }

      if (!furniture.snappedWall) return

      if (isRegletaLateral) {
        // Posición = calculateKBRLateralMatrix (al ras). NO projectItemOnWall:
        // el setTimeout desplazaba el LB (“primero bien, luego mal”).
        // Solo snappedWall + rotación alineada a la pared lateral.
        const sideWallId = findRinconeroRegletaSideWallId(furniture, lateralMatrix)
        if (sideWallId) {
          get().editSnappedWall(lateralId, sideWallId)
          alignLateralRotationToWall(lateralId, sideWallId)
        }
      } else {
        get().editSnappedWall(lateralId, furniture.snappedWall)
      }
    }

    /** Gira el lateral para que su espalda mire a la pared; no mueve x/z. */
    function alignLateralRotationToWall(lateralId: AnyID, wallId: AnyID) {
      const lateral = get().items.get(lateralId)
      const wall = get().items.get(wallId) as Walls | undefined
      if (!lateral || !isLinkedItem(lateral) || !wall || !isWall(wall)) return

      const start = new Vector3(wall.start[0], 0, wall.start[1])
      const end = new Vector3(wall.end[0], 0, wall.end[1])
      const wallXAxis = end.clone().sub(start)
      if (wallXAxis.lengthSq() < 1e-6) return
      wallXAxis.normalize()
      const wallQuat = new Quaternion().setFromUnitVectors(new Vector3(1, 0, 0), wallXAxis)

      const pos = new Vector3()
      const scale = new Vector3()
      lateral.matrix.decompose(pos, new Quaternion(), scale)
      const newMatrix = new Matrix4().compose(pos, wallQuat, scale)
      const eulerY = new Euler().setFromQuaternion(wallQuat, 'YXZ').y

      set((store) => {
        const lat = store.items.get(lateralId)
        if (!lat || !isLinkedItem(lat)) return
        lat.matrix = newMatrix
        lat.rotation = eulerY
        lat.x = pos.x
        lat.z = pos.z
      })
    }

    /**
     * Pared del final del brazo de regleta (perpendicular a item.snappedWall).
     */
    function findRinconeroRegletaSideWallId(
      item: LinkedItem,
      lateralMatrix: Matrix4,
    ): AnyID | null {
      if (!item.snappedWall) return null
      const allItems = get().items
      const parentWall = allItems.get(item.snappedWall) as Walls | undefined
      if (!parentWall) return null

      const lateralPos = new Vector3()
      lateralMatrix.decompose(lateralPos, new Quaternion(), new Vector3())
      lateralPos.y = 0

      const distToWall = (wall: Walls): number => {
        const start = new Vector3(wall.start[0], 0, wall.start[1])
        const end = new Vector3(wall.end[0], 0, wall.end[1])
        const seg = end.clone().sub(start)
        const lenSq = seg.lengthSq()
        if (lenSq < 1e-6) return lateralPos.distanceTo(start)
        const t = Math.max(0, Math.min(1, lateralPos.clone().sub(start).dot(seg) / lenSq))
        return lateralPos.distanceTo(start.clone().addScaledVector(seg, t))
      }

      const candidates: AnyID[] = []
      if (parentWall.prevWall) candidates.push(parentWall.prevWall)
      if (parentWall.nextWall) candidates.push(parentWall.nextWall)

      let bestId: AnyID | null = null
      let bestDist = Infinity
      for (const wallId of candidates) {
        if (wallId === item.snappedWall) continue
        const wall = allItems.get(wallId)
        if (!wall || !isWall(wall)) continue
        const d = distToWall(wall)
        if (d < bestDist) {
          bestDist = d
          bestId = wallId
        }
      }
      if (bestId) return bestId

      for (const [id, obj] of allItems) {
        if (id === item.snappedWall || !isWall(obj)) continue
        const d = distToWall(obj)
        if (d < bestDist && d < 800) {
          bestDist = d
          bestId = id
        }
      }
      return bestId
    }

    /**
     * Procesa laterales para KARB (igual que KBR pero con regleta directa, no invertida)
     */
    async function processLateralKARB(item: LinkedItem, isFirstItem: boolean) {
      const furniture =
        (get().items.get(item.id) as LinkedItem | undefined) ?? item
      const liveWidth = furniture.width || 0
      const lateralConfig = buildLateralConfig(furniture, isFirstItem)
      if (!lateralConfig) {
        dbgSkip(item.id, isFirstItem, 'buildLateralConfig=null (KARB)')
        return
      }
      const { catalogID, heightOffset } = lateralConfig
      let lateralMatrix: Matrix4

      const catalog = furniture.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = catalog.endsWith('i')

      if (furniture.regleta) {
        const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem

        if (isRegletaLateral) {
          lateralMatrix = calculateKARBLateralMatrix(furniture, isFirstItem, heightOffset)
        } else {
          const offset = isFirstItem ? -19 : liveWidth
          lateralMatrix = getRelativeMatrix(furniture.matrix, offset, undefined, heightOffset)
        }
      } else {
        const offset = isFirstItem ? -19 : liveWidth
        lateralMatrix = getRelativeMatrix(furniture.matrix, offset, undefined, heightOffset)
      }

      if (!claimLateralSide(furniture.id, isFirstItem)) {
        dbgSkip(item.id, isFirstItem, 'lado ya reclamado antes en esta pasada (KARB)')
        return
      }

      const lateralId = await drawLateralLinkedItem(
        furniture,
        catalogID,
        lateralMatrix,
        'processLateralKARB',
      )
      if (!lateralId) {
        dbgSkip(item.id, isFirstItem, `drawItem falló para ${catalogID} (KARB)`)
        return
      }

      const finalPosition = new Vector3()
      lateralMatrix.decompose(finalPosition, new Quaternion(), new Vector3())
      get().editItem(lateralId, { x: finalPosition.x, z: finalPosition.z })

      if (furniture.tipoNormalizado === 'MAL') {
        get().editItemHeight(furniture.y, lateralId)
      }

      // Para laterales de KARB con regleta, NO proyectar en la pared porque la posición
      // ya está calculada correctamente con calculateKARBLateralMatrix
      const isKARBWithRegletaLateral =
        furniture.regleta && (isLeftRinconero ? isFirstItem : !isFirstItem)

      if (
        furniture.snappedWall &&
        !isInCorner(furniture, isFirstItem) &&
        !isKARBWithRegletaLateral
      ) {
        get().editSnappedWall(lateralId, furniture.snappedWall)
        setTimeout(() => {
          const lateralItem = get().items.get(lateralId)
          if (lateralItem && lateralItem.matrix) {
            get().projectItemOnWall(
              lateralId,
              furniture.snappedWall,
              lateralItem.matrix,
              true,
            )
          }
        }, 100)
      } else if (furniture.snappedWall && isKARBWithRegletaLateral) {
        // Para KARB con regleta, solo asignar la pared pero NO proyectar (la posición ya está correcta)
        get().editSnappedWall(lateralId, furniture.snappedWall)
      }
    }

    /**
     * Procesa laterales para rinconeros altos (KALB) en los lados cortos/alejados
     * Los rinconeros en L tienen dos lados cortos: uno en cada brazo del L (las esquinas)
     */
    async function processLateralKALB(item: LinkedItem, isFirstShortSide: boolean) {
      const lateralConfig = buildLateralConfig(item, isFirstShortSide)
      if (!lateralConfig) {
        dbgSkip(item.id, isFirstShortSide, 'buildLateralConfig=null (KALB)')
        return
      }
      const { catalogID, heightOffset } = lateralConfig

      const basePosition = new Vector3()
      const baseQuaternion = new Quaternion()
      const baseScale = new Vector3()
      item.matrix.decompose(basePosition, baseQuaternion, baseScale)

      let lateralMatrix: Matrix4

      if (isFirstShortSide) {
        const offset = -19
        lateralMatrix = getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
      } else {
        // Segundo lado corto: en el extremo del segundo brazo (lado del segundo brazo, rotado 270°)
        // Este es el lateral que va apoyado al frente, sin desplazamiento extra de 19mm
        const rotationAdjustment = new Quaternion().setFromAxisAngle(
          new Vector3(0, 1, 0),
          (3 * Math.PI) / 2,
        )
        const newQuat = baseQuaternion.clone().multiply(rotationAdjustment)

        const xAxis = new Vector3(1, 0, 0).applyQuaternion(baseQuaternion)
        const zAxis = new Vector3(0, 0, 1).applyQuaternion(baseQuaternion)

        const endOfSecondArm = basePosition
          .clone()
          .add(xAxis.multiplyScalar(item.width))
          .add(zAxis.multiplyScalar(item.thickness))

        // Crear una matriz temporal en el extremo del segundo brazo con la rotación correcta
        const tempMatrix = new Matrix4().compose(endOfSecondArm, newQuat, baseScale)

        // Aquí NO aplicamos offset extra en X: el frente hace de referencia
        // (offset = 0 → solo heightOffset)
        lateralMatrix = getRelativeMatrix(tempMatrix, 0, undefined, heightOffset)
      }

      if (!claimLateralSide(item.id, isFirstShortSide)) {
        dbgSkip(item.id, isFirstShortSide, 'lado ya reclamado antes en esta pasada (KALB)')
        return
      }

      const lateralId = await drawLateralLinkedItem(item, catalogID, lateralMatrix, 'processLateralKALB')
      if (!lateralId) {
        dbgSkip(item.id, isFirstShortSide, `drawItem falló para ${catalogID} (KALB)`)
        return
      }

      // Actualizar x, z del lateral desde la matriz
      const finalPosition = new Vector3()
      lateralMatrix.decompose(finalPosition, new Quaternion(), new Vector3())
      get().editItem(lateralId, { x: finalPosition.x, z: finalPosition.z })

      // Cambiar altura para normalizados
      if (item.tipoNormalizado === 'MAL') {
        get().editItemHeight(item.y, lateralId)
      }
    }

    /**
     * Calcula la matriz del lateral para rinconeros KBR de manera relativa al rinconero
     */
    function calculateKBRLateralMatrix(
      item: LinkedItem,
      isFirstItem: boolean,
      heightOffset: number,
    ): Matrix4 {
      function getRegletaInvertedValue(value: number, morph: Morphs) {
        if (!morph) return value
        if (value >= morph.max) return morph.min
        if (value <= morph.min) return morph.max
        return morph.max + morph.min - value
      }

      const baseMatrix = item.matrix.clone()
      const basePosition = new Vector3()
      const baseQuaternion = new Quaternion()
      const baseScale = new Vector3()
      baseMatrix.decompose(basePosition, baseQuaternion, baseScale)

      // Obtener el valor de regleta usando la misma lógica que getRelativeMatrix
      // const regletaMorph = item.morphs?.find((m) => m.name === 'Regleta')
      // const regletaRaw = typeof item.regleta === 'number' ? item.regleta : 60
      // const regletaValue = getRegletaInvertedValue(regletaRaw, regletaMorph)

      // const thicknessValue =
      //   typeof item.thickness === 'number' && item.thickness > 0 ? item.thickness : 600
      // const widthValue = typeof item.width === 'number' && item.width > 0 ? item.width : 1000

      const hand = (item.hand || item.mano || '').toString().toLowerCase()
      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = hand.includes('cd') || catalog.endsWith('i')

      // Determinar si este lateral está más próximo a la regleta
      const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem

      let lateralPosition: Vector3
      let lateralQuaternion: Quaternion

      if (isRegletaLateral) {
        const rinconeroQuat = baseQuaternion.clone()
        const rinconeroPos = basePosition.clone()

        if (!isLeftRinconero) {
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
          const newPos = rinconeroPos.clone().add(xAxis.multiplyScalar(item.width))

          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            -Math.PI / 2,
          )
          const newQuat = rinconeroQuat.clone().multiply(rotationAdjustment)

          // thickness + regleta: al ras del final de la encimera/regleta (sin −50:
          // ese hueco dejaba el LB separado a la derecha).
          const rinconeroOffset =
            (item.thickness || 0) +
            (getRegletaInvertedValue(
              item.regleta || 0,
              item.morphs?.find((m) => m.name === 'Regleta'),
            ) || 0)

          const zAxis = new Vector3(0, 0, 1).applyQuaternion(rinconeroQuat)
          newPos.add(zAxis.multiplyScalar(rinconeroOffset))

          if (heightOffset) newPos.y += heightOffset

          lateralPosition = newPos
          lateralQuaternion = newQuat
        } else {
          const newPos = rinconeroPos.clone()

          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            Math.PI / 2,
          )
          const newQuat = rinconeroQuat.clone().multiply(rotationAdjustment)

          const rinconeroOffset =
            (item.thickness || 0) +
            (getRegletaInvertedValue(
              item.regleta || 0,
              item.morphs?.find((m) => m.name === 'Regleta'),
            ) || 0)

          const zAxis = new Vector3(1, 0, 0).applyQuaternion(newQuat)
          newPos.add(zAxis.multiplyScalar(-rinconeroOffset))

          if (heightOffset) newPos.y += heightOffset

          lateralPosition = newPos
          lateralQuaternion = newQuat
        }
      } else {
        const offset = isFirstItem ? -19 : item.width
        return getRelativeMatrix(item.matrix, offset, item, heightOffset)
      }

      return new Matrix4().compose(lateralPosition, lateralQuaternion, baseScale)
    }

    /**
     * Calcula la matriz del lateral para rinconeros KARB (igual que KBR pero con regleta directa, no invertida, y ajuste -40mm)
     */
    function calculateKARBLateralMatrix(
      item: LinkedItem,
      isFirstItem: boolean,
      heightOffset: number,
    ): Matrix4 {
      const baseMatrix = item.matrix.clone()
      const basePosition = new Vector3()
      const baseQuaternion = new Quaternion()
      const baseScale = new Vector3()
      baseMatrix.decompose(basePosition, baseQuaternion, baseScale)

      const hand = (item.hand || item.mano || '').toString().toLowerCase()
      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = hand.includes('cd') || catalog.endsWith('i')

      // Determinar si este lateral está más próximo a la regleta
      const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem

      let lateralPosition: Vector3
      let lateralQuaternion: Quaternion

      if (isRegletaLateral) {
        const rinconeroQuat = baseQuaternion.clone()
        const rinconeroPos = basePosition.clone()

        // Para KARB: usar regleta directa (NO invertida) y ajuste de -40mm
        const regletaValue = item.regleta || 0
        const KARB_OFFSET_ADJUSTMENT = -50

        if (!isLeftRinconero) {
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
          const newPos = rinconeroPos.clone().add(xAxis.multiplyScalar(item.width))

          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            -Math.PI / 2,
          )
          const newQuat = rinconeroQuat.clone().multiply(rotationAdjustment)

          const rinconeroOffset = (item.thickness || 0) + regletaValue + KARB_OFFSET_ADJUSTMENT

          const zAxis = new Vector3(0, 0, 1).applyQuaternion(rinconeroQuat)
          newPos.add(zAxis.multiplyScalar(rinconeroOffset))

          if (heightOffset) newPos.y += heightOffset

          lateralPosition = newPos
          lateralQuaternion = newQuat
        } else {
          const newPos = rinconeroPos.clone()

          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            Math.PI / 2,
          )
          const newQuat = rinconeroQuat.clone().multiply(rotationAdjustment)

          const rinconeroOffset = (item.thickness || 0) + regletaValue + KARB_OFFSET_ADJUSTMENT

          const zAxis = new Vector3(1, 0, 0).applyQuaternion(newQuat)
          newPos.add(zAxis.multiplyScalar(-rinconeroOffset))

          if (heightOffset) newPos.y += heightOffset

          lateralPosition = newPos
          lateralQuaternion = newQuat
        }
      } else {
        // Lateral que NO es de regleta: usar lógica simple sin pasar item como referenceItem
        // para evitar que getRelativeMatrix aplique lógica especial de rinconero
        const offset = isFirstItem ? -19 : item.width
        return getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
      }

      return new Matrix4().compose(lateralPosition, lateralQuaternion, baseScale)
    }

    function isInCorner(item: LinkedItem, isFirstItem: boolean) {
      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = catalog.endsWith('i')
      return (isLeftRinconero && !isFirstItem) || (!isLeftRinconero && isFirstItem)
    }

    /**
     * Verifica si hay espacio (~19mm) para un lateral en ese lado local.
     * No asumir que left=start de pared: el eje local X del mueble puede ir
     * en sentido contrario a wall.start→end (esquinas).
     */
    function hasAvailableSpace(item: LinkedItem, isFirstItem: boolean): boolean {
      // Si el mueble no está pegado a una pared, asumimos que hay espacio
      if (!item.snappedWall) return true

      const wall = items.get(item.snappedWall) as Walls
      if (!wall) return false

      const itemPos = new Vector3()
      const itemQuat = new Quaternion()
      item.matrix.decompose(itemPos, itemQuat, new Vector3())

      const wallStart = new Vector3(wall.start[0], 0, wall.start[1])
      const wallEnd = new Vector3(wall.end[0], 0, wall.end[1])
      const wallDir = wallEnd.clone().sub(wallStart)
      const wallLength = wallDir.length()
      if (wallLength < 1e-3) return true
      wallDir.multiplyScalar(1 / wallLength)

      // Origen del mueble = esquina izq-trasera; el lateral sale por left (−X) o right (+X)
      const localX = new Vector3(1, 0, 0).applyQuaternion(itemQuat)
      const sideAlongItem = isFirstItem ? 0 : item.width || 0
      const sideOrigin = itemPos.clone().addScaledVector(localX, sideAlongItem)
      const outward = isFirstItem ? -1 : 1
      const outerPoint = sideOrigin.clone().addScaledVector(localX, outward * 19)

      const along = outerPoint.clone().sub(wallStart).dot(wallDir)
      // Holgura respecto a ambos extremos de la pared (juntas con pared perpendicular)
      return along >= 20 && wallLength - along >= 20
    }

    // Función helper para detectar si hay unidades MBJ adyacentes a una unidad COL en un lado específico
    function hasMBJAdjacent(item: LinkedItem, isFirstItem: boolean): boolean {
      if (item.tipoNormalizado !== 'COL') return false

      const items = get().items
      const sceneBoundingBoxes = get().sceneBoundingBoxes
      const itemOBB = sceneBoundingBoxes.get(item.id)

      if (!itemOBB) {
        return false
      }

      // Primero verificar si están en el mismo grupo contiguo (más confiable)
      const { groups } = get().getContiguousItemsForLaterals()
      for (const group of groups) {
        const colInGroup = group.find((g) => g.id === item.id)
        if (colInGroup) {
          // Buscar MBJ en el mismo grupo
          for (const groupItem of group) {
            if (groupItem.id === item.id) continue
            const groupItemObj = items.get(groupItem.id) as LinkedItem
            if (
              groupItemObj &&
              isLinkedItem(groupItemObj) &&
              groupItemObj.tipoNormalizado === 'MBJ' &&
              groupItemObj.visible
            ) {
              // Verificar si el MBJ está en el lado correcto
              // Si es el primer item del grupo y estamos verificando el lado izquierdo, el MBJ debe estar después
              // Si es el último item del grupo y estamos verificando el lado derecho, el MBJ debe estar antes
              const colIndex = group.findIndex((g) => g.id === item.id)
              const mbjIndex = group.findIndex((g) => g.id === groupItem.id)

              if (isFirstItem && mbjIndex < colIndex) {
                return true
              } else if (!isFirstItem && mbjIndex > colIndex) {
                return true
              }
            }
          }
        }
      }

      const itemPos = new Vector3()
      const itemQuat = new Quaternion()
      item.matrix.decompose(itemPos, itemQuat, new Vector3())

      // Obtener los límites del item COL usando su OBB
      const colOBB = itemOBB.boundingBox
      const colRight = new Vector3(1, 0, 0).applyQuaternion(itemQuat)
      const colLeft = new Vector3(-1, 0, 0).applyQuaternion(itemQuat)

      // Calcular los límites izquierdo y derecho del COL
      const colWidth = item.width || 0
      const colLeftEdge = itemPos.clone().add(colLeft.multiplyScalar(colWidth / 2))
      const colRightEdge = itemPos.clone().add(colRight.multiplyScalar(colWidth / 2))

      // Contar todas las unidades MBJ visibles
      const allMBJItems: Array<{ id: string; catalogID: string; tipoNormalizado: string }> = []
      for (const [id, otherItem] of items) {
        if (!isLinkedItem(otherItem)) continue
        if (otherItem.catalogID === 'Frontal') continue
        if (!otherItem.visible) continue
        if (otherItem.tipoNormalizado === 'MBJ') {
          allMBJItems.push({
            id,
            catalogID: otherItem.catalogID?.toString() || 'unknown',
            tipoNormalizado: otherItem.tipoNormalizado || 'unknown',
          })
        }
      }

      for (const [id, otherItem] of items) {
        if (id === item.id) continue
        if (!isLinkedItem(otherItem)) continue
        if (otherItem.catalogID === 'Frontal') continue
        if (!otherItem.visible) continue
        if (otherItem.tipoNormalizado !== 'MBJ') continue

        const otherPos = new Vector3()
        const otherQuat = new Quaternion()
        otherItem.matrix.decompose(otherPos, otherQuat, new Vector3())

        // Verificar si están en la misma pared
        if (
          item.snappedWall &&
          otherItem.snappedWall &&
          item.snappedWall !== otherItem.snappedWall
        ) {
          continue
        }

        const otherItemOBB = sceneBoundingBoxes.get(id)
        if (!otherItemOBB) {
          continue
        }

        const mbjOBB = otherItemOBB.boundingBox

        // PRIMERO verificar si están en el mismo grupo contiguo
        // Si no están en el mismo grupo, descartar inmediatamente (no son realmente contiguos)
        const { groups } = get().getContiguousItemsForLaterals()
        let isInSameGroup = false
        for (const group of groups) {
          const colInGroup = group.find((g) => g.id === item.id)
          const mbjInGroup = group.find((g) => g.id === id)
          if (colInGroup && mbjInGroup) {
            isInSameGroup = true
            break
          }
        }

        if (!isInSameGroup) {
          continue
        }

        // Verificar si las OBB se intersectan o están muy cerca
        if (colOBB.intersectsOBB(mbjOBB)) {
          return true
        }

        // Calcular la distancia entre los límites de las OBB
        // Obtener los límites del MBJ
        const mbjRight = new Vector3(1, 0, 0).applyQuaternion(otherQuat)
        const mbjLeft = new Vector3(-1, 0, 0).applyQuaternion(otherQuat)
        const mbjWidth = otherItem.width || 0
        const mbjLeftEdge = otherPos.clone().add(mbjLeft.multiplyScalar(mbjWidth / 2))
        const mbjRightEdge = otherPos.clone().add(mbjRight.multiplyScalar(mbjWidth / 2))

        // Verificar si el MBJ está en el lado correcto de la COL
        // SOLO si están en el mismo grupo contiguo (ya verificado arriba)
        let distanceToEdge: number
        if (isFirstItem) {
          distanceToEdge = colLeftEdge.distanceTo(mbjRightEdge)
        } else {
          // Lado derecho: verificar distancia entre el borde derecho del COL y el borde izquierdo del MBJ
          distanceToEdge = colRightEdge.distanceTo(mbjLeftEdge)
        }

        // Solo considerar MBJs muy cercanos (<50mm) - isInSameGroup ya verificado arriba
        if (distanceToEdge < 50) {
          const depthDiff = Math.abs(itemPos.z - otherPos.z)
          const maxDepthDiff = distanceToEdge < 20 ? 400 : 100 // Más permisivo si están muy cerca

          if (depthDiff < maxDepthDiff) {
            const heightDiff = Math.abs(itemPos.y - otherPos.y)
            // console.log(
            //   `  📊 MBJ ${id} (${otherItem.catalogID}): diferencia altura=${heightDiff.toFixed(2)}`,
            // )

            if (heightDiff < 2000) {
              if (distanceToEdge < 20) {
                return true
              }

              const isAligned = areItemsAlignedOnWall(
                item,
                otherItem,
                isFirstItem ? 'left' : 'right',
                getRegletaRealValue,
              )

              if (isAligned || distanceToEdge < 30) {
                return true
              }
            }
          }
        }
      }

      return false
    }

    function buildLateralConfig(item: LinkedItem, isFirstItem: boolean) {
      const type = item.tipoNormalizado
      if (!type) {
        return null
      }

      if (type === 'MAL') {
        const heightInCm = getMalLateralHeightCm(item)
        const catalogID = resolveMalLateralCatalogID(heightInCm)

        if (!catalogID) {
          // console.warn(
            // `No se encontró lateral válido para altura ${item.height ?? 0}mm (${heightInCm}cm).`,
          // )
          return null
        }

        return {
          catalogID,
          // Misma base Y que el MAL: si no, el LA sobresale ~20 mm por arriba.
          heightOffset: 0,
        }
      }

      if (type === 'MBJ') {
        const depth = Math.round(item.thickness || 0)
        const height = getLateralCatalogHeightMm(item)
        const catalogID = resolveMbjLateralCatalogID(height, depth)

        if (!catalogID) {
          // console.warn(
            // `No se encontró lateral MBJ para altura ${item.height ?? 0}mm (fondo ${depth}mm).`,
          // )
          return null
        }

        return {
          catalogID,
          heightOffset: 0,
        }
      }

      let idPrefix = ''
      if (type === 'COL') {
        // Para COL, verificar si hay MBJ adyacentes en este lado
        const hasMBJ = hasMBJAdjacent(item, isFirstItem)

        if (hasMBJ) {
          // Si hay MBJ adyacente, usar LC13860 o LC14860 según la altura de la columna
          // LC13860 es para columnas de 2200mm de altura
          // LC14860 es para columnas de 2300mm de altura
          const itemHeight = getLateralCatalogHeightMm(item)
          const heightInCm = Math.round(itemHeight / 10)

          // Determinar qué lateral usar según la altura
          // Si la altura es 2200mm (220cm) o cerca, usar LC13860
          // Si la altura es 2300mm (230cm) o cerca, usar LC14860
          let lateralCatalogID: string
          if (Math.abs(heightInCm - 220) <= 5) {
            // Altura cerca de 2200mm
            lateralCatalogID = 'LC13860'
          } else if (Math.abs(heightInCm - 230) <= 5) {
            // Altura cerca de 2300mm
            lateralCatalogID = 'LC14860'
          } else {
            // Por defecto, usar LC13860 si altura <= 2250mm, LC14860 si altura > 2250mm
            lateralCatalogID = heightInCm <= 225 ? 'LC13860' : 'LC14860'
          }

          return {
            catalogID: lateralCatalogID,
            heightOffset: 0,
          }
        }
        // Si no hay MBJ adyacente, usar el lateral normal CC
        idPrefix = 'CC'
      } else {
        return null
      }

      const height = getLateralCatalogHeightMm(item)
      const depth = Math.round(item.thickness || 0)
      if (idPrefix === 'CC') {
        return {
          catalogID: buildCcLateralCatalogID(Math.round(height / 10)),
          heightOffset: 0,
        }
      }
      const catalogID = `${idPrefix}${height / 10}${depth / 10}`

      return {
        catalogID,
        heightOffset: 0,
      }
    }
      } finally {
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
          store.layers[store.currentLayer].selected.clear()
          for (const selectedId of originalSelected) {
            if (store.items.has(selectedId)) {
              store.layers[store.currentLayer].selected.add(selectedId)
            }
          }
        })
      }
    }

    if (isLinkedVisualBatchActive() || isLateralPlacementBatchActive()) {
      return executeDraw()
    }

    return runLateralPlacementBatch(() => executeDraw())
  },
  createItemGroup: (selectedItems: Set<AnyID>) => {
    const itemIds = Array.from(selectedItems)

    if (itemIds.length < 2) return

    // IMPORTANTE: Actualizar todas las OBBs antes de calcular posición del grupo
    // Esto asegura que las dimensiones sean correctas tanto en 2D como en 3D
    get().setOBBScene()

    // Obtener referencias actualizadas después de setOBBScene
    const { items, currentLayer, sceneBoundingBoxes } = get()

    // Find min boundary (bottom-left-back)
    const minPoint = new Vector3(Infinity, Infinity, Infinity)
    const maxPoint = new Vector3(-Infinity, -Infinity, -Infinity)
    const itemsData: { id: AnyID; worldMatrix: Matrix4 }[] = []

    let hasBounds = false

    itemIds.forEach((id) => {
      const item = items.get(id)
      if (item) {
        let worldMatrix: Matrix4
        if (item.matrix) {
          worldMatrix = item.matrix.clone()
        } else {
          // Fallback if no matrix
          worldMatrix = new Matrix4().setPosition(item.x ?? 0, item.y ?? 0, item.z ?? 0)
        }

        const obbData = sceneBoundingBoxes.get(id)
        if (obbData && obbData.boundingBox) {
          const obb = obbData.boundingBox
          // Calculate AABB points from OBB
          const center = new Vector3().copy(obb.center)

          // We must calculate all corner vertices
          // OBB is defined by center, rotation3x3, halfSize
          const result = new Vector3()
          const corners = [
            new Vector3(-1, -1, -1),
            new Vector3(1, -1, -1),
            new Vector3(1, 1, -1),
            new Vector3(-1, 1, -1),
            new Vector3(-1, -1, 1),
            new Vector3(1, -1, 1),
            new Vector3(1, 1, 1),
            new Vector3(-1, 1, 1),
          ]

          corners.forEach((p) => {
            p.x *= obb.halfSize.x
            p.y *= obb.halfSize.y
            p.z *= obb.halfSize.z
            p.applyMatrix3(obb.rotation).add(obb.center)
            minPoint.min(p)
            maxPoint.max(p)
          })
          hasBounds = true
        } else {
          // Fallback to position if no OBB
          const pos = new Vector3()
          pos.setFromMatrixPosition(worldMatrix)
          minPoint.min(pos)
          maxPoint.max(pos)
        }

        itemsData.push({ id, worldMatrix })
      }
    })

    if (!hasBounds && minPoint.x === Infinity) return

    // If no bounds were found but we have items, use the min position found
    if (minPoint.x === Infinity) {
      return
    }

    // Create Group Matrix: gizmo en esquina trasera inferior izquierda
    const groupPosition = new Vector3(maxPoint.x, minPoint.y, minPoint.z)

    // El gizmo del grupo siempre debe tener una rotación de 90 grados en el eje Y
    const groupRotation = new Quaternion().setFromEuler(new Euler(0, -Math.PI / 2, 0))

    const groupMatrix = new Matrix4().compose(groupPosition, groupRotation, new Vector3(1, 1, 1))
    const groupInverse = groupMatrix.clone().invert()

    // Create group ID
    const groupID = getId('itemGroup')

    // Perform state mutations inside set()
    set((state) => {
      // 1. Create new group object
      const newGroup = {
        id: groupID,
        type: 'itemGroup',
        children: new Set(itemIds),
        matrix: groupMatrix,
        x: groupPosition.x,
        y: groupPosition.y,
        z: groupPosition.z,

        visible: true,
        selected: true,
        isBlocked: false,
        canCollide: true,
        name: 'Nuevo Grupo',
        rotation: 0,
      } as any

      // Add group to items map
      state.items.set(groupID, newGroup)

      // Add group to current layer items
      const layer = state.layers[state.currentLayer]
      if (layer) {
        layer.items.add(groupID)

        // Update selection (select only the group)
        layer.selected.clear()
        layer.selected.add(groupID)
      }

      state.currentObjectID = groupID

      // 3. Guardar matrices LOCALES en los hijos (relativas al grupo) y bloquearlos.
      // El componente ItemGroup2D/3D usa localMatrices.current (de su propio ref) para render.
      itemsData.forEach(({ id, worldMatrix }) => {
        const item = state.items.get(id)
        if (item) {
          const localMatrix = groupInverse.clone().multiply(worldMatrix)

          const pos = new Vector3()
          const rot = new Quaternion()
          const scale = new Vector3()
          localMatrix.decompose(pos, rot, scale)

          // Si el item estaba pegado a una pared, removerlo de highItems/lowItems
          if (item.snappedWall) {
            const wall = state.items.get(item.snappedWall)
            if (wall && (isWall(wall) || isPartition(wall) || isHiddenWall(wall))) {
              if (item.tipoNormalizado === 'MAL') {
                wall.highItems?.delete(id as AnyID)
              }
              if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
                wall.lowItems?.delete(id as AnyID)
              }
            }
            // Limpiar snappedWall del item hijo
            ;(item as any).snappedWall = ''
          }

          // Mutate the draft item (matriz LOCAL respecto al grupo)
          ;(item as any).matrix = localMatrix
          ;(item as any).x = pos.x
          ;(item as any).y = pos.y
          ;(item as any).z = pos.z
          ;(item as any).isBlocked = true
          // Quitar hijo de colisionables (ya no actúa individualmente)
          state.collisionableItems.delete(id as AnyID)
        }
      })

      // Añair el grupo como unidad colisionable
      state.collisionableItems.add(groupID)
    })

    // Calular la OBB del grupo recién creado
    get().setItemGroupOBB(groupID)
  },
  getFrontalHeight: () => {
    const items = get().items
    const prefs = get().prefs
    for (const [, item] of items) {
      if ((isItem(item) || isLinkedItem(item)) && item.catalogID === 'Frontal') {
        return item.height
      }
    }

    return prefs.ALTURAFRONTALES
  },
  disbandItemGroup: (groupId: AnyID) => {
    set((store) => {
      const group = store.items.get(groupId) as ItemGroup | undefined
      if (!group || group.type !== 'itemGroup') return

      const childrenIds = Array.from(group.children) as AnyID[]
      const groupMatrix = group.matrix.clone()

      // Eliminar el grupo del store y de la capa
      store.items.delete(groupId)
      store.layers[store.currentLayer].items.delete(groupId)
      store.layers[store.currentLayer].selected.delete(groupId)
      store.sceneBoundingBoxes.delete(groupId)
      store.collisionableItems.delete(groupId)

      // Devolver los hijos a la capa actual como items independientes y desbloquearlos
      childrenIds.forEach((childId) => {
        store.layers[store.currentLayer].items.add(childId)
        const child = store.items.get(childId)
        if (child) {
          // Aplicar la transformación del grupo a la transformación del hijo
          // La nueva matriz del hijo es: groupMatrix * childMatrix (transformación mundo)
          const childMatrix = child.matrix.clone()
          child.matrix = groupMatrix.clone().multiply(childMatrix)
          ;(child as any).isBlocked = false
          // Restaurar colisión individual si el item la tenía habilitada
          if ((child as any).canCollide !== false) {
            store.collisionableItems.add(childId)
          }
        }
      })

      // Seleccionar los hijos tras desagrupar
      store.layers[store.currentLayer].selected = new Set(childrenIds)
      if (childrenIds.length > 0) {
        store.currentObjectID = childrenIds[0]
      }
    })
  },

  /**
   * Renumera todos los items de forma jerárquica
   * Los items padres obtienen números 1, 2, 3...
   * Los hijos obtienen números como 1.1, 1.2, 2.1, 2.2...
   * Los nietos obtienen números como 1.1.1, 1.1.2...
   * Esta función procesa la store y asigna los números increment
   */
  renumberItemsHierarchically: () => {
    set((store) => {
      const items = store.items
      const areas = store.areas

      // Identificar items padres (aquellos sin firstParentID o con depth === 0)
      const parentItems: AnyID[] = []
      const allItemsArray: any[] = []

      // Recopilar todos los items y áreas
      items.forEach((item, id) => {
        allItemsArray.push({ id, item, isArea: false })

        // Es padre si no tiene firstParentID (items universales o linkedItems raíz)
        const isParent = !('firstParentID' in item) || item.depth === 0
        if (isParent) {
          parentItems.push(id)
        }
      })

      areas.forEach((area, id) => {
        allItemsArray.push({ id, item: area, isArea: true })
        parentItems.push(id) // Las áreas siempre son padres
      })

      // Ordenar padres por tipo: primero áreas, luego paredes, después el resto
      parentItems.sort((a, b) => {
        const itemA = items.get(a) || areas.get(a)
        const itemB = items.get(b) || areas.get(b)

        if (!itemA || !itemB) return 0

        const typeA = itemA.type
        const typeB = itemB.type

        // Definir prioridades: areas=1, walls=2, resto=3
        const getPriority = (type: string) => {
          if (type === 'areas') return 1
          if (type === 'walls') return 2
          return 3
        }

        const priorityA = getPriority(typeA)
        const priorityB = getPriority(typeB)

        // Ordenar por prioridad primero
        if (priorityA !== priorityB) {
          return priorityA - priorityB
        }

        // Si tienen la misma prioridad, ordenar por ID (orden de creación)
        const aStr = String(a)
        const bStr = String(b)
        return aStr.localeCompare(bStr)
      })

      // Función recursiva para numerar un item y sus hijos
      const numberItemAndChildren = (itemId: AnyID, parentNumber: string): void => {
        const item = items.get(itemId)
        if (!item) return

        // Asignar el número a este item
        item.increment = parentNumber

        // Si tiene hijos, numerarlos
        if ('childs' in item && item.childs && item.childs.size > 0) {
          const childrenArray = Array.from(item.childs)

          // Ordenar los hijos según childrenOrder si existe
          if ('childrenOrder' in item && item.childrenOrder && item.childrenOrder.length > 0) {
            const orderMap = new Map<string, number>()
            item.childrenOrder.forEach((orderStr, index) => {
              const childId = orderStr.split('!')[1]
              orderMap.set(childId, index)
            })

            childrenArray.sort((a, b) => {
              const orderA = orderMap.get(a) ?? Infinity
              const orderB = orderMap.get(b) ?? Infinity
              return orderA - orderB
            })
          }

          // Numerar cada hijo
          childrenArray.forEach((childId, index) => {
            const childNumber = `${parentNumber}.${index + 1}`
            numberItemAndChildren(childId as AnyID, childNumber)
          })
        }
      }

      // Numerar los padres y sus descendientes
      parentItems.forEach((parentId, index) => {
        const parentNumber = String(index + 1)
        const item = items.get(parentId) || areas.get(parentId)

        if (item) {
          item.increment = parentNumber

          // Si es un item con hijos, numerarlos recursivamente
          if ('childs' in item && item.childs && item.childs.size > 0) {
            const childrenArray = Array.from(item.childs)

            // Ordenar los hijos según childrenOrder si existe
            if ('childrenOrder' in item && item.childrenOrder && item.childrenOrder.length > 0) {
              const orderMap = new Map<string, number>()
              item.childrenOrder.forEach((orderStr, index) => {
                const childId = orderStr.split('!')[1]
                orderMap.set(childId, index)
              })

              childrenArray.sort((a, b) => {
                const orderA = orderMap.get(a) ?? Infinity
                const orderB = orderMap.get(b) ?? Infinity
                return orderA - orderB
              })
            }

            // Numerar cada hijo
            childrenArray.forEach((childId, index) => {
              const childNumber = `${parentNumber}.${index + 1}`
              numberItemAndChildren(childId as AnyID, childNumber)
            })
          }
        }
      })
    })
  },
})